import "@fontsource-variable/outfit";
import "@fontsource-variable/archivo/wdth.css";
import "@fontsource-variable/spline-sans-mono";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "@fontsource-variable/fraunces/opsz.css";
import "@fontsource-variable/instrument-sans";
import "@fontsource/instrument-serif";
import React, { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import { createRoot } from "react-dom/client";
import type {
  ChangeSetRecord,
  CustomDomainRecord,
  CutoverRehearsalRecord,
  FullSiteAuditReportV1,
  JobRecord,
  MigrationAcceptanceContractV1,
  MigrationRecord,
  MonthlyReportId,
  MigrationAuditMetricsV1,
  MigrationComparisonReportV1,
  OwnerNamedCompetitor,
  SiteId,
  SiteRecord,
  MigrationProposal,
  PilotSiteManager,
  PilotYesNo,
  RecommendationMeasurementRecord,
  RecommendationRecord,
  SeoAuditRunRecord,
  SiteAgainAssessmentReportV1,
  VerifyAssessmentUnlockResponse,
  WeeklyReportRecord,
} from "@siteagain/shared-types";

import "./styles.css";
import "./journey.css";
import { assessmentCopy } from "./assessment-copy";
import { AssessmentReportView, LanguageSwitch } from "./assessment-report";
import { getLocale, useLocale, type Locale } from "./i18n";
import { OwnerAudit } from "./owner-audit";
import { JourneyQuickProof } from "./quick-proof";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { OperatorConsole } from "./operator-console";
import {
  adoptSearchConsoleResult,
  peekSearchConsoleResult,
  SearchConsoleSection,
} from "./search-console-section";
import { adoptEnquiriesLink, EnquiriesSection, peekEnquiriesLink } from "./enquiries-section";
import {
  adoptMonthlyReportLink,
  MonthlyReportLinkDialog,
  MonthlyReportsSection,
} from "./monthly-reports-section";

type SiteView = SiteRecord & {
  previewUrl: string | null;
  /** Content-derived, so it can only come from the API — cutover needs it. */
  previewDeploymentId?: string | null;
  latestMigration?: MigrationRecord | null;
};
type ChangeView = ChangeSetRecord;
type StudioTab = "preview" | "proof" | "proposal" | "findings" | "growth" | "launch";


interface ApiErrorPayload {
  error?: { message?: string };
}

interface SessionUser {
  id: string;
  email: string;
  name: string;
}

interface MigrationResponse {
  migration: MigrationRecord;
  progress: {
    step: MigrationRecord["currentStep"];
    percent: number;
    message: string | null;
    jobs: JobRecord[];
  };
  qualityGateReport: MigrationRecord["qualityGateReport"];
  previewUrl: string | null;
}

interface ChangeResponse {
  changeSet: ChangeView;
  usage: { totalMicroUsd: number };
}

const CHANGE_TERMINAL = new Set(["deployed", "rejected", "failed", "rolled_back"]);
const MIGRATION_TERMINAL = new Set(["preview_ready", "completed", "failed", "aborted"]);
/** Where "start over" sends a visitor back to — the public site owns URL entry. */
const MARKETING_ORIGIN = "https://siteagain.com";
const PREVIEW_ROUTER_ORIGIN = "https://siteagain-preview-router-staging.ismael.workers.dev";
const PRODUCTION_PREVIEW_ROUTER_ORIGIN = "https://siteagain-preview-router.ismael.workers.dev";
const LEGACY_PREVIEW_ROUTER_ORIGIN = "https://nodash-preview-router-staging.ismael.workers.dev";

const MIGRATION_STAGES = [
  { key: "audit", label: "Audit", detail: "Speed, structure and search signals", at: 5 },
  { key: "opportunity", label: "Opportunity", detail: "SEO, GEO and maintenance gains", at: 15 },
  { key: "first-page", label: "First page", detail: "Rebuild the page you shared", at: 42 },
  { key: "verify", label: "Verify", detail: "Quality and SEO preservation", at: 65 },
  { key: "preview", label: "Preview", detail: "Publish your private comparison", at: 96 },
] as const;

const CHANGE_PHASES = [
  { key: "requested", label: "Received" },
  { key: "planning", label: "Plan" },
  { key: "editing", label: "Build" },
  { key: "validating", label: "Verify" },
  { key: "awaiting_approval", label: "Review" },
  { key: "deployed", label: "Live" },
] as const;

/**
 * The signed-in app, plus the dialog an emailed monthly-report link opens. The dialog renders
 * above every screen of the app, including onboarding, so the link works whatever the user
 * was doing when they followed it.
 */
function App({ monthlyReportId, onMonthlyReportClosed, ...rest }: ComponentProps<typeof AppInner> & {
  monthlyReportId: MonthlyReportId | null;
  onMonthlyReportClosed(): void;
}) {
  return (
    <>
      <AppInner {...rest} />
      {monthlyReportId && <MonthlyReportLinkDialog reportId={monthlyReportId} onClose={onMonthlyReportClosed} />}
    </>
  );
}

function AppInner({ user, operatorEnabled, journey, onJourneyConsumed, onSignOut, onDeleteAccount }: {
  user: SessionUser;
  operatorEnabled: boolean;
  /** The pre-signup assessment this account should pick up, if there is one. */
  journey: AnonymousState | null;
  onJourneyConsumed(): void;
  onSignOut(): void;
  onDeleteAccount(): void;
}) {
  const [sites, setSites] = useState<SiteView[]>([]);
  const [siteId, setSiteId] = useState("");
  const [changes, setChanges] = useState<ChangeView[]>([]);
  const [selectedChangeId, setSelectedChangeId] = useState("");
  const [selectedChange, setSelectedChange] = useState<ChangeView | null>(null);
  const [migration, setMigration] = useState<MigrationRecord | null>(null);
  const [migrationJobs, setMigrationJobs] = useState<JobRecord[]>([]);
  const [usageMicroUsd, setUsageMicroUsd] = useState(0);
  const [tab, setTab] = useState<StudioTab>("preview");
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [showOperator, setShowOperator] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Coming back from Google's consent screen: open the site that was being connected, on its Growth tab.
  const pendingSearchConsole = useRef(peekSearchConsoleResult());
  // The "someone got in touch" email links to the site's enquiries the same way.
  const pendingEnquiries = useRef(peekEnquiriesLink());
  useEffect(() => {
    const pending = pendingSearchConsole.current ?? (pendingEnquiries.current ? { siteId: pendingEnquiries.current } : null);
    if (!pending || sites.length === 0) return;
    pendingSearchConsole.current = null;
    pendingEnquiries.current = null;
    const target = sites.find((site) => site.id === pending.siteId) ?? sites[0];
    if (!target) return;
    setSiteId(target.id);
    setShowOnboarding(false);
    setTab("growth");
    setPreviewOpen(true);
  }, [sites]);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === siteId) ?? null,
    [siteId, sites],
  );
  const hasActiveChange = changes.some((change) => !CHANGE_TERMINAL.has(change.status));
  const activeChangeId = changes.find((change) => !CHANGE_TERMINAL.has(change.status))?.id ?? "";
  const migrationReady = Boolean(selectedSite?.previewUrl) && isMigrationPreviewReady(migration);
  const composerDisabled = !migrationReady || hasActiveChange;

  function scrollToChange(changeId: string) {
    window.setTimeout(() => {
      document.getElementById(`conversation-change-${changeId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);
  }

  const loadSites = useCallback(async () => {
    const response = await api<{ sites: SiteView[] }>("/sites");
    setSites(response.sites);
    setSiteId((current) => current || response.sites[0]?.id || "");
    setShowOnboarding((current) => current || response.sites.length === 0);
    return response.sites;
  }, []);

  const loadChanges = useCallback(async (activeSiteId: string) => {
    if (!activeSiteId) {
      setChanges([]);
      setSelectedChangeId("");
      setSelectedChange(null);
      return;
    }
    const response = await api<{ changeSets: ChangeView[] }>(
      `/sites/${activeSiteId}/change-sets`,
    );
    setChanges(response.changeSets);
    setSelectedChangeId((current) =>
      response.changeSets.some((change) => change.id === current)
        ? current
        : response.changeSets[0]?.id || "",
    );
  }, []);

  const loadSelectedChange = useCallback(async (changeId: string) => {
    if (!changeId) {
      setSelectedChange(null);
      setUsageMicroUsd(0);
      return;
    }
    const response = await api<ChangeResponse>(`/change-sets/${changeId}`);
    setSelectedChange(response.changeSet);
    setUsageMicroUsd(response.usage.totalMicroUsd);
    setChanges((current) =>
      current.map((item) =>
        item.id === response.changeSet.id ? response.changeSet : item,
      ),
    );
  }, []);

  const refreshChange = useCallback(async (changeId: string) => {
    const response = await api<ChangeResponse>(`/change-sets/${changeId}`);
    setChanges((current) =>
      current.map((item) =>
        item.id === response.changeSet.id ? response.changeSet : item,
      ),
    );
  }, []);

  const loadMigration = useCallback(async (migrationId: string) => {
    const response = await api<MigrationResponse>(`/migrations/${migrationId}`);
    setMigration(response.migration);
    setMigrationJobs(response.progress.jobs);
    setSites((current) =>
      current.map((site) =>
        site.id === response.migration.siteId
          ? {
              ...site,
              latestMigration: response.migration,
              previewUrl: response.previewUrl ?? site.previewUrl,
            }
          : site,
      ),
    );
    return response;
  }, []);

  useEffect(() => {
    void loadSites()
      .catch((reason: unknown) => setError(messageFor(reason)))
      .finally(() => setLoading(false));
  }, [loadSites]);

  useEffect(() => {
    if (!selectedSite) {
      setMigration(null);
      setMigrationJobs([]);
      return;
    }
    setMigration(selectedSite.latestMigration ?? null);
    setMigrationJobs([]);
  }, [selectedSite?.id]);

  useEffect(() => {
    void loadChanges(siteId).catch((reason: unknown) => setError(messageFor(reason)));
  }, [loadChanges, siteId]);

  useEffect(() => {
    void loadSelectedChange(selectedChangeId).catch((reason: unknown) =>
      setError(messageFor(reason)),
    );
  }, [loadSelectedChange, selectedChangeId]);

  useEffect(() => {
    const migrationId = selectedSite?.latestMigration?.id;
    if (!migrationId) return;
    void loadMigration(migrationId).catch((reason: unknown) => setError(messageFor(reason)));
    if (migration && MIGRATION_TERMINAL.has(migration.status)) return;
    const timer = window.setInterval(() => {
      void loadMigration(migrationId)
        .then((response) => {
          if (MIGRATION_TERMINAL.has(response.migration.status)) void loadSites();
        })
        .catch((reason: unknown) => setError(messageFor(reason)));
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [loadMigration, loadSites, migration?.status, selectedSite?.latestMigration?.id]);

  useEffect(() => {
    if (!activeChangeId) return;
    const timer = window.setInterval(() => {
      const refresh = activeChangeId === selectedChangeId ? loadSelectedChange : refreshChange;
      void refresh(activeChangeId).catch((reason: unknown) =>
        setError(messageFor(reason)),
      );
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [activeChangeId, loadSelectedChange, refreshChange, selectedChangeId]);

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (!composerDisabled) document.getElementById("change-request")?.focus();
      }
    }
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [composerDisabled]);

  async function onboard(sourceUrl: string, goal: OnboardingGoal) {
    setError("");
    setNotice("Starting your website audit…");
    const key = crypto.randomUUID();
    // When the visitor already paid for an assessment before signing in, hand
    // the API that capability so the site inherits the finished report rather
    // than crawling the same URL a second time (ONB-002).
    const adoptable = journey
      && journey.auditJobId
      && journey.auditToken
      && journey.submittedUrl === sourceUrl
      ? { jobId: journey.auditJobId, token: journey.auditToken }
      : null;
    const created = await api<{ site: SiteRecord }>("/sites", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": `site-${key}`,
      },
      body: JSON.stringify({ sourceUrl, goal, locale: getLocale(), ...(adoptable ? { assessment: adoptable } : {}) }),
    });
    void sendTelemetry(
      "account_site_created",
      { adoptedAssessment: adoptable !== null, goal },
      journey?.anonymousId,
      created.site.id,
    );
    const newSite: SiteView = {
      ...created.site,
      previewUrl: null,
      latestMigration: null,
    };
    setSites((current) => [newSite, ...current.filter((site) => site.id !== newSite.id)]);
    setSiteId(newSite.id);
    setMigration(null);
    setMigrationJobs([]);
    setSelectedChangeId("");
    setSelectedChange(null);
    setShowOnboarding(false);
    onJourneyConsumed();
    setNotice(
      adoptable
        ? "Using the assessment you already have. You will choose the direction before anything is rebuilt."
        : "Your audit has started. Nothing is rebuilt or published until you choose what to do.",
    );
    window.setTimeout(() => setNotice(""), 4_000);
  }

  /**
   * The only build the journey starts before a plan is paid for is the as-is
   * quick proof (ADR-0031): one render, ready in seconds. It creates no
   * migration and leaves the site's status alone; the full build is started
   * from Mission Control once the plan is active.
   */
  async function startQuickProof(direction: JourneyDirection) {
    if (!selectedSite) return;
    setError("");
    const started = await api<{ proof: { id: string } }>(
      `/sites/${selectedSite.id}/proofs`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": `proof-${selectedSite.id}-${crypto.randomUUID()}`,
        },
        body: JSON.stringify({ direction }),
      },
    );
    rememberJourneyPreference(selectedSite.id, `proof:${direction}`, started.proof.id);
  }

  async function requestChange(requestText: string) {
    if (!siteId || hasActiveChange) return;
    setError("");
    setNotice("Request received. Building a safe plan…");
    const created = await api<{ changeSet: ChangeView }>(
      `/sites/${siteId}/change-sets`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": `change-${crypto.randomUUID()}`,
        },
        body: JSON.stringify({ requestText }),
      },
    );
    setSelectedChangeId(created.changeSet.id);
    setSelectedChange(created.changeSet);
    await loadChanges(siteId);
    scrollToChange(created.changeSet.id);
    setNotice("");
  }

  async function restartHomepageMigration() {
    if (!siteId) return;
    setError("");
    setNotice("Rebuilding the homepage from the root URL…");
    const started = await api<{ migration: MigrationRecord }>(
      `/sites/${siteId}/migrations`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": `homepage-rebuild-${crypto.randomUUID()}`,
        },
        body: JSON.stringify({ singlePage: true }),
      },
    );
    setMigration(started.migration);
    setMigrationJobs([]);
    setSelectedChangeId("");
    setSelectedChange(null);
    setSites((current) => current.map((item) => item.id === siteId
      ? { ...item, status: "migrating", previewUrl: null, latestMigration: started.migration }
      : item));
    setNotice("The homepage audit and rebuild have started. Your live site is untouched.");
    window.setTimeout(() => setNotice(""), 5_000);
  }

  async function decide(action: "approve" | "reject" | "rollback") {
    if (!selectedChange) return;
    setError("");
    setNotice(
      action === "approve"
        ? "Publishing your approved version…"
        : action === "rollback"
          ? "Restoring the previous version…"
          : "Rejecting this version…",
    );
    await api(`/change-sets/${selectedChange.id}/${action}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": `decision-${action}-${selectedChange.id}`,
      },
      body: "{}",
    });
    await loadSelectedChange(selectedChange.id);
    await loadSites();
    setNotice("");
  }

  function selectSite(nextSiteId: string) {
    setSiteId(nextSiteId);
    setSelectedChangeId("");
    setSelectedChange(null);
    setTab("preview");
    setShowOnboarding(false);
  }

  function selectChange(nextChangeId: string) {
    setSelectedChangeId(nextChangeId);
    scrollToChange(nextChangeId);
  }

  function openStudioPanel(nextTab: StudioTab) {
    setTab(nextTab);
    setPreviewOpen(true);
    window.setTimeout(() => {
      if (window.matchMedia("(max-width: 760px)").matches) {
        document.querySelector(".sandbox")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 0);
  }

  if (loading) return <LoadingScreen />;

  if (operatorEnabled && showOperator) {
    return <OperatorConsole user={user} onExit={() => setShowOperator(false)} onSignOut={onSignOut} />;
  }

  if (showOnboarding || !selectedSite) {
    return (
      <OnboardingPage
        hasProjects={sites.length > 0}
        user={user}
        error={error}
        onBack={() => {
          setError("");
          setShowOnboarding(false);
          setSiteId(sites[0]?.id || "");
        }}
        onSubmit={onboard}
        {...(journey ? { prefill: { url: journey.submittedUrl, goal: journey.goal, assessed: journey.auditJobId !== null } } : {})}
        {...(operatorEnabled ? { onOpenOperator: () => setShowOperator(true) } : {})}
        onSignOut={onSignOut}
      />
    );
  }

  return (
    <JourneyShell
      user={user}
      sites={sites}
      site={selectedSite}
      migration={migration}
      migrationJobs={migrationJobs}
      changes={changes}
      selectedChange={selectedChange}
      usageMicroUsd={usageMicroUsd}
      notice={notice}
      error={error}
      hasActiveChange={hasActiveChange}
      onDismissError={() => setError("")}
      onSelectSite={selectSite}
      onSelectChange={selectChange}
      onNewProject={() => {
        setError("");
        setShowOnboarding(true);
      }}
      onOpenOperator={operatorEnabled ? () => setShowOperator(true) : undefined}
      onSignOut={onSignOut}
      onDeleteAccount={onDeleteAccount}
      onSubmitChange={async (text) => {
        try {
          await requestChange(text);
        } catch (reason) {
          setError(messageFor(reason));
          throw reason;
        }
      }}
      onRestartHomepage={() =>
        void restartHomepageMigration().catch((reason: unknown) => setError(messageFor(reason)))
      }
      onDecision={(action) =>
        void decide(action).catch((reason: unknown) => setError(messageFor(reason)))
      }
      onStartProof={startQuickProof}
      onSelectFix={(prompt) => {
        setNotice(`Agent started fix: "${prompt.slice(0, 45)}…"`);
        void requestChange(prompt).catch((reason: unknown) => setError(messageFor(reason)));
      }}
      onRecommendationPreview={(changeSetId) => {
        setSelectedChangeId(changeSetId);
        void loadChanges(siteId).catch((reason: unknown) => setError(messageFor(reason)));
      }}
    />
  );
}

function StudioTopBar({
  sites,
  site,
  changes,
  user,
  previewOpen,
  onTogglePreview,
  onSelectSite,
  onSelectChange,
  onNewProject,
  onOpenOperator,
  onSignOut,
  onDeleteAccount,
}: {
  sites: SiteView[];
  site: SiteView;
  changes: ChangeView[];
  user: SessionUser;
  previewOpen: boolean;
  onTogglePreview(): void;
  onSelectSite(id: string): void;
  onSelectChange(id: string): void;
  onNewProject(): void;
  onOpenOperator?: () => void;
  onSignOut(): void;
  onDeleteAccount(): void;
}) {
  return (
    <header className="studio-topbar">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="website-context-trigger" aria-label="Switch current website">
            <span className="topbar-mark"><Logo /></span>
            <span className="website-context-copy">
              <small>Current website</small>
              <span className="website-context-name">
                <strong>SiteAgain</strong><i aria-hidden="true" /><b>{websiteHost(site)}</b>
              </span>
            </span>
            <ChevronDownIcon />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="project-menu topbar-project-menu">
          <DropdownMenuLabel>Move to another website</DropdownMenuLabel>
          {sites.map((item) => (
            <DropdownMenuItem
              className={item.id === site.id ? "selected" : ""}
              key={item.id}
              onSelect={() => onSelectSite(item.id)}
            >
              <span className="menu-favicon">{item.name.slice(0, 1).toUpperCase()}</span>
              <span className="menu-item-copy"><strong>{item.name}</strong><small>{websiteHost(item)} · {shortStatus(item.status)}</small></span>
              {item.id === site.id && <CheckIcon />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="new-project-item" onSelect={onNewProject}><PlusIcon /><span><strong>Create new project</strong><small>Add another website to SiteAgain</small></span></DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="topbar-actions">
        <button className="topbar-icon-button" onClick={onTogglePreview} aria-label={previewOpen ? "Hide preview" : "Open preview"} title={previewOpen ? "Hide preview" : "Open preview"}><PanelIcon /></button>
        {changes.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="topbar-icon-button" aria-label="Change history" title="Change history"><HistoryIcon /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="history-menu">
              <DropdownMenuLabel>Change history</DropdownMenuLabel>
              {changes.map((item) => (
                <DropdownMenuItem key={item.id} onSelect={() => onSelectChange(item.id)}>
                  <StatusDot status={item.status} />
                  <span className="menu-item-copy"><strong>{item.requestText}</strong><small>{relativeTime(item.createdAt)} · {shortStatus(item.status)}</small></span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {onOpenOperator && <button className="topbar-icon-button" onClick={onOpenOperator} aria-label="Open Mission Control" title="Mission Control"><CommandIcon /></button>}
        <span className="topbar-divider" aria-hidden="true" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="topbar-avatar" aria-label="Open account menu">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="account-menu">
            <DropdownMenuLabel>
              <strong>{user.name || "Your account"}</strong>
              <span>{user.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onNewProject}><PlusIcon />Create new project</DropdownMenuItem>
            {onOpenOperator && <DropdownMenuItem onSelect={onOpenOperator}><CommandIcon />Mission Control</DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="destructive" onSelect={onDeleteAccount}>Delete account</DropdownMenuItem>
            <DropdownMenuItem className="destructive" onSelect={onSignOut}>Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

type OnboardingGoal = "traffic" | "leads" | "maintenance";

const ONBOARDING_GOALS: Array<{ value: OnboardingGoal; label: string; detail: string }> = [
  { value: "leads", label: "More qualified enquiries", detail: "Clearer journeys, stronger calls to action and less friction" },
  { value: "traffic", label: "More search visibility", detail: "SEO, local search and readiness for AI answers" },
  { value: "maintenance", label: "Less dependency", detail: "Change the site without waiting on an agency retainer" },
];

function OnboardingPage({
  hasProjects,
  user,
  error,
  prefill,
  onBack,
  onSubmit,
  onOpenOperator,
  onSignOut,
}: {
  hasProjects: boolean;
  user: SessionUser;
  error: string;
  /** URL and goal carried in from before sign-in, so they are never re-asked. */
  prefill?: { url: string; goal: OnboardingGoal | null; assessed: boolean } | undefined;
  onBack(): void;
  onSubmit(url: string, goal: OnboardingGoal): Promise<void>;
  onOpenOperator?: () => void;
  onSignOut(): void;
}) {
  const [url, setUrl] = useState(prefill?.url ?? "");
  const [urlError, setUrlError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  // A goal carried in from the marketing site is honoured; otherwise the
  // homepage audit defaults to the broadest outcome and the visitor is never
  // asked to pick between options they cannot yet judge.
  const goal: OnboardingGoal = prefill?.goal ?? "leads";
  const displayHost = sourceHost(url || prefill?.url || "");

  async function submit() {
    if (busy) return;
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setUrlError("Enter a valid website address, like example.com.");
      return;
    }
    setUrlError("");
    setBusy(true);
    try {
      await onSubmit(normalized, goal);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="jr jr-onboarding">
      <header className="jr-top">
        <div className="jr-top-left">
          <button className="jr-logo" type="button" onClick={hasProjects ? onBack : undefined}>
            <span className="jr-logo-mark">S</span>
            <span>SiteAgain</span>
          </button>
        </div>
        <div className="jr-top-right">
          <LanguageSwitch />
          {onOpenOperator && <button className="jr-signin" type="button" onClick={onOpenOperator}>Mission Control</button>}
          {hasProjects && <button className="jr-signin" type="button" onClick={onBack}>Back to studio</button>}
          <span className="jr-public-note">{user.name || user.email}</span>
          <button className="jr-signin" type="button" onClick={onSignOut}>Sign out</button>
        </div>
      </header>

      <div className="jr-wrap">
        <div className="jr-onboarding-grid">
          <section className="jr-onboarding-copy">
            <span className="jr-eyebrow">Built to outperform the agency model</span>
            <h1 className="jr-h1" style={{ marginTop: 16 }}>Know what to fix. Keep control of what changes.</h1>
            <p className="jr-lede">
              Start with a clear audit of your site, search visibility and buyer journey. You then choose whether to migrate as it is, improve conversion or redesign the experience. Nothing is rebuilt or published before that choice.
            </p>

            <form
              className={`jr-url-card ${dragging ? "dragging" : ""}`}
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                const dropped = event.dataTransfer.getData("text/uri-list") || event.dataTransfer.getData("text/plain");
                if (dropped) {
                  setUrl(dropped.trim());
                  setUrlError("");
                }
              }}
            >
              <label htmlFor="source-website">Your current website</label>
              <div className="jr-url-row">
                <div className="input-shell">
                  <GlobeIcon />
                  <input
                    id="source-website"
                    type="text"
                    inputMode="url"
                    value={url}
                    onChange={(event) => {
                      setUrl(event.target.value);
                      if (urlError) setUrlError("");
                    }}
                    placeholder="yourwebsite.com"
                    aria-label="Website URL"
                    aria-invalid={Boolean(urlError)}
                    aria-describedby="url-help"
                    autoFocus
                  />
                </div>
                <button className="jr-btn" type="submit" disabled={!url.trim() || busy}>
                  {busy ? <SpinnerIcon /> : <SparkIcon />}
                  {busy ? "Starting…" : prefill?.assessed ? "Open my audit" : "Audit my website"}
                  {!busy && <ArrowIcon />}
                </button>
              </div>
              <p id="url-help">Paste a link or drop it here · Free check · No domain changes</p>
            </form>

            {(urlError || error) && <div className="jr-error" role="alert"><AlertIcon /> {urlError || error}</div>}

            <div className="jr-lenses" aria-label="What SiteAgain analyzes">
              {["Site inventory", "SEO + AI search", "Speed", "Conversion path"].map((lens) => (
                <span className="jr-chip outline" key={lens}>{lens}</span>
              ))}
            </div>
            <div className="jr-onboarding-proofs">
              <div><strong>Audit first</strong><span>Understand the gaps and the number of pages before any build.</span></div>
              <div><strong>Choose the work</strong><span>Move as-is, sharpen conversion or redesign the experience.</span></div>
              <div><strong>Stay in control</strong><span>Preview, approve, roll back and export everything.</span></div>
            </div>
          </section>

          <aside className="jr-onboarding-aside">
            <div className="jr-assistant">
              <span>S</span>
              <p>
                {displayHost && prefill?.assessed
                  ? <>I still have your assessment of <strong>{displayHost}</strong>. We&apos;ll turn it into a clear decision, not an automatic rebuild.</>
                  : displayHost
                    ? <>I&apos;ll audit <strong>{displayHost}</strong> and show you what we find. You choose the direction afterwards.</>
                    : <>Add your website. I&apos;ll map the pages, find the highest-impact gaps and explain what I would do next.</>}
              </p>
            </div>
            <div className="jr-card pad jr-onboarding-benefits">
              <span className="jr-eyebrow">Why SiteAgain</span>
              <div className="jr-benefit-row">
                <strong>Senior thinking, not a ticket queue</strong>
                <span>Recommendations tied to your pages, search evidence and commercial goals.</span>
              </div>
              <div className="jr-benefit-row">
                <strong>Weekly action beats monthly reports</strong>
                <span>See what changed, what it measured and what deserves attention next.</span>
              </div>
              <div className="jr-benefit-row">
                <strong>Autopilot with human control</strong>
                <span>Approve changes, pause automation and roll back whenever you want.</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function StudioRail({
  site,
  change,
  changes,
  selectedChangeId,
  migration,
  migrationJobs,
  usageMicroUsd,
  composerDisabled,
  notice,
  error,
  onDismissError,
  onSubmitChange,
  onRestartHomepage,
  onDecision,
  onOpenPreview,
  onOpenAudit,
  onOpenProof,
}: {
  site: SiteView;
  change: ChangeView | null;
  changes: ChangeView[];
  selectedChangeId: string;
  migration: MigrationRecord | null;
  migrationJobs: JobRecord[];
  usageMicroUsd: number;
  composerDisabled: boolean;
  notice: string;
  error: string;
  onDismissError(): void;
  onSubmitChange(text: string): Promise<void>;
  onRestartHomepage(): void;
  onDecision(action: "approve" | "reject" | "rollback"): void;
  onOpenPreview(): void;
  onOpenAudit(): void;
  onOpenProof(changeId: string): void;
}) {
  const ready = Boolean(site.previewUrl) && isMigrationPreviewReady(migration);
  const historicalChanges = useMemo(
    () => changes
      .map((item) => item.id === change?.id ? change : item)
      .sort((left, right) => Date.parse(left.createdAt) - Date.parse(right.createdAt)),
    [change, changes],
  );

  return (
    <aside className="studio-rail">
      <div className="rail-conversation">
        {error && (
          <div className="rail-alert error" role="alert"><AlertIcon /><span>{error}</span><button onClick={onDismissError}>×</button></div>
        )}
        {notice && <div className="rail-alert"><span className="working-dot" /><span>{notice}</span></div>}

        <div className={`conversation-timeline ${ready ? "ready" : "building"}`}>
          {!ready && historicalChanges.length > 0 && (
            <ChangeHistory
              changes={historicalChanges}
              selectedChangeId={selectedChangeId}
              onOpenProof={onOpenProof}
              onDecision={onDecision}
            />
          )}
          {!ready ? (
            <MigrationJourney jobs={migrationJobs} migration={migration} site={site} onRestartHomepage={onRestartHomepage} />
          ) : (
            <>
              <ReadyConversation
                jobs={migrationJobs}
                migration={migration}
                site={site}
                actionsDisabled={composerDisabled}
                onSelectOffer={onSubmitChange}
                onRestartHomepage={onRestartHomepage}
                onOpenPreview={onOpenPreview}
                onOpenAudit={onOpenAudit}
              />
              {historicalChanges.length > 0 && (
                <ChangeHistory
                  changes={historicalChanges}
                  selectedChangeId={selectedChangeId}
                  onOpenProof={onOpenProof}
                  onDecision={onDecision}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ChangeComposer
        disabled={composerDisabled}
        disabledReason={!ready ? "Unlocks when your preview is ready" : "Finish the current change first"}
        onSubmit={onSubmitChange}
      />
      {change && <div className="rail-cost">Run cost ${(usageMicroUsd / 1_000_000).toFixed(4)}</div>}
    </aside>
  );
}

function ChangeHistory({
  changes,
  selectedChangeId,
  onOpenProof,
  onDecision,
}: {
  changes: ChangeView[];
  selectedChangeId: string;
  onOpenProof(changeId: string): void;
  onDecision(action: "approve" | "reject" | "rollback"): void;
}) {
  return (
    <div className="change-history-thread" aria-label="Complete change conversation history">
      {changes.map((change) => (
        <ChangeConversation
          change={change}
          key={change.id}
          selected={change.id === selectedChangeId}
          onOpenProof={() => onOpenProof(change.id)}
          onDecision={onDecision}
        />
      ))}
    </div>
  );
}

function MigrationJourney({
  jobs,
  migration,
  site,
  onRestartHomepage,
}: {
  jobs: JobRecord[];
  migration: MigrationRecord | null;
  site: SiteView;
  onRestartHomepage(): void;
}) {
  const percent = migration?.progressPercent ?? (site.status === "migrating" ? 3 : 0);
  const failed = migration?.status === "failed" || site.status === "failed";
  const stalled = !failed
    && migration !== null
    && !MIGRATION_TERMINAL.has(migration.status)
    && Date.now() - Date.parse(migration.createdAt) > 15 * 60_000;
  const score = qualityScore(migration);
  const insights = migrationConversationInsights(migration, jobs, site);
  const host = sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name));
  const sourceInsights = insights.filter((insight) => insight.stage === "audit");
  const opportunityReady = insights.some((insight) => insight.stage === "opportunity");
  const workInsights = insights.filter((insight) => insight.stage !== "audit" && insight.stage !== "opportunity");

  return (
    <section className="migration-journey">
      <div className="assistant-line"><span><Logo /></span><strong>SiteAgain</strong><i>{failed ? "Needs attention" : "Working now"}</i></div>
      <div className="operator-thread" role="log" aria-live="polite" aria-relevant="additions">
        <article className="operator-message opening-message">
          <p>We started auditing <strong>{host}</strong>.</p>
          <small>I’m checking performance, search visibility, AI-readiness and the structure of your homepage. Your live site stays untouched.</small>
        </article>

        {sourceInsights.map((insight, index) => (
          <article className="operator-message result-message" key={insight.stage}>
            <span className="message-check"><CheckIcon /></span>
            <div>
              <small>Step {index + 1} · {insight.label}</small>
              <strong>{insight.headline}</strong>
              <p>{insight.body}</p>
              {insight.facts.length > 0 && <ul>{insight.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>}
            </div>
          </article>
        ))}

        {opportunityReady && (
          <OpportunityMessage migration={migration} site={site} />
        )}

        {workInsights.map((insight, index) => (
          <article className="operator-message result-message" key={insight.stage}>
            <span className="message-check"><CheckIcon /></span>
            <div>
              <small>Step {sourceInsights.length + index + 2} · {insight.label}</small>
              <strong>{insight.headline}</strong>
              <p>{insight.body}</p>
              {insight.facts.length > 0 && <ul>{insight.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>}
            </div>
          </article>
        ))}

        <article className={`operator-message current-message ${failed ? "attention" : ""}`}>
          <span className="thinking-dots" aria-hidden="true"><i /><i /><i /></span>
          <div>
            <strong>{failed ? migrationFailureHeading(percent) : migrationStageHeading(percent)}</strong>
            <p>{failed ? migrationFailureMessage(percent) : migrationStageMessage(percent, site)}</p>
            {score !== null && <small>Latest verified quality score · {score}/100</small>}
          </div>
        </article>
      </div>

      <div className="calm-progress" aria-label={`Migration ${percent}% complete`}>
        <span>{failed ? "Work saved" : "Working quietly in the background"}</span><em>{percent}%</em>
        <i><b style={{ width: `${percent}%` }} /></i>
      </div>
      <div className={`leave-note ${failed ? "failed" : ""}`}>
        {failed ? <AlertIcon /> : <ClockIcon />}
        <span>
          <strong>{failed ? "The completed work is still recorded." : "No need to keep this open."}</strong>
          <small>{failed ? "Your findings and completed checks remain here while the preview issue is resolved." : "We’ll continue in the background and keep your place."}</small>
        </span>
      </div>
      {failed && <button className="retry-homepage" type="button" onClick={onRestartHomepage}>Retry the homepage build <ArrowIcon /></button>}
      {stalled && <button className="retry-homepage" type="button" onClick={onRestartHomepage}>Resume with the optimized homepage path <ArrowIcon /></button>}
    </section>
  );
}

function ReadyConversation({
  jobs,
  migration,
  site,
  actionsDisabled,
  onSelectOffer,
  onRestartHomepage,
  onOpenPreview,
  onOpenAudit,
}: {
  jobs: JobRecord[];
  migration: MigrationRecord | null;
  site: SiteView;
  actionsDisabled: boolean;
  onSelectOffer(text: string): void;
  onRestartHomepage(): void;
  onOpenPreview(): void;
  onOpenAudit(): void;
}) {
  const score = qualityScore(migration) ?? 100;
  const seoGeoScore = migration?.seoScore == null ? score : percentScore(migration.seoScore);
  const insights = migrationConversationInsights(migration, jobs, site);
  const gate = migration?.qualityGateReport;
  const optimization = imageOptimizationSummary(jobs);
  const reduction = optimization && optimization.bytesBefore > 0
    ? Math.max(0, Math.round((1 - optimization.bytesAfter / optimization.bytesBefore) * 100))
    : null;
  const attention = gate?.verdict === "NEEDS_REVIEW" || Boolean(gate?.reviewReasons.length);
  const legacyNestedPreview = hasNestedPreviewPath(site.previewUrl);
  const sourceInsights = insights.filter((insight) => insight.stage === "audit" || insight.stage === "opportunity");
  const buildInsights = insights.filter((insight) => insight.stage === "first-page" || insight.stage === "verify");
  return (
    <section className="ready-conversation">
      <div className="assistant-line"><span><Logo /></span><strong>SiteAgain</strong><i className="ready">Preview ready</i></div>
      <div className="operator-thread ready-thread">
        <p className="conversation-date">Your homepage journey · Full history</p>
        <article className="operator-message opening-message">
          <p>I started by auditing <strong>{sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name))}</strong>.</p>
          <small>Every result below was recorded as it happened. Your live site stayed untouched throughout.</small>
        </article>

        <SourceAuditMessage report={gate?.comparison} insights={sourceInsights} />
        <OpportunityMessage migration={migration} site={site} report={gate?.comparison} />

        {buildInsights.map((insight, index) => (
          <article className="operator-message result-message history-result-message" key={insight.stage}>
            <span className="message-check"><CheckIcon /></span>
            <div>
              <small>Step {index + 3} · {insight.label}</small>
              <strong>{insight.headline}</strong>
              <p>{insight.body}</p>
              {insight.facts.length > 0 && <ul>{insight.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>}
            </div>
          </article>
        ))}

        {legacyNestedPreview && (
          <article className="operator-message attention-message legacy-preview-message">
            <span><AlertIcon /></span>
            <div>
              <strong>This older preview was built from an inner page.</strong>
              <p>I can replace it with a fresh homepage build from the root URL. Your live website stays untouched.</p>
              <button type="button" onClick={onRestartHomepage}>Rebuild the homepage</button>
            </div>
          </article>
        )}

        <button className="operator-message result-message audit-result-message" type="button" onClick={onOpenAudit}>
          <span className="message-check"><CheckIcon /></span>
          <div>
            <small>SEO + GEO audit complete</small>
            <strong>Your new foundation is lighter and easier to understand.</strong>
            <div className="inline-audit-results">
              <span><small>Quality</small><strong>{score}<em>/100</em></strong></span>
              <span><small>SEO + GEO</small><strong>{seoGeoScore}<em>/100</em></strong></span>
              <span><small>Broken links</small><strong>{gate?.brokenLinks ?? 0}<em> remain</em></strong></span>
              <span><small>Images</small><strong>{optimization?.imagesOptimized ?? 0}<em> optimized</em></strong></span>
            </div>
            <span className="audit-open-label">See the full SEO + GEO audit <ArrowIcon /></span>
          </div>
        </button>

        {gate?.comparison && hasMeaningfulOptimizationGain(gate.comparison) && (
          <MigrationComparisonCard report={gate.comparison} />
        )}

        <article className="operator-message calm-success-message">
          <span className="message-spark"><SparkIcon /></span>
          <div>
            <strong>{reduction && reduction > 0 ? `Amazing — image weight is down ${reduction}%.` : "Amazing — the new homepage is already leaner."}</strong>
            <p>{optimization ? `${optimization.imagesSeen} images were checked, ${optimization.imagesOptimized} recompressed, and below-the-fold images now load only when needed.` : "Images use async decoding and lazy loading away from the first view."}</p>
          </div>
        </article>

        {attention && (
          <article className="operator-message attention-message">
            <span><AlertIcon /></span>
            <div><strong>One small point needs our attention.</strong><p>The automated build is ready, but our team should review {gate?.reviewReasons.length ?? 1} fidelity detail{(gate?.reviewReasons.length ?? 1) === 1 ? "" : "s"} before the final move.</p><button className="refresh-homepage-preview" type="button" onClick={onRestartHomepage}>Refresh from the live homepage</button></div>
          </article>
        )}

        <article className="operator-message opening-message ready-opening final-history-message">
          <span className="message-check"><CheckIcon /></span>
          <div><small>Step 5 · Private result</small><strong>Your homepage is ready.</strong><p>I rebuilt the page on a fast, maintainable foundation. Now you can judge the outcome, not a promise.</p></div>
        </article>

        {site.previewUrl && (
          <button className="homepage-ready-link" type="button" onClick={onOpenPreview}>
            <span className="preview-action-icon"><EyeIcon /></span>
            <span><small>Private homepage preview</small><strong>Explore it beside our conversation</strong></span>
            <span className="preview-action-label">Preview <ArrowIcon /></span>
          </button>
        )}

        <article className="operator-message offer-question">
          <div><strong>Would you like us to give it a UI/UX refresh so it converts more?</strong><p>Choose how you want to continue. Nothing is charged or published without confirmation.</p></div>
        </article>

        <div className="migration-offers">
          <button disabled={actionsDisabled} onClick={() => onSelectOffer("Yes — I want the €900 UI/UX refresh before migrating the homepage.")}>
            <span><strong>Yes, refresh it</strong><small>UI/UX and conversion pass</small></span><em>€900</em><ArrowIcon />
          </button>
          <button disabled={actionsDisabled} onClick={() => onSelectOffer("No redesign — move the approved homepage as-is for €300.")}>
            <span><strong>No, just move it</strong><small>Migrate the approved build as-is</small></span><em>€300</em><ArrowIcon />
          </button>
          <a href="mailto:ismael@siteagain.com?subject=SiteAgain%20homepage%20migration%20call">
            <span><strong>I prefer to talk first</strong><small>Book a short call with our team</small></span><em>Call</em><ArrowIcon />
          </a>
        </div>
      </div>
      <small className="source-line">Rebuilt from {sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name))}</small>
    </section>
  );
}

function SourceAuditMessage({
  report,
  insights,
}: {
  report: MigrationComparisonReportV1 | undefined;
  insights: MigrationConversationInsight[];
}) {
  const source = report?.source.metrics;
  const audit = insights.find((insight) => insight.stage === "audit");
  const seo = insights.find((insight) => insight.stage === "opportunity");
  return (
    <article className="operator-message result-message source-audit-message">
      <span className="message-check"><CheckIcon /></span>
      <div>
        <small>Step 1 · Current website audit</small>
        <strong>Here’s what I found on the website you have today.</strong>
        <p>{audit?.body ?? "I captured the current page and measured its search, structure and delivery signals before changing anything."}</p>
        <div className="source-audit-grid">
          <AuditMetric label="Performance" value={metricScore(source?.performanceScore)} detail="current score" />
          <AuditMetric label="SEO" value={metricScore(source?.seoScore)} detail="current score" />
          <AuditMetric label="Broken links" value={source?.brokenLinks == null ? "Checking" : String(source.brokenLinks)} detail="found" />
          <AuditMetric label="Missing assets" value={source?.missingAssets == null ? "Checking" : String(source.missingAssets)} detail="found" />
        </div>
        {seo?.facts.length ? <ul>{seo.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul> : null}
      </div>
    </article>
  );
}

function OpportunityMessage({
  migration,
  site,
  report,
}: {
  migration: MigrationRecord | null;
  site: SiteView;
  report?: MigrationComparisonReportV1 | undefined;
}) {
  const source = report?.source.metrics;
  const candidate = report?.candidate.metrics;
  const goal = typeof site.config.goal === "string" ? site.config.goal : "leads";
  const performanceTarget = candidate?.performanceScore == null ? "90+" : String(percentScore(candidate.performanceScore));
  const seoTargetValue = Math.max(
    source?.seoScore == null ? 0 : percentScore(source.seoScore),
    candidate?.seoScore == null ? 0 : percentScore(candidate.seoScore),
    migration?.seoScore == null ? 0 : percentScore(migration.seoScore),
  );
  const seoTarget = seoTargetValue > 0 ? String(seoTargetValue) : "90+";
  return (
    <article className="operator-message opportunity-message">
      <span className="message-spark"><SparkIcon /></span>
      <div>
        <small>Step 2 · The opportunity</small>
        <strong>Based on that audit, this is the outcome worth aiming for.</strong>
        <p>{goalOpportunityLead(goal)}</p>
        <div className="target-row">
          <span><small>Performance target</small><strong>{performanceTarget}<em>/100</em></strong></span>
          <ArrowIcon />
          <span><small>SEO + GEO target</small><strong>{seoTarget}<em>/100</em></strong></span>
        </div>
        <small className="target-note">Targets use the source baseline and the verified preview where available.</small>
      </div>
    </article>
  );
}

function AuditMetric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <span><small>{label}</small><strong>{value}</strong><em>{detail}</em></span>;
}

function metricScore(value: number | null | undefined): string {
  return value == null ? "Pending" : `${percentScore(value)}/100`;
}

function goalOpportunityLead(goal: string): string {
  if (goal === "traffic") return "We’ll protect the search signals that already work, close the discoverability gaps, and give every important page a faster route into search and AI answers.";
  if (goal === "maintenance") return "We’ll keep the visible experience intact while removing the weight and fragile dependencies that make every update slower and more expensive.";
  return "We’ll preserve what search engines already understand, make the page faster, and sharpen the path from first visit to qualified enquiry.";
}

const COMPARISON_METRICS: Array<{ key: keyof MigrationAuditMetricsV1; label: string; score: boolean }> = [
  { key: "visualFidelity", label: "Visual fidelity", score: true },
  { key: "seoScore", label: "SEO preservation", score: true },
  { key: "functionalPassRate", label: "Functional checks", score: true },
  { key: "performanceScore", label: "Performance budget", score: true },
  { key: "brokenLinks", label: "Broken links", score: false },
  { key: "missingAssets", label: "Missing assets", score: false },
];

function MigrationComparisonCard({ report }: { report: MigrationComparisonReportV1 }) {
  const outcomes = new Map(report.findings.map((finding) => [finding.metric, finding]));
  const candidateLabel = report.candidate.stage === "production" ? "Production" : "Preview";
  return (
    <article className="migration-comparison-card">
      <div className="comparison-heading">
        <span><small>Verified comparison</small><strong>Current → {candidateLabel}</strong></span>
        <em>{report.summary.wins} {report.summary.wins === 1 ? "gain" : "gains"} · {report.summary.losses} {report.summary.losses === 1 ? "drop" : "drops"}</em>
      </div>
      <div className="comparison-table" role="table" aria-label="Current and preview audit comparison">
        <div className="comparison-row comparison-labels" role="row">
          <span role="columnheader">Metric</span><span role="columnheader">Current</span><span role="columnheader">Preview</span><span role="columnheader">Delta</span>
        </div>
        {COMPARISON_METRICS.map(({ key, label, score }) => {
          const finding = outcomes.get(key);
          return (
            <div className="comparison-row" role="row" key={key}>
              <strong role="cell">{label}</strong>
              <span role="cell">{comparisonValue(report.source.metrics[key], score)}</span>
              <span role="cell">{comparisonValue(report.candidate.metrics[key], score)}</span>
              <em role="cell" className={finding?.outcome ?? "unmeasured"}>
                {finding ? comparisonDelta(finding.delta, score) : "—"}
              </em>
            </div>
          );
        })}
      </div>
      <small className="comparison-round">Best deterministic visual round: {report.selectedVisualRound ?? 0}</small>
    </article>
  );
}

function hasMeaningfulOptimizationGain(report: MigrationComparisonReportV1): boolean {
  return report.findings.some((finding) => {
    if (finding.outcome !== "win") return false;
    if (finding.metric === "performanceScore") return Math.abs(finding.delta) >= 0.15;
    if (["seoScore", "functionalPassRate", "visualFidelity"].includes(finding.metric)) {
      return Math.abs(finding.delta) >= 0.1;
    }
    if (finding.metric === "brokenLinks" || finding.metric === "missingAssets") {
      return Math.abs(finding.delta) >= 2;
    }
    return false;
  });
}

function comparisonValue(value: number | null, score: boolean): string {
  if (value === null) return "—";
  return score ? `${Math.round(value * 100)}/100` : String(value);
}

function comparisonDelta(delta: number, score: boolean): string {
  const value = score ? Math.round(delta * 100) : Math.round(delta);
  return `${value > 0 ? "+" : ""}${value}`;
}

function ChangeConversation({
  change,
  selected,
  onOpenProof,
  onDecision,
}: {
  change: ChangeView;
  selected: boolean;
  onOpenProof(): void;
  onDecision(action: "approve" | "reject" | "rollback"): void;
}) {
  const insights = changeConversationInsights(change);
  return (
    <section
      className={`change-conversation ${selected ? "selected" : ""}`}
      id={`conversation-change-${change.id}`}
      aria-current={selected ? "true" : undefined}
    >
      <div className="conversation-date">{formatConversationDate(change.createdAt)}</div>
      <article className="user-bubble">{change.requestText}</article>
      <article className="assistant-response">
        <div className="assistant-line"><span><Logo /></span><strong>SiteAgain</strong><i className={change.status === "awaiting_approval" || change.status === "deployed" ? "ready" : ""}>{shortStatus(change.status)}</i></div>
        <h1>{humanStatus(change.status)}</h1>
        <p>{statusExplanation(change)}</p>
        {insights.length > 0 && (
          <div className="journey-discoveries change-discoveries" role="log" aria-live="polite" aria-relevant="additions" aria-label="Change request updates">
            <div className="discoveries-heading"><span>Updates on your request</span><small>{insights.length} update{insights.length === 1 ? "" : "s"}</small></div>
            {[...insights].reverse().map((insight) => (
              <article className="discovery-message" key={insight.stage}>
                <span className="discovery-check"><CheckIcon /></span>
                <div>
                  <small>{insight.label}</small>
                  <strong>{insight.headline}</strong>
                  <p>{insight.body}</p>
                  {insight.facts.length > 0 && <ul>{insight.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>}
                </div>
              </article>
            ))}
          </div>
        )}
        <div className="change-progress">
          {CHANGE_PHASES.map((phase) => (
            <div className={changeProgressReached(change.status, phase.key) ? "reached" : ""} key={phase.key}>
              <span>{changeProgressReached(change.status, phase.key) && <CheckIcon />}</span><small>{phase.label}</small>
            </div>
          ))}
        </div>
        {(change.plan || change.previewUrl) && <button className="proof-cta" onClick={onOpenProof}><ProofIcon />View release proof<ArrowIcon /></button>}
        {selected && <DecisionBar change={change} onDecision={onDecision} compact />}
      </article>
    </section>
  );
}

function ChangeComposer({
  disabled,
  disabledReason,
  onSubmit,
}: {
  disabled: boolean;
  disabledReason: string;
  onSubmit(text: string): void;
}) {
  const [value, setValue] = useState("");
  return (
    <div className="composer-wrap">
      <form onSubmit={(event) => {
        event.preventDefault();
        const text = value.trim();
        if (!text) return;
        onSubmit(text);
        setValue("");
      }}>
        <textarea
          id="change-request"
          aria-label="Ask SiteAgain to change your site"
          rows={2}
          value={value}
          disabled={disabled}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder={disabled ? disabledReason : "Ask SiteAgain to change your site…"}
        />
        <div><span><ShieldIcon />Approval protected</span><button disabled={disabled || !value.trim()} aria-label="Send request"><ArrowUpIcon /></button></div>
      </form>
      <small>Enter to send · Shift + Enter for a new line</small>
    </div>
  );
}

function Sandbox({
  site,
  migration,
  change,
  usageMicroUsd,
  userEmail,
  tab,
  onTab,
  onClose,
  onDecision,
  onRecommendationPreview,
  onSelectFix,
  onRequestChange,
  changeBlocked,
}: {
  site: SiteView;
  migration: MigrationRecord | null;
  change: ChangeView | null;
  usageMicroUsd: number;
  userEmail: string;
  tab: StudioTab;
  onTab(tab: StudioTab): void;
  onClose(): void;
  onDecision(action: "approve" | "reject" | "rollback"): void;
  onRecommendationPreview(changeSetId: string): void;
  onSelectFix?: ((prompt: string) => void) | undefined;
  onRequestChange?: ((requestText: string) => void) | undefined;
  changeBlocked?: boolean | undefined;
}) {
  const candidateUrl = change?.previewUrl ?? null;
  const previewUrl = candidateUrl || (isMigrationPreviewReady(migration) ? site.previewUrl : null);
  const publicUrl = previewUrl ? publicPreviewUrl(previewUrl) : null;
  const embeddedUrl = previewUrl ? embeddedPreviewUrl(previewUrl) : null;

  return (
    <Tabs className="sandbox" value={tab} onValueChange={(value) => onTab(value as StudioTab)}>
      <header className="sandbox-header">
        <TabsList className="sandbox-tabs" aria-label="Studio view">
          <TabsTrigger value="preview"><EyeIcon />Preview</TabsTrigger>
          <TabsTrigger value="findings"><SparkIcon />SEO + GEO audit</TabsTrigger>
          <TabsTrigger value="proof"><ProofIcon />Release proof{change?.status === "awaiting_approval" && <i />}</TabsTrigger>
          <TabsTrigger value="proposal"><FileTextIcon />Plan</TabsTrigger>
          <TabsTrigger value="launch"><GlobeIcon />Launch</TabsTrigger>
          <TabsTrigger value="growth"><GrowthIcon />Growth</TabsTrigger>
        </TabsList>
        <div className="environment"><span />Sandbox</div>
        {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer">Open preview<ExternalIcon /></a>}
        <button className="close-preview" type="button" onClick={onClose} aria-label="Close preview" title="Focus on conversation"><XIcon /></button>
      </header>

      <TabsContent value="proof">
        <ProofView change={change} usageMicroUsd={usageMicroUsd} onDecision={onDecision} />
      </TabsContent>
      <TabsContent value="proposal">
        <MigrationPlanView site={site} />
      </TabsContent>
      <TabsContent value="launch">
        <LaunchView site={site} migration={migration} />
      </TabsContent>
      <TabsContent value="findings">
        <FindingsView site={site} migration={migration} onSelectFix={onSelectFix} />
      </TabsContent>

      <TabsContent value="growth">
        <GrowthView site={site} userEmail={userEmail} onPreviewCreated={onRecommendationPreview} onRequestChange={onRequestChange} changeBlocked={changeBlocked ?? false} />
      </TabsContent>
      <TabsContent value="preview">
        {publicUrl && embeddedUrl ? (
          <PreviewBrowser publicUrl={publicUrl} embeddedUrl={embeddedUrl} siteName={site.name} />
        ) : (

          <div className="browser-shell">
            <BrowserBar displayValue={sourceHost(migration?.sourceUrl ?? site.name)} />
            <SandboxPending migration={migration} site={site} />
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

function PreviewBrowser({
  publicUrl,
  embeddedUrl,
  siteName,
}: {
  publicUrl: string;
  embeddedUrl: string;
  siteName: string;
}) {
  const [reloadKey, setReloadKey] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    setStatus("loading");
    const timeout = window.setTimeout(() => setStatus((current) => current === "loading" ? "error" : current), 15_000);
    return () => window.clearTimeout(timeout);
  }, [embeddedUrl, reloadKey]);

  function reload() {
    setStatus("loading");
    setReloadKey((current) => current + 1);
  }

  async function verifyLoadedPreview() {
    try {
      const response = await fetch(embeddedUrl, { method: "HEAD", cache: "no-store" });
      setStatus(response.ok ? "ready" : "error");
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="browser-shell">
      <BrowserBar
        displayValue={displayUrl(publicUrl)}
        status={status}
        onReload={reload}
      />
      <div className="preview-viewport">
        <iframe
          key={`${embeddedUrl}:${reloadKey}`}
          className="site-frame"
          src={embeddedUrl}
          title={`${siteName} private preview`}
          onLoad={() => void verifyLoadedPreview()}
          onError={() => setStatus("error")}
        />
        {status !== "ready" && (
          <div className={`preview-state-overlay ${status}`} role="status">
            {status === "loading" ? <SpinnerIcon /> : <AlertIcon />}
            <strong>{status === "loading" ? "Connecting to your sandbox" : "The sandbox took too long to respond"}</strong>
            <span>{status === "loading" ? "Loading the private immutable build…" : "Your build is safe. Retry here or open it in a new tab."}</span>
            {status === "error" && (
              <div><button onClick={reload}><RefreshIcon />Retry</button><a href={publicUrl} target="_blank" rel="noreferrer">Open preview<ExternalIcon /></a></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BrowserBar({
  displayValue,
  status,
  onReload,
}: {
  displayValue: string;
  status?: "loading" | "ready" | "error";
  onReload?(): void;
}) {
  return (
    <div className="browser-bar">
      <div className="traffic-lights"><i /><i /><i /></div>
      <div className="address-bar"><LockIcon /><span>{displayValue}</span></div>
      {onReload ? <button aria-label="Reload preview" onClick={onReload}><RefreshIcon /></button> : <span />}
      {status && <span className={`preview-connection ${status}`}>{status === "ready" ? "Connected" : status === "error" ? "Offline" : "Connecting"}</span>}
    </div>
  );
}

function SandboxPending({ migration, site }: { migration: MigrationRecord | null; site: SiteView }) {
  const percent = migration?.progressPercent ?? 0;
  const failed = migration?.status === "failed" || site.status === "failed";
  return (
    <div className="sandbox-pending">
      <div className="blueprint-grid" />
      <div className="pending-preview-card">
        <div className="pending-logo"><Logo /><span style={{ width: `${Math.max(6, percent)}%` }} /></div>
        <small>{failed ? "Preview needs attention" : "Building private preview"}</small>
        <h2>{failed ? "The comparison didn’t publish." : `${percent}% ready`}</h2>
        <p>{failed ? migrationFailureMessage(percent) : migration?.progressMessage || "The sandbox will appear here as soon as the quality gates pass."}</p>
        <div className="skeleton-site" aria-hidden="true"><span /><span /><span /><span /></div>
      </div>
    </div>
  );
}

function ProofView({
  change,
  usageMicroUsd,
  onDecision,
}: {
  change: ChangeView | null;
  usageMicroUsd: number;
  onDecision(action: "approve" | "reject" | "rollback"): void;
}) {
  if (!change) {
    return (
      <div className="proof-empty">
        <div><ProofIcon /></div><small>Release proof</small><h1>Nothing to prove yet.</h1>
        <p>Ask for a change. The exact plan, files, preview, quality checks, and approval controls will collect here.</p>
        <ul><li><CheckIcon />Exact before and after</li><li><CheckIcon />Build and SEO checks</li><li><CheckIcon />Human approval boundary</li></ul>
      </div>
    );
  }

  return (
    <div className="proof-view">
      <div className="proof-title"><div><small>Release proof · {change.id.slice(-8)}</small><h1>{change.requestText}</h1></div><StatusPill status={change.status} /></div>
      <div className="proof-grid">
        <section className="proof-section plan-proof">
          <div className="section-number">01</div><div><small>Plan</small><h2>{change.plan?.summary ?? "Planning the safest implementation…"}</h2></div>
          {change.plan && <><div className="proof-metrics"><Metric label="Risk" value={change.plan.risk} /><Metric label="Scope" value={change.plan.scope.replaceAll("_", " ")} /><Metric label="Files" value={String(change.plan.files.length)} /></div><div className="file-list">{change.plan.files.map((file) => <code key={file.path}>{file.path}</code>)}</div><p className="seo-note"><ShieldIcon />{change.plan.seoImpact}</p></>}
        </section>

        <section className="proof-section quality-proof">
          <div className="section-number">02</div><div><small>Quality</small><h2>Hard gates before production</h2></div>
          <div className="quality-list">
            <QualityRow label="Build" value={change.buildResult?.passed ? "Passed" : "Pending"} passed={change.buildResult?.passed === true} />
            <QualityRow label="Functional" value={change.buildResult?.functional ? `${change.buildResult.functional.passed}/${change.buildResult.functional.total} passed` : "Pending"} passed={change.buildResult?.functional != null && change.buildResult.functional.brokenLinks.length === 0 && change.buildResult.functional.missingAssets.length === 0} />
            <QualityRow label="SEO contract" value={change.seoValidationResult ? (change.seoValidationResult.blockingFindings.length === 0 ? "Passed" : "Blocked") : "Pending"} passed={change.seoValidationResult != null && change.seoValidationResult.blockingFindings.length === 0} />
            <QualityRow label="Visual match" value={change.visualDiff?.fidelityPercent == null ? "Not scored" : `${change.visualDiff.fidelityPercent}%`} passed={change.visualDiff?.fidelityPercent != null} />
            <QualityRow label="Run cost" value={`$${(usageMicroUsd / 1_000_000).toFixed(4)}`} />
          </div>
          {change.visualDiff?.captures && change.visualDiff.captures.length > 1 && (
            <div className="capture-list">
              {change.visualDiff.captures.map((capture) => (
                <a key={`${capture.path}:${capture.viewport ?? "legacy"}`} href={capture.diffImageUrl ?? capture.afterUrl} target="_blank" rel="noreferrer">
                  <code>{capture.path}{capture.viewport ? ` · ${capture.viewport}` : ""}</code>
                  <span>{capture.fidelityPercent == null ? "Capture unavailable" : `${capture.fidelityPercent}% match`}</span>
                </a>
              ))}
            </div>
          )}
        </section>

        <section className="proof-section changes-proof">
          <div className="section-number">03</div><div><small>Exact changes</small><h2>{change.appliedChanges.length ? `${change.appliedChanges.length} verified edit${change.appliedChanges.length === 1 ? "" : "s"}` : "Waiting for the build…"}</h2></div>
          <div className="diff-list">{change.appliedChanges.map((edit, index) => <div className="diff-card" key={`${edit.file}:${index}`}><code>{edit.file}</code><p className="before"><span>−</span>{edit.before}</p><p className="after"><span>+</span>{edit.after}</p></div>)}</div>
        </section>

        <section className="proof-section preview-proof">
          <div className="section-number">04</div><div><small>Preview</small><h2>Inspect the candidate</h2></div>
          {change.previewUrl ? <a className="big-preview-link" href={publicPreviewUrl(change.previewUrl)} target="_blank" rel="noreferrer"><EyeIcon /><span><small>Private candidate</small><strong>Open full preview</strong></span><ExternalIcon /></a> : <div className="proof-pending">Preview will unlock after validation.</div>}
        </section>
      </div>
      <DecisionBar change={change} onDecision={onDecision} />
    </div>
  );
}

function DecisionBar({
  change,
  onDecision,
  compact = false,
}: {
  change: ChangeView;
  onDecision(action: "approve" | "reject" | "rollback"): void;
  compact?: boolean;
}) {
  if (change.status === "awaiting_approval") {
    return (
      <div className={`decision-bar ${compact ? "compact" : ""}`}>
        <p><ShieldIcon /><span><strong>You control production</strong><small>All hard gates passed.</small></span></p>
        <div><button className="secondary" onClick={() => onDecision("reject")}>Reject</button><button className="primary" onClick={() => onDecision("approve")}><CheckIcon />Approve & publish</button></div>
      </div>
    );
  }
  if (change.status === "deployed") {
    return <div className={`decision-bar deployed ${compact ? "compact" : ""}`}><p><CheckIcon /><span><strong>Published</strong><small>The previous version is retained.</small></span></p><button className="secondary" onClick={() => onDecision("rollback")}>Roll back</button></div>;
  }
  return null;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="metric"><small>{label}</small><strong>{value}</strong></div>;
}

function QualityRow({ label, value, passed }: { label: string; value: string; passed?: boolean }) {
  return <div><span className={passed ? "passed" : ""}>{passed ? <CheckIcon /> : <i />}</span><strong>{label}</strong><em>{value}</em></div>;
}

function StatusDot({ status }: { status: string }) {
  return <span className={`status-dot ${status}`} title={shortStatus(status)} />;
}

function StatusPill({ status }: { status: string }) {
  return <span className={`status-pill ${status}`}><StatusDot status={status} />{shortStatus(status)}</span>;
}

function LoadingScreen() {
  return <div className="loading-screen"><div className="loading-mark"><Logo /><span /></div><p>Preparing your workspace</p></div>;
}

async function api<T = Record<string, unknown>>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, init);
  const body = (await response.json()) as T & ApiErrorPayload;
  if (!response.ok) throw new Error(body.error?.message ?? `Request failed (${response.status})`);
  return body;
}

function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return "";
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname.includes(".")) return "";
    return url.toString();
  } catch {
    return "";
  }
}

function hasNestedPreviewPath(value: string | null): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    if (isPreviewRouterHostname(url.hostname)) {
      return url.pathname.split("/").filter(Boolean).length > 2;
    }
    return isPreviewHostname(url.hostname) && url.pathname !== "/";
  } catch {
    return false;
  }
}

function isMigrationPreviewReady(migration: MigrationRecord | null): boolean {
  return migration?.status === "preview_ready" || migration?.status === "completed";
}

function publicPreviewUrl(value: string): string {
  try {
    const url = new URL(value);
    if (isPreviewRouterHostname(url.hostname)) {
      const gatewayHostname = previewHostnameFromGatewayPath(url.pathname);
      return import.meta.env.DEV && gatewayHostname?.endsWith(".site.localhost")
        ? `${url.pathname}${url.search}${url.hash}`
        : url.toString();
    }
    const routerOrigin = url.hostname.endsWith(".site-staging.nodash.fr")
      ? LEGACY_PREVIEW_ROUTER_ORIGIN
      : url.hostname.endsWith(".site.siteagain.com")
        ? PRODUCTION_PREVIEW_ROUTER_ORIGIN
        : PREVIEW_ROUTER_ORIGIN;
    if (!isPreviewHostname(url.hostname)) return url.toString();
    return `${routerOrigin}${previewGatewayPath(url)}`;
  } catch {
    return value;
  }
}

function embeddedPreviewUrl(value: string): string {
  try {
    const url = new URL(value);
    if (isPreviewRouterHostname(url.hostname)) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    if (!isPreviewHostname(url.hostname)) return url.toString();
    return previewGatewayPath(url);
  } catch {
    return value;
  }
}

function previewGatewayPath(url: URL): string {
  const pathname = url.pathname.startsWith("/") ? url.pathname : `/${url.pathname}`;
  return `/p/${encodeURIComponent(url.hostname)}${pathname}${url.search}${url.hash}`;
}

function previewHostnameFromGatewayPath(pathname: string): string | null {
  const encodedHostname = /^\/p\/([^/]+)/u.exec(pathname)?.[1];
  if (!encodedHostname) return null;
  try {
    return decodeURIComponent(encodedHostname);
  } catch {
    return null;
  }
}

function isPreviewHostname(hostname: string): boolean {
  return hostname.endsWith(".site-staging.siteagain.com")
    || hostname.endsWith(".site.siteagain.com")
    || hostname.endsWith(".site-staging.nodash.fr");
}

function isPreviewRouterHostname(hostname: string): boolean {
  return hostname === new URL(PREVIEW_ROUTER_ORIGIN).hostname
    || hostname === new URL(PRODUCTION_PREVIEW_ROUTER_ORIGIN).hostname
    || hostname === new URL(LEGACY_PREVIEW_ROUTER_ORIGIN).hostname;
}

function displayUrl(value: string): string {
  try {
    const url = new URL(value);
    return `${url.hostname}${url.pathname === "/" ? "" : url.pathname}`;
  } catch {
    return value;
  }
}

function sourceHost(value: string): string {
  try {
    return new URL(normalizeUrl(value)).hostname.replace(/^www\./, "");
  } catch {
    return value;
  }
}

function websiteHost(site: SiteView): string {
  const value = String(site.config.sourceUrl ?? site.name);
  try {
    return new URL(normalizeUrl(value)).hostname;
  } catch {
    return value;
  }
}

function qualityScore(migration: MigrationRecord | null): number | null {
  if (!migration) return null;
  const values = [migration.seoScore, migration.fidelityScore]
    .filter((value): value is number => typeof value === "number")
    .map((value) => value <= 1 ? value * 100 : value);
  if (!values.length) return null;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

interface MigrationConversationInsight {
  stage: (typeof MIGRATION_STAGES)[number]["key"];
  label: string;
  headline: string;
  body: string;
  facts: string[];
}

function migrationConversationInsights(
  migration: MigrationRecord | null,
  jobs: JobRecord[],
  site: SiteView,
): MigrationConversationInsight[] {
  const percent = migration?.progressPercent ?? 0;
  const host = sourceHost(String(site.config.sourceUrl ?? migration?.sourceUrl ?? site.name));
  const goal = typeof site.config.goal === "string" ? site.config.goal : "leads";
  const insights: MigrationConversationInsight[] = [];
  const crawl = completedJobResult(jobs, "crawl_site");

  if (percent >= 15 || crawl) {
    const pages = arraySize(crawl?.pages);
    const assets = arraySize(crawl?.assets);
    const errors = arraySize(crawl?.errors);
    insights.push({
      stage: "audit",
      label: "Audit result",
      headline: errors > 0
        ? `I reached ${host}, with ${errors} item${errors === 1 ? "" : "s"} to watch.`
        : `I reached ${host} and mapped the page successfully.`,
      body: pages > 0
        ? `I inspected ${quantity(pages, "page")}, followed its structure, and captured the source evidence I’ll use for the comparison.`
        : "The site is reachable. I captured the page you shared and mapped the source evidence needed for a fair comparison.",
      facts: [
        ...(pages > 0 ? [`${quantity(pages, "page")} inspected`] : []),
        ...(assets > 0 ? [`${quantity(assets, "linked asset")} discovered`] : []),
        ...(errors === 0 && crawl ? ["No crawl errors"] : []),
      ],
    });
  }

  const seo = completedJobResult(jobs, "seo_inventory");
  if (percent >= 42 || seo) {
    const totals = recordValue(seo?.totals);
    const indexable = numberField(totals, "indexableUrls");
    const titles = numberField(totals, "titles");
    const descriptions = numberField(totals, "descriptions");
    const schemas = numberField(totals, "schemas");
    const internalLinks = numberField(totals, "internalLinks");
    const gaps = [
      ...(indexable > titles ? ["page titles"] : []),
      ...(indexable > descriptions ? ["search descriptions"] : []),
      ...(schemas === 0 ? ["structured data"] : []),
      ...(internalLinks === 0 ? ["internal discovery paths"] : []),
    ];
    insights.push({
      stage: "opportunity",
      label: "What we can improve",
      headline: opportunityHeadline(gaps, goal),
      body: gaps.length > 0
        ? `The clearest gaps are ${joinNatural(gaps)}. ${goalOpportunityAction(goal)}`
        : `The essential page-level search signals are present. ${goalOpportunityAction(goal)}`,
      facts: [
        ...(indexable > 0 ? [`${titles}/${indexable} page titles`, `${descriptions}/${indexable} search descriptions`] : []),
        ...(schemas >= 0 ? [`${quantity(schemas, "structured-data block")}`] : []),
        ...(internalLinks >= 0 ? [`${quantity(internalLinks, "internal link")}`] : []),
      ].slice(0, 3),
    });
  }

  const build = completedJobResult(jobs, "build_project");
  if (percent >= 65 || build) {
    const artifact = recordValue(build?.artifact);
    const files = arraySize(artifact?.files);
    const warnings = arraySize(build?.warnings);
    insights.push({
      stage: "first-page",
      label: "Homepage built",
      headline: build?.passed === false
        ? "The first build exposed an issue, so I stopped it safely."
        : "Your new homepage builds cleanly in isolation.",
      body: build?.passed === false
        ? "The isolated build did not pass, so nothing moved on to preview and your live website remained unchanged."
        : "I turned the source page into a maintainable candidate and produced a deployable build without changing your live website.",
      facts: [
        ...(build?.passed === true ? ["Production build passed"] : []),
        ...(files > 0 ? [`${quantity(files, "generated file")}`] : []),
        ...(build ? [warnings === 0 ? "No build warnings" : quantity(warnings, "build warning")] : []),
      ],
    });
  }

  const gate = migration?.qualityGateReport;
  if (percent >= 96 || gate) {
    const seoPreservation = gate ? percentScore(gate.seoPreservation) : null;
    const passed = gate?.functionalTests.passed ?? 0;
    const total = gate?.functionalTests.total ?? 0;
    const gatePassed = gate && ["PASS", "PASS_WITH_WARNINGS"].includes(gate.verdict);
    insights.push({
      stage: "verify",
      label: "New page audit",
      headline: gatePassed ? "The candidate passed the safety checks." : "I finished checking the candidate.",
      body: gate
        ? `SEO preservation is ${seoPreservation}% and ${passed} of ${total} functional checks passed. I also checked links and copied assets before preparing the preview.`
        : "I checked the build, links, assets and important search signals before allowing the preview to move forward.",
      facts: gate ? [
        `${seoPreservation}% SEO preserved`,
        `${passed}/${total} functional checks`,
        gate.brokenLinks === 0 ? "No broken links" : quantity(gate.brokenLinks, "broken link"),
      ] : [],
    });
  }

  if (percent >= 100 || migration?.status === "preview_ready") {
    insights.push({
      stage: "preview",
      label: "Homepage ready",
      headline: "Your homepage is ready to explore.",
      body: "The comparison is private and your current website is still untouched. You can review the result and ask for changes before making any decision.",
      facts: ["Private preview", "Live site unchanged"],
    });
  }

  return insights;
}

function completedJobResult(jobs: JobRecord[], task: string): Record<string, unknown> | null {
  const job = jobs.filter((candidate) => candidate.task === task && candidate.status === "completed").at(-1);
  return recordValue(job?.result);
}

interface ImageOptimizationSummary {
  imagesSeen: number;
  imagesOptimized: number;
  bytesBefore: number;
  bytesAfter: number;
}

function imageOptimizationSummary(jobs: JobRecord[]): ImageOptimizationSummary | null {
  const build = completedJobResult(jobs, "build_project");
  const value = recordValue(build?.imageOptimization);
  if (!value) return null;
  const imagesSeen = numberField(value, "imagesSeen");
  const imagesOptimized = numberField(value, "imagesOptimized");
  const bytesBefore = numberField(value, "bytesBefore");
  const bytesAfter = numberField(value, "bytesAfter");
  return [imagesSeen, imagesOptimized, bytesBefore, bytesAfter].every((item) => item >= 0)
    ? { imagesSeen, imagesOptimized, bytesBefore, bytesAfter }
    : null;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function arraySize(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function numberField(record: Record<string, unknown> | null, key: string): number {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : -1;
}

function percentScore(value: number): number {
  return Math.round(value <= 1 ? value * 100 : value);
}

function quantity(value: number, noun: string): string {
  return `${value} ${noun}${value === 1 ? "" : "s"}`;
}

function joinNatural(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
}

function opportunityHeadline(gaps: string[], goal: string): string {
  if (gaps.includes("structured data")) return "Search and AI engines need clearer context.";
  if (gaps.includes("search descriptions") || gaps.includes("page titles")) return "Your search result can explain the value more clearly.";
  if (gaps.includes("internal discovery paths")) return "The page needs a clearer path to the next action.";
  if (goal === "maintenance") return "The strongest gain is easier, safer maintenance.";
  if (goal === "traffic") return "The search foundation is solid enough to build on.";
  return "The strongest gain is a clearer route from visit to enquiry.";
}

function goalOpportunityAction(goal: string): string {
  if (goal === "maintenance") return "The rebuild is focusing on a structure that makes future edits faster and safer.";
  if (goal === "traffic") return "The rebuild is making the page easier for search and AI engines to interpret.";
  return "The rebuild is making the offer and the next action easier for the right visitor to understand.";
}

interface ChangeConversationInsight {
  stage: string;
  label: string;
  headline: string;
  body: string;
  facts: string[];
}

function changeConversationInsights(change: ChangeView): ChangeConversationInsight[] {
  const insights: ChangeConversationInsight[] = [];

  if (change.plan) {
    const operations = change.plan.operations.length;
    const files = change.plan.files.length;
    insights.push({
      stage: "plan",
      label: "01 · Plan ready",
      headline: change.plan.summary,
      body: `I mapped the safest ${change.plan.scope.replaceAll("_", " ")} approach before editing anything. ${change.plan.seoImpact}`,
      facts: [
        `${change.plan.risk} risk`,
        quantity(files, "file"),
        quantity(operations, "planned edit"),
      ],
    });
  }

  if (change.appliedChanges.length > 0) {
    const files = new Set(change.appliedChanges.map((edit) => edit.file)).size;
    const editTypes = [...new Set(change.appliedChanges.map((edit) => edit.type.replaceAll("_", " ")))];
    insights.push({
      stage: "editing",
      label: "02 · Edits complete",
      headline: `I made ${quantity(change.appliedChanges.length, "verified edit")} in isolation.`,
      body: `${change.appliedChanges[0]?.reasoning ?? "I applied the requested update against the private candidate."} Your published version stayed untouched.`,
      facts: [quantity(files, "file"), ...editTypes.slice(0, 2)],
    });
  }

  if (change.buildResult || change.seoValidationResult) {
    const buildPassed = change.buildResult?.passed === true;
    const seoPassed = change.seoValidationResult?.blockingFindings.length === 0;
    const functional = change.buildResult?.functional;
    const checksPassed = buildPassed && (change.seoValidationResult ? seoPassed : true);
    insights.push({
      stage: "validation",
      label: "03 · Safety checks complete",
      headline: checksPassed ? "The candidate passed its build and SEO checks." : "A safety check found something that needs attention.",
      body: checksPassed
        ? "I verified the private build before asking you to review it. The release proof contains the exact edits and evidence."
        : "I stopped the candidate from moving forward automatically. Your published version is unchanged.",
      facts: [
        ...(change.buildResult ? [buildPassed ? "Build passed" : "Build stopped"] : []),
        ...(functional ? [`${functional.passed}/${functional.total} functional checks`] : []),
        ...(change.seoValidationResult ? [seoPassed ? `${percentScore(change.seoValidationResult.score)}% SEO score` : quantity(change.seoValidationResult.blockingFindings.length, "SEO blocker")] : []),
        ...(change.visualDiff?.fidelityPercent != null ? [`${percentScore(change.visualDiff.fidelityPercent)}% visual match`] : []),
      ].slice(0, 3),
    });
  }

  if (change.previewUrl && !["deployed", "rejected", "rolled_back"].includes(change.status)) {
    insights.push({
      stage: "preview",
      label: "04 · Private candidate ready",
      headline: "Your update is ready for a decision.",
      body: "I published an isolated preview for review. Nothing goes live unless you approve it.",
      facts: ["Approval protected", "Live version unchanged"],
    });
  }

  if (change.status === "deployed") {
    insights.push({
      stage: "decision",
      label: "05 · Approved and published",
      headline: "Your approved change is live.",
      body: "I published only the version you approved. The previous version remains available if you need to roll back.",
      facts: ["Approval recorded", "Rollback available"],
    });
  } else if (change.status === "rejected") {
    insights.push({
      stage: "decision",
      label: "05 · Candidate rejected",
      headline: "I left the live version exactly as it was.",
      body: "The private candidate will not be published. The findings and release proof remain available for reference.",
      facts: ["Nothing published", "Live version unchanged"],
    });
  } else if (change.status === "rolled_back") {
    insights.push({
      stage: "decision",
      label: "06 · Rollback complete",
      headline: "The previous version is live again.",
      body: "I restored the retained version and kept the change history intact so the decision remains traceable.",
      facts: ["Previous version restored", "History retained"],
    });
  } else if (change.status === "failed") {
    insights.push({
      stage: "stopped",
      label: "Stopped safely",
      headline: changeFailureHeadline(change),
      body: changeFailureDetail(change),
      facts: ["Nothing published", "Live version unchanged"],
    });
  }

  return insights;
}

function changeFailureHeadline(change: ChangeView): string {
  if (change.seoValidationResult?.blockingFindings.length) return "SEO protection stopped this candidate.";
  if (change.buildResult?.passed === false) return "The private build did not pass.";
  if (change.appliedChanges.length > 0) return "I couldn’t verify the edited candidate safely.";
  if (change.plan) return "I couldn’t apply the plan safely.";
  return "I couldn’t complete this request safely.";
}

function changeFailureDetail(change: ChangeView): string {
  if (change.seoValidationResult?.blockingFindings.length) {
    return `I found ${quantity(change.seoValidationResult.blockingFindings.length, "blocking SEO issue")} and stopped before publication. Your live version is unchanged.`;
  }
  if (change.buildResult?.passed === false) return "The isolated build failed, so it never reached preview or publication. Your live version is unchanged.";
  return "I stopped before anything could be published. The completed plan and evidence remain available above.";
}

function migrationFailureHeading(percent: number): string {
  if (percent >= 96) return "The work is ready, but the preview didn’t publish.";
  if (percent >= 65) return "I found an issue during the final checks.";
  if (percent >= 42) return "I paused the rebuild safely.";
  return "I couldn’t complete the audit safely.";
}

function migrationFailureMessage(percent: number): string {
  if (percent >= 96) return "The audit, rebuild and safety checks completed. Only the private comparison failed to publish; your live website is unchanged.";
  if (percent >= 65) return "I kept the completed audit and rebuild, then stopped before showing you a candidate that had not passed every check. Your live website is unchanged.";
  if (percent >= 42) return "I kept the findings gathered so far and stopped before an incomplete rebuild could move forward. Your live website is unchanged.";
  return "I stopped without making any changes to your live website. The completed findings remain visible below.";
}

function migrationStageHeading(percent: number): string {
  if (percent < 15) return "I’m auditing what you have.";
  if (percent < 42) return "I’m finding the biggest opportunities.";
  if (percent < 65) return "Let’s build your homepage.";
  if (percent < 96) return "I’m checking every detail.";
  return "Your comparison is almost ready.";
}

function migrationStageMessage(percent: number, site: SiteView): string {
  const host = sourceHost(String(site.config.sourceUrl ?? site.name));
  if (percent < 15) return `Looking at how ${host} performs, how search engines read it, and how maintainable it is.`;
  if (percent < 42) return "Turning technical findings into improvements that could create more visibility, conversions and independence.";
  if (percent < 65) return "Rebuilding the page you shared on a fast, maintainable architecture while preserving its important search signals.";
  if (percent < 96) return "Testing the build, links, assets and SEO preservation before I show you anything.";
  return "Preparing a private preview. Your live website has not been changed.";
}

function messageFor(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}

function humanStatus(status: string): string {
  const labels: Record<string, string> = {
    requested: "I’ve got your request.", planning: "I’m planning the safest change.", editing: "I’m rebuilding it in isolation.", validating: "I’m checking every detail.", preview_ready: "Your preview is ready.", awaiting_approval: "Ready for your decision.", approved: "Your approval is recorded.", deploying: "I’m publishing the new version.", deployed: "Your change is live.", rejected: "This version was rejected.", failed: "I stopped before anything unsafe happened.", rolled_back: "The previous version is restored.",
  };
  return labels[status] ?? status;
}

function statusExplanation(change: ChangeView): string {
  if (change.status === "awaiting_approval") return "The update passed the build and SEO gates. Review the proof, then decide whether it should go live.";
  if (change.status === "deployed") return "The approved version is active. The previous version is still intact if you need to roll back.";
  if (change.status === "failed") return changeFailureDetail(change);
  if (change.status === "rejected") return "The candidate will not be published. Your live site remains unchanged.";
  if (change.status === "rolled_back") return "The retained previous version is active again.";
  return "You can leave this page. I’ll add the plan, exact edits, preview, and quality results as they become ready.";
}

function changeProgressReached(current: string, target: string): boolean {
  const order = ["requested", "planning", "editing", "validating", "preview_ready", "awaiting_approval", "approved", "deploying", "deployed"];
  if (["failed", "rejected", "rolled_back"].includes(current)) return false;
  return order.indexOf(current) >= order.indexOf(target);
}

function shortStatus(status: string): string {
  const labels: Record<string, string> = { active: "Ready", migrating: "Migrating", awaiting_approval: "Ready to review", preview_ready: "Preview ready", rolled_back: "Rolled back", deployed: "Live", completed: "Complete", failed: "Stopped", needs_human: "Needs review" };
  return labels[status] ?? status.replaceAll("_", " ");
}

function relativeTime(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
}

function formatConversationDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" }).format(new Date(value));
}

function Logo() { return <svg className="logo" viewBox="0 0 32 32" aria-hidden="true"><path d="M3 27V5h6l14 14V5h6v22h-6L9 13v14H3Z" /></svg>; }
function ArrowIcon() { return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 5l5 5-5 5" /></svg>; }
function ArrowUpIcon() { return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 15V5M5.5 9.5 10 5l4.5 4.5" /></svg>; }
function ShieldIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 20 6v5c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-3Z" /><path d="m8.5 12 2.2 2.2 4.8-5" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4.2 4.2L19 7" /></svg>; }
function PlusIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>; }
function GlobeIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9S14.5 18.5 12 21c-2.5-2.5-3.8-5.5-3.8-9S9.5 5.5 12 3Z" /></svg>; }
function ExternalIcon() { return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M8 5h7v7M15 5 7 13" /><path d="M13 10v5H5V7h5" /></svg>; }
function ProofIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h11l3 3v15H5V3Z" /><path d="M15 3v4h4M8 12l2.2 2.2L16 9M8 18h8" /></svg>; }
function FileTextIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>; }

const ANONYMOUS_STORAGE_KEY = "siteagain_anonymous_onboarding_v1";

/**
 * The pre-signup journey, persisted so it survives the round trip through
 * authentication (ONB-002). `auditJobId` plus `auditToken` are the capability
 * the account later trades in to inherit the finished assessment instead of
 * paying for the same crawl twice.
 */
interface AnonymousState {
  anonymousId: string;
  submittedUrl: string;
  goal: OnboardingGoal | null;
  siteId: string | null;
  auditJobId: string | null;
  auditToken: string | null;
  createdAt: string;
  email?: string | null;
  name?: string | null;
  /** True only after the emailed verification link was opened. */
  emailExchanged?: boolean;
  /** Set when a verification link was requested, so the gate can say so. */
  verificationSentTo?: string | null;
  verifiedAt?: string | null;
}

/** A stale audit is worse than none: the report itself expires server-side. */
const ANONYMOUS_STATE_MAX_AGE_MS = 24 * 60 * 60_000;

function getAnonymousSession(): AnonymousState | null {
  try {
    const raw = sessionStorage.getItem(ANONYMOUS_STORAGE_KEY) || localStorage.getItem(ANONYMOUS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AnonymousState>;
    if (typeof parsed?.submittedUrl !== "string" || typeof parsed.anonymousId !== "string") return null;
    if (Date.now() - Date.parse(parsed.createdAt ?? "") > ANONYMOUS_STATE_MAX_AGE_MS) {
      clearAnonymousSession();
      return null;
    }
    return {
      anonymousId: parsed.anonymousId,
      submittedUrl: parsed.submittedUrl,
      goal: parsed.goal ?? null,
      siteId: parsed.siteId ?? null,
      auditJobId: parsed.auditJobId ?? null,
      auditToken: parsed.auditToken ?? null,
      createdAt: parsed.createdAt ?? new Date().toISOString(),
      email: typeof parsed.email === "string" ? parsed.email : null,
      name: typeof parsed.name === "string" ? parsed.name : null,
      emailExchanged: parsed.emailExchanged === true,
      verificationSentTo: typeof parsed.verificationSentTo === "string" ? parsed.verificationSentTo : null,
      verifiedAt: typeof parsed.verifiedAt === "string" ? parsed.verifiedAt : null,
    };
  } catch {
    return null;
  }
}

/**
 * Reads the handoff the marketing site sends as `?url=…&goal=…`, seeds the
 * anonymous journey from it, and clears the query so a refresh does not restart
 * a paid audit. Returns whatever journey is now in play.
 */
function adoptEntryParameters(): AnonymousState | null {
  const existing = getAnonymousSession();
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return existing;
  }
  const rawUrl = params.get("url");
  if (!rawUrl) return existing;

  const submittedUrl = normalizeUrl(rawUrl);
  const rawGoal = params.get("goal");
  const goal = ONBOARDING_GOALS.some((option) => option.value === rawGoal)
    ? rawGoal as OnboardingGoal
    : null;

  params.delete("url");
  params.delete("goal");
  const query = params.toString();
  window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);

  if (!submittedUrl) return existing;
  // The same visitor returning with the same URL keeps their audit.
  if (existing && existing.submittedUrl === submittedUrl) {
    if (goal && existing.goal !== goal) {
      const updated = { ...existing, goal };
      saveAnonymousSession(updated);
      return updated;
    }
    return existing;
  }

  const state: AnonymousState = {
    anonymousId: crypto.randomUUID(),
    submittedUrl,
    goal,
    siteId: null,
    auditJobId: null,
    auditToken: null,
    createdAt: new Date().toISOString(),
    email: null,
    name: null,
    emailExchanged: false,
  };
  saveAnonymousSession(state);
  void sendTelemetry("url_submitted", { goal, source: "marketing" }, state.anonymousId);
  return state;
}

/**
 * The verification link emailed by `POST /audit/{jobId}/unlock` carries a
 * one-time token in `?unlock=`. It is read once and exchanged for the report;
 * the token never becomes part of the persisted journey.
 */
function unlockTokenFromLocation(): string | null {
  try {
    const token = new URLSearchParams(window.location.search).get("unlock");
    return token && /^[a-f0-9]{64}$/.test(token) ? token : null;
  } catch {
    return null;
  }
}

function clearUnlockParam(): void {
  try {
    // A report address keeps its permanent link, so a reload, a bookmark, or a
    // copied address bar opens the report again on any device.
    if (reportJobIdFromLocation()) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("unlock")) return;
    params.delete("unlock");
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  } catch {}
}

/** `/r/<jobId>` is the dedicated address of one audit's report. */
function reportJobIdFromLocation(): string | null {
  try {
    const match = /^\/r\/(job_[A-Za-z0-9_-]{1,80})\/?$/u.exec(window.location.pathname);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function reportHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function saveAnonymousSession(state: AnonymousState) {
  try {
    const serialized = JSON.stringify(state);
    sessionStorage.setItem(ANONYMOUS_STORAGE_KEY, serialized);
    localStorage.setItem(ANONYMOUS_STORAGE_KEY, serialized);
  } catch {}
}

function clearAnonymousSession() {
  try {
    sessionStorage.removeItem(ANONYMOUS_STORAGE_KEY);
    localStorage.removeItem(ANONYMOUS_STORAGE_KEY);
  } catch {}
}

async function sendTelemetry(event: string, metadata?: Record<string, unknown>, anonymousId?: string, siteId?: string) {
  try {
    await fetch("/api/telemetry/funnel", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event,
        anonymousId: anonymousId || getAnonymousSession()?.anonymousId,
        siteId,
        timestamp: new Date().toISOString(),
        metadata,
      }),
    });
  } catch {}
}

interface PublicAuditStart {
  audit: { jobId: string; status: string; poll: string; authorization: { scheme: string; token: string } };
}

interface PublicAuditPoll {
  audit: {
    jobId: string;
    status: "pending" | "running" | "completed" | "failed" | "dead_lettered";
    result: SiteAgainAssessmentReportV1 | null;
    error: Record<string, unknown> | null;
  };
}

/**
 * First value before an account exists.
 *
 * A visitor who arrives from the marketing site with a URL gets their real
 * assessment here, without signing in — the API admits this route on an HMAC
 * capability and an IP rate limit rather than a session. Signing in afterwards
 * carries the finished report into their workspace instead of re-running it.
 */
type AccessMode = "invite_only" | "open";

function AuditEmailGate({
  host,
  jobId,
  auditToken,
  goal,
  locale,
  verificationSentTo,
  onVerificationSent,
}: {
  host: string;
  jobId: string;
  auditToken: string;
  goal: OnboardingGoal | null;
  locale: Locale;
  /** Set when this browser already asked for a link; the gate then shows "check your inbox". */
  verificationSentTo: string | null;
  onVerificationSent(email: string, name: string | null): void;
}) {
  const t = assessmentCopy(locale);
  const [email, setEmail] = useState(verificationSentTo ?? "");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(verificationSentTo !== null);
  const [resent, setResent] = useState(false);

  async function requestLink(cleanEmail: string): Promise<void> {
    const response = await fetch(`/api/audit/${jobId}/unlock`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Audit ${auditToken}`,
      },
      body: JSON.stringify({
        email: cleanEmail,
        name: name.trim() || undefined,
        locale: locale === "fr" ? "fr" : "en",
        ...(goal ? { goal } : {}),
        consent: true,
        source: sent ? "report_gate_resend" : "report_gate",
      }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
      throw new Error(payload?.error?.message ?? "The verification email could not be sent");
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError(locale === "fr" ? "Veuillez saisir une adresse e-mail valide." : "Please enter a valid email address.");
      return;
    }
    if (!consent) {
      setError(locale === "fr" ? "Le consentement est requis pour envoyer l'évaluation." : "Consent is required to send your assessment.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await requestLink(cleanEmail);
      if (sent) {
        setResent(true);
      } else {
        setSent(true);
        onVerificationSent(cleanEmail, name.trim() || null);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The verification email could not be sent");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <section className="audit-email-gate" aria-labelledby="gate-title">
        <div className="gate-card">
          <header className="gate-header">
            <span className="gate-badge"><SparkIcon /> {t.emailGate.badge}</span>
            <h2 id="gate-title">{t.emailGate.sentTitle}</h2>
            <p className="gate-subtitle">{t.emailGate.sentBody(email.trim(), host)}</p>
          </header>
          <div className="gate-preview-signals" aria-label="Audit summary">
            {t.scanSteps.map((step) => (
              <div key={step} className="gate-signal-pill">
                <CheckIcon />
                <span>{step}</span>
              </div>
            ))}
          </div>
          <form onSubmit={handleSubmit} className="gate-form" noValidate>
            {error && <p className="inline-error" role="alert"><AlertIcon /> {error}</p>}
            {resent && !error && <p className="gate-resent" role="status"><CheckIcon /> {t.emailGate.resendDone}</p>}
            <button className="audit-submit gate-submit" type="submit" disabled={busy}>
              {busy ? <SpinnerIcon /> : <MailIcon />}
              <span>{busy ? t.emailGate.resending : t.emailGate.resendButton}</span>
              <ArrowIcon />
            </button>
          </form>
          <footer className="gate-footer">
            <small>{t.emailGate.lockedNotice}</small>
          </footer>
        </div>
      </section>
    );
  }

  return (
    <section className="audit-email-gate" aria-labelledby="gate-title">
      <div className="gate-card">
        <header className="gate-header">
          <span className="gate-badge"><SparkIcon /> {t.emailGate.badge}</span>
          <h2 id="gate-title">{t.emailGate.unlockTitle}</h2>
          <p className="gate-subtitle">{t.emailGate.unlockSubtitle(host)}</p>
        </header>

        <div className="gate-preview-signals" aria-label="Audit summary">
          {t.scanSteps.map((step) => (
            <div key={step} className="gate-signal-pill">
              <CheckIcon />
              <span>{step}</span>
            </div>
          ))}
        </div>

        <div className="gate-locked-banner">
          <svg className="gate-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <p>{t.emailGate.lockedNotice}</p>
        </div>

        <form onSubmit={handleSubmit} className="gate-form" noValidate>
          <div className="gate-fields">
            <div className="gate-field">
              <label htmlFor="gate-email">{t.emailGate.emailLabel}</label>
              <input
                id="gate-email"
                type="email"
                required
                autoComplete="email"
                placeholder={t.emailGate.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="gate-field">
              <label htmlFor="gate-name">{t.emailGate.nameLabel}</label>
              <input
                id="gate-name"
                type="text"
                autoComplete="name"
                placeholder={t.emailGate.namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <label className="gate-consent">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={busy}
            />
            <span>{t.emailGate.consentText}</span>
          </label>

          {error && <p className="inline-error" role="alert"><AlertIcon /> {error}</p>}

          <button className="audit-submit gate-submit" type="submit" disabled={busy}>
            {busy ? <SpinnerIcon /> : <SparkIcon />}
            <span>{busy ? t.emailGate.submitting : t.emailGate.unlockButton}</span>
            <ArrowIcon />
          </button>
        </form>
      </div>
    </section>
  );
}

/**
 * The Pilot application (funnel step 10). It is only rendered when the report
 * classified the site as a strong candidate, and it carries the same audit
 * capability that unlocked the report so the API can verify the site it is
 * qualifying. Four answers, no essay.
 */
function PilotApplyForm({
  jobId,
  auditToken,
  email,
  name,
  goal,
  locale,
}: {
  jobId: string;
  auditToken: string;
  email: string;
  name: string | null;
  goal: OnboardingGoal | null;
  locale: Locale;
}) {
  const t = assessmentCopy(locale);
  const [horizonGoal, setHorizonGoal] = useState("");
  const [siteManager, setSiteManager] = useState<PilotSiteManager>("me");
  const [gscAccess, setGscAccess] = useState<PilotYesNo>("yes");
  const [capacity, setCapacity] = useState<PilotYesNo>("yes");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (horizonGoal.trim().length < 3) {
      setError(t.pilotApply.error);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/pilot-applications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name ?? email.split("@")[0],
          email,
          locale: locale === "fr" ? "fr" : "en",
          ...(goal ? { goal } : {}),
          consent: true,
          source: "report_pilot_apply",
          horizonGoal: horizonGoal.trim(),
          siteManager,
          gscAccess,
          implementationCapacity: capacity,
          audit: { jobId, token: auditToken },
        }),
      });
      const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
      if (!response.ok) throw new Error(payload?.error?.message ?? t.pilotApply.error);
      setDone(true);
      void sendTelemetry("pilot_applied", { jobId });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t.pilotApply.error);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="pilot-apply-done" role="status">
        <CheckIcon />
        <div>
          <strong>{t.pilotApply.doneTitle}</strong>
          <p>{t.pilotApply.doneBody(email)}</p>
        </div>
      </div>
    );
  }

  return (
    <form className="pilot-apply" onSubmit={handleSubmit}>
      <div className="pilot-apply-head">
        <strong>{t.pilotApply.title}</strong>
        <p>{t.pilotApply.lede}</p>
      </div>
      <label className="pilot-apply-field">
        <span>{t.pilotApply.horizonLabel}</span>
        <input
          type="text"
          maxLength={500}
          required
          placeholder={t.pilotApply.horizonPlaceholder}
          value={horizonGoal}
          onChange={(event) => setHorizonGoal(event.target.value)}
          disabled={busy}
        />
      </label>
      <div className="pilot-apply-row">
        <label className="pilot-apply-field">
          <span>{t.pilotApply.managerLabel}</span>
          <select value={siteManager} onChange={(event) => setSiteManager(event.target.value as PilotSiteManager)} disabled={busy}>
            {(["me", "developer", "agency", "nobody"] as const).map((option) => (
              <option key={option} value={option}>{t.pilotApply.managerOptions[option]}</option>
            ))}
          </select>
        </label>
        <label className="pilot-apply-field">
          <span>{t.pilotApply.gscLabel}</span>
          <select value={gscAccess} onChange={(event) => setGscAccess(event.target.value as PilotYesNo)} disabled={busy}>
            {(["yes", "no"] as const).map((option) => (
              <option key={option} value={option}>{t.pilotApply.gscOptions[option]}</option>
            ))}
          </select>
        </label>
        <label className="pilot-apply-field">
          <span>{t.pilotApply.capacityLabel}</span>
          <select value={capacity} onChange={(event) => setCapacity(event.target.value as PilotYesNo)} disabled={busy}>
            {(["yes", "no"] as const).map((option) => (
              <option key={option} value={option}>{t.pilotApply.capacityOptions[option]}</option>
            ))}
          </select>
          <small>{t.pilotApply.capacityHint}</small>
        </label>
      </div>
      {error && <p className="inline-error" role="alert"><AlertIcon /> {error}</p>}
      <button className="audit-submit" type="submit" disabled={busy || horizonGoal.trim().length < 3}>
        {busy ? <SpinnerIcon /> : <ArrowIcon />}
        <span>{busy ? t.pilotApply.submitting : t.pilotApply.submit}</span>
      </button>
    </form>
  );
}

function ScanEmailPrompt({
  jobId,
  auditToken,
  goal,
  locale,
  onSent,
}: {
  jobId: string | null;
  auditToken: string | null;
  goal: OnboardingGoal | null;
  locale: Locale;
  onSent(email: string): void;
}) {
  const t = assessmentCopy(locale);
  const [email, setEmail] = useState("");
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) return;
    if (!jobId || !auditToken) {
      setError(locale === "fr" ? "L'analyse démarre encore ; réessayez dans un instant." : "The scan is still starting; try again in a moment.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/audit/${jobId}/unlock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Audit ${auditToken}`,
        },
        body: JSON.stringify({
          email: cleanEmail,
          locale: locale === "fr" ? "fr" : "en",
          ...(goal ? { goal } : {}),
          consent: true,
          source: "scan_prompt",
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
        throw new Error(payload?.error?.message ?? "The verification email could not be sent");
      }
      // Only a confirmed delivery may claim the email was saved.
      setSavedTo(cleanEmail);
      onSent(cleanEmail);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The verification email could not be sent");
    } finally {
      setBusy(false);
    }
  }

  if (savedTo) {
    return (
      <div className="scan-email-prompt-saved" role="status">
        <span className="scan-saved-badge" aria-hidden="true"><CheckIcon /></span>
        <span className="scan-saved-message">{t.emailGate.scanSaved(savedTo)}</span>
      </div>
    );
  }

  return (
    <div className="scan-email-prompt">
      <p>{t.emailGate.scanPrompt}</p>
      <form onSubmit={handleSubmit} className="scan-email-form">
        <input
          type="email"
          placeholder={t.emailGate.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={busy}
        />
        <button type="submit" disabled={busy || !email.trim()}>
          {busy ? <SpinnerIcon /> : <SparkIcon />}
          <span>{t.emailGate.scanButton}</span>
        </button>
      </form>
      {error && <p className="inline-error" role="alert"><AlertIcon /> {error}</p>}
    </div>
  );
}

function PublicAssessment({
  state,
  onState,
  onSignIn,
  onRestart,
}: {
  state: AnonymousState;
  onState(next: AnonymousState): void;
  onSignIn(): void;
  onRestart(): void;
}) {
  const [report, setReport] = useState<SiteAgainAssessmentReportV1 | null>(null);
  const [status, setStatus] = useState<"starting" | "running" | "ready" | "failed">("starting");
  const [error, setError] = useState("");
  const locale = useLocale();
  const t = assessmentCopy(locale);
  const host = sourceHost(state.submittedUrl);

  function handleVerificationSent(email: string, name: string | null) {
    const next: AnonymousState = {
      ...state,
      email,
      name,
      verificationSentTo: email,
    };
    saveAnonymousSession(next);
    onState(next);
    void sendTelemetry("verification_sent", { host }, state.anonymousId);
  }

  useEffect(() => {
    let active = true;
    let timer = 0;

    async function poll(jobId: string, token: string): Promise<void> {
      const response = await fetch(`/api/audit/${jobId}`, { headers: { Authorization: `Audit ${token}` } });
      if (!response.ok) throw new Error(`The assessment could not be read (${response.status})`);
      const body = (await response.json()) as PublicAuditPoll;
      if (!active) return;
      if (body.audit.status === "completed" && body.audit.result) {
        setReport(body.audit.result);
        setStatus("ready");
        void sendTelemetry("assessment_ready", { host }, state.anonymousId);
        return;
      }
      if (body.audit.status === "failed" || body.audit.status === "dead_lettered") {
        setStatus("failed");
        setError(assessmentCopy(getLocale()).scanFailedGeneric);
        void sendTelemetry("assessment_failed", { host }, state.anonymousId);
        return;
      }
      setStatus("running");
      timer = window.setTimeout(() => void poll(jobId, token).catch(fail), 3_000);
    }

    function fail(reason: unknown) {
      if (!active) return;
      setStatus("failed");
      setError(messageFor(reason));
    }

    async function run() {
      try {
        if (state.auditJobId && state.auditToken) {
          await poll(state.auditJobId, state.auditToken);
          return;
        }
        // Keyed on the visitor and the URL so a refresh replays the same audit
        // rather than starting — and paying for — a second crawl.
        const started = await api<PublicAuditStart>("/audit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": `audit-${state.anonymousId}`,
          },
          body: JSON.stringify({
            url: state.submittedUrl,
            locale: getLocale(),
            ...(state.goal ? { goal: state.goal } : {}),
          }),
        });
        if (!active) return;
        const next = {
          ...state,
          auditJobId: started.audit.jobId,
          auditToken: started.audit.authorization.token,
        };
        saveAnonymousSession(next);
        onState(next);
        void sendTelemetry("assessment_started", { host, goal: state.goal }, state.anonymousId);
        await poll(started.audit.jobId, started.audit.authorization.token);
      } catch (reason) {
        fail(reason);
      }
    }

    void run();
    return () => { active = false; window.clearTimeout(timer); };
    // The audit is identified by the visitor and their URL; re-running on every
    // stored-state change would start a second crawl.
  }, [state.anonymousId, state.submittedUrl]);

  return (
    <main className="jr public-journey">
      <JourneyPublicTopBar host={host} onRestart={onRestart} onSignIn={onSignIn} reportReady={status === "ready"} />

      {status !== "ready" && status !== "failed" && (
        <div aria-live="polite">
          <JourneyLiveCheck
            host={host}
            startedAt={state.createdAt}
            saveCard={!(state.emailExchanged || state.verificationSentTo) ? (
              <div className="jr-save">
                <span className="jr-eyebrow" style={{ color: "#a39e94" }}>Keep your results</span>
                <h3>Save my report</h3>
                <p>We will email the full report and a link to come back. No password, no newsletter.</p>
                <ScanEmailPrompt
                  jobId={state.auditJobId}
                  auditToken={state.auditToken}
                  goal={state.goal}
                  locale={locale}
                  onSent={(email) => handleVerificationSent(email, null)}
                />
                <small>Or wait here — the answer opens on its own.</small>
              </div>
            ) : (
              <div className="jr-save">
                <span className="jr-eyebrow" style={{ color: "#a39e94" }}>Saved</span>
                <h3>We will email you the report.</h3>
                <p>Confirm the link in your inbox to unlock the full evidence.</p>
                <span className="jr-chip" style={{ background: "#2a2926", color: "#e6e1d8" }}>{state.verificationSentTo ?? state.email ?? "your inbox"}</span>
              </div>
            )}
          />
        </div>
      )}

      {status === "failed" && (
        <div className="jr-wrap">
          <div className="jr-card pad">
            <span className="jr-eyebrow">Website check</span>
            <h1 className="jr-h1 small">{t.scanFailed(host)}</h1>
            <p className="jr-lede">{t.scanFailedDetail}</p>
            {error && <p className="jr-error" style={{ marginTop: 14 }}><AlertIcon />{error}</p>}
            <div className="jr-row" style={{ marginTop: 18 }}>
              <button className="jr-btn" type="button" onClick={onRestart}><ArrowIcon />{t.tryAnother}</button>
            </div>
          </div>
        </div>
      )}

      {status === "ready" && report && (
        !state.emailExchanged ? (
          state.auditJobId && state.auditToken ? (
            <div className="jr-wrap">
              <div className="jr-report-head">
                <span className="jr-eyebrow">Your website check · {host}</span>
              </div>
              <AuditEmailGate
                host={host}
                jobId={state.auditJobId}
                auditToken={state.auditToken}
                goal={state.goal}
                locale={locale}
                verificationSentTo={state.verificationSentTo ?? null}
                onVerificationSent={handleVerificationSent}
              />
            </div>
          ) : (
            <div className="jr-wrap">
              <div className="jr-card pad"><p className="jr-muted">{t.emailGate.lockedNotice}</p></div>
            </div>
          )
        ) : (
          <JourneyReportView
            report={report}
            host={host}
            onBuildPreview={onSignIn}
            buildLabel="Sign in to choose my direction →"
            onPreviewFix={onSignIn}
            onSignIn={onSignIn}
            fullReport={
              <AssessmentReportView
                report={report}
                host={host}
                showLanguageSwitch={false}
                onStartGrowth={onSignIn}
                {...(state.auditJobId && state.auditToken && state.email
                  ? {
                      pilotSlot: (
                        <PilotApplyForm
                          jobId={state.auditJobId}
                          auditToken={state.auditToken}
                          email={state.email}
                          name={state.name ?? null}
                          goal={state.goal}
                          locale={locale}
                        />
                      ),
                    }
                  : {})}
              />
            }
          />
        )
      )}
    </main>
  );
}

function JourneyPublicTopBar({
  host,
  onRestart,
  onSignIn,
  reportReady,
}: {
  host: string;
  onRestart(): void;
  onSignIn(): void;
  reportReady: boolean;
}) {
  return (
    <header className="jr-top">
      <div className="jr-top-left">
        <button className="jr-logo" type="button" onClick={onRestart}>
          <span className="jr-logo-mark">S</span>
          <span>SiteAgain</span>
        </button>
        <span className="jr-site-pill"><i className="dot" /><span className="host">{host}</span></span>
      </div>
      <nav className="jr-steps" aria-label="Your progress">
        <span className={`jr-step ${reportReady ? "done" : "now"}`}>
          <span className="n">{reportReady ? <CheckIcon /> : 1}</span>Check
        </span>
        <span className="jr-step"><span className="n">2</span>Choose</span>
        <span className="jr-step"><span className="n">3</span>Preview</span>
        <span className="jr-step"><span className="n">4</span>Go live</span>
        <span className="jr-step"><span className="n">5</span>Grow</span>
      </nav>
      <div className="jr-top-right">
        <span className="jr-public-note">Free · no card · live site untouched</span>
        <LanguageSwitch />
        <button className="jr-signin" type="button" onClick={onSignIn}>Sign in</button>
      </div>
    </header>
  );
}

interface SiteAuditSummary {
  id: string;
  siteId: string;
  sourceUrl: string;
  status: "running" | "ready" | "blocked" | "failed";
  version: string;
  pageCount: number;
  undecidedUrlCount: number;
  criticalUnresolvedCount: number;
  createdAt: string;
  completedAt: string | null;
}

interface ProposalSummary {
  id: string;
  siteId: string;
  auditId: string;
  revision: number;
  status: string;
  supersededById: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  expiresAt: string;
  createdAt: string;
  pricing: { projectFixedPriceMicroUsd: number; ongoingMonthlyMicroUsd: number; currency: string };
}

const AUDIT_TERMINAL = new Set(["ready", "blocked", "failed"]);
const PROPOSAL_OPEN = new Set(["draft", "sent", "viewed"]);

function usd(microUsd: number): string {
  return `$${Math.round(microUsd / 1_000_000).toLocaleString()}`;
}

/**
 * The commercial half of the journey, in the order a customer meets it:
 * an indicative quote from the homepage assessment, then the full-site audit
 * that turns it into a real scope, then the acceptance contract they sign.
 *
 * Every stage refuses to render numbers it cannot source. A proposal without a
 * ready audit is not shown as provisional — it is not generated at all.
 */
function MigrationPlanView({ site }: { site: SiteView }) {
  const [indicative, setIndicative] = useState<MigrationProposal | null>(null);
  const [indicativeIssue, setIndicativeIssue] = useState("");
  const [audits, setAudits] = useState<SiteAuditSummary[]>([]);
  const [report, setReport] = useState<FullSiteAuditReportV1 | null>(null);
  const [proposals, setProposals] = useState<ProposalSummary[]>([]);
  const [contract, setContract] = useState<MigrationAcceptanceContractV1 | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const audit = audits[0] ?? null;
  const currentProposal = proposals.find((item) => item.supersededById === null) ?? proposals[0] ?? null;

  const loadAudits = useCallback(async () => {
    const response = await api<{ audits: SiteAuditSummary[] }>(`/sites/${site.id}/audits`);
    setAudits(response.audits);
    return response.audits;
  }, [site.id]);

  const loadReport = useCallback(async (auditId: string) => {
    const response = await api<{ audit: SiteAuditSummary; report: FullSiteAuditReportV1 | null }>(`/audits/${auditId}`);
    setReport(response.report);
    setAudits((current) => current.map((item) => item.id === response.audit.id ? response.audit : item));
    return response.audit;
  }, []);

  const loadProposals = useCallback(async () => {
    const response = await api<{ proposals: ProposalSummary[] }>(`/sites/${site.id}/proposals`);
    setProposals(response.proposals);
    return response.proposals;
  }, [site.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setReport(null);
    setContract(null);
    void (async () => {
      // The indicative quote is optional: a site whose assessment has not
      // finished simply has no quote yet, which is not an error to shout about.
      const [quote, auditList, proposalList] = await Promise.all([
        api<{ proposal: MigrationProposal }>(`/sites/${site.id}/proposal`)
          .then((response) => response.proposal)
          .catch((reason: unknown) => { if (active) setIndicativeIssue(messageFor(reason)); return null; }),
        loadAudits().catch((reason: unknown) => { if (active) setError(messageFor(reason)); return []; }),
        loadProposals().catch(() => [] as ProposalSummary[]),
      ]);
      if (!active) return;
      setIndicative(quote);
      const latest = auditList[0];
      if (latest) await loadReport(latest.id).catch(() => null);
      const open = proposalList.find((item) => item.supersededById === null) ?? proposalList[0];
      if (open) await loadContract(open.id).catch(() => null);
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [site.id, loadAudits, loadProposals, loadReport]);

  // A running audit settles on the queue; poll until it does.
  useEffect(() => {
    if (!audit || AUDIT_TERMINAL.has(audit.status)) return;
    const timer = window.setInterval(() => {
      void loadReport(audit.id).catch(() => null);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [audit?.id, audit?.status, loadReport]);

  async function loadContract(proposalId: string) {
    const response = await api<{ contract: MigrationAcceptanceContractV1 }>(`/proposals/${proposalId}`);
    setContract(response.contract);
  }

  async function run(label: string, operation: () => Promise<void>) {
    setBusy(label);
    setError("");
    try {
      await operation();
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setBusy("");
    }
  }

  function startFullAudit() {
    return run("audit", async () => {
      setNotice("Crawling the whole site. This takes longer than the homepage scan.");
      await api(`/sites/${site.id}/audits`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": `full-audit-${crypto.randomUUID()}` },
        body: "{}",
      });
      const list = await loadAudits();
      if (list[0]) await loadReport(list[0].id);
    });
  }

  function decideUrl(legacyUrl: string, decision: "unchanged" | "redirect" | "removed", destination?: string) {
    if (!audit) return Promise.resolve();
    return run(`url:${legacyUrl}`, async () => {
      const response = await api<{ readiness: FullSiteAuditReportV1["readiness"] }>(
        `/audits/${audit.id}/url-decisions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decisions: [{
              legacyUrl,
              decision,
              ...(destination ? { destination } : {}),
              ...(decision === "redirect" ? { redirectStatus: 301 } : {}),
              reason: "Decided by the site owner in the migration plan",
            }],
          }),
        },
      );
      setNotice(response.readiness.status === "ready"
        ? "Every URL is decided. You can generate the acceptance contract."
        : "Decision saved.");
      await loadReport(audit.id);
    });
  }

  function decideIntegration(itemId: string, verdict: "pass" | "fail" | "review", note: string) {
    if (!audit) return Promise.resolve();
    return run(`integration:${itemId}`, async () => {
      await api(`/audits/${audit.id}/integration-decisions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, verdict, note }),
      });
      await loadReport(audit.id);
    });
  }

  function generateContract() {
    if (!audit) return Promise.resolve();
    return run("contract", async () => {
      const created = await api<{ proposal: ProposalSummary }>(`/audits/${audit.id}/proposals`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": `proposal-${audit.id}-${crypto.randomUUID()}`,
        },
        ...(currentProposal ? { body: JSON.stringify({ supersedesId: currentProposal.id }) } : { body: "{}" }),
      });
      await loadProposals();
      await loadContract(created.proposal.id);
      setNotice("Contract generated from the accepted audit.");
    });
  }

  function decideProposal(transition: "accept" | "decline", acceptedBy?: string) {
    if (!currentProposal) return Promise.resolve();
    return run(`proposal:${transition}`, async () => {
      await api(`/proposals/${currentProposal.id}/transitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transition,
          actorType: "customer",
          ...(acceptedBy ? { acceptedBy } : {}),
        }),
      });
      await loadProposals();
      await loadContract(currentProposal.id);
      setNotice(transition === "accept" ? "Contract accepted. Scheduling follows." : "Contract declined.");
    });
  }

  if (loading) return <div className="proof-pending"><SpinnerIcon /> Loading your migration plan…</div>;

  const undecided = report?.urlContract.entries.filter((entry) => entry.decision === "undecided") ?? [];
  const unresolvedIntegrations = report?.operational.items.filter((item) => item.critical && item.verdict !== "pass") ?? [];

  return (
    <div className="plan-view">
      {error && <div className="rail-alert error" role="alert"><AlertIcon /><span>{error}</span><button onClick={() => setError("")}>×</button></div>}
      {notice && !error && <div className="rail-alert"><span className="working-dot" /><span>{notice}</span></div>}

      <header className="findings-header">
        <div>
          <small>Migration plan · {websiteHost(site)}</small>
          <h1>From a homepage proof to an agreed migration</h1>
          <p>Three steps: an indicative range, a full-site audit that fixes the scope, then a contract you accept.</p>
        </div>
      </header>

      <section className="plan-stage">
        <div className="audit-section-heading">
          <span><small>01</small><strong>Indicative range</strong></span>
          <p>From the homepage assessment only — not a quote.</p>
        </div>
        {indicative
          ? (
            <div className="plan-indicative">
              <div className="proof-metrics">
                <Metric label="Pages estimated" value={String(indicative.scope.estimatedPages)} />
                <Metric label="Timeline" value={`${indicative.timelineWeeks.min}–${indicative.timelineWeeks.max} weeks`} />
                <Metric label="Indicative price" value={`$${indicative.pricing.projectFixedPriceUsd.toLocaleString()}`} />
                <Metric label="Growth" value={`$${indicative.pricing.ongoingMonthlyUsd}/mo`} />
              </div>
              <p className="plan-caveat">
                <AlertIcon /> Estimated from one page. The full-site audit below replaces every number here with a
                counted one before anything is agreed.
              </p>
            </div>
          )
          : <p className="assessment-empty">{indicativeIssue || "No indicative range yet — the homepage assessment has to finish first."}</p>}
      </section>

      <section className="plan-stage">
        <div className="audit-section-heading">
          <span><small>02</small><strong>Full-site audit</strong></span>
          <p>Counts every URL and every behaviour that has to survive the move.</p>
        </div>

        {!audit && (
          <div className="plan-start">
            <p>Nothing has been crawled beyond your homepage yet.</p>
            <button className="audit-submit" type="button" disabled={busy === "audit"} onClick={() => void startFullAudit()}>
              {busy === "audit" ? <SpinnerIcon /> : <SparkIcon />}
              <span>{busy === "audit" ? "Starting the crawl…" : "Run the full-site audit"}</span>
            </button>
          </div>
        )}

        {audit && !AUDIT_TERMINAL.has(audit.status) && (
          <div className="proof-pending"><SpinnerIcon /> Crawling {sourceHost(audit.sourceUrl)} — {audit.pageCount} page(s) so far.</div>
        )}

        {audit && audit.status === "failed" && (
          <div className="proof-pending error"><AlertIcon /> The audit failed. Run it again to retry.</div>
        )}

        {report && (
          <>
            <div className="plan-inventory">
              <div className="proof-metrics">
                <Metric label="Pages crawled" value={String(report.crawl.pageCount)} />
                <Metric label="URLs in contract" value={String(report.urlContract.totals.total)} />
                <Metric label="Undecided" value={String(report.urlContract.totals.undecided)} />
                <Metric label="Critical unresolved" value={String(report.operational.totals.criticalUnresolved)} />
              </div>
              {report.crawl.truncated && (
                <p className="plan-caveat">
                  <AlertIcon /> The crawl stopped at its {report.crawl.maxPages}-page ceiling, so this inventory is
                  incomplete and the scope stays provisional.
                </p>
              )}
            </div>

            <div className={`plan-readiness ${report.readiness.status}`}>
              <strong>{report.readiness.status === "ready" ? "Ready to contract" : "Blocked"}</strong>
              {report.readiness.blockers.length > 0 && (
                <ul>
                  {report.readiness.blockers.map((blocker) => (
                    <li key={blocker.code}>
                      {blocker.message}
                      {blocker.urls.length > 0 && <small> · {blocker.urls.length} URL(s)</small>}
                    </li>
                  ))}
                </ul>
              )}
              {report.readiness.warnings.length > 0 && (
                <ul className="warnings">{report.readiness.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
              )}
            </div>

            {undecided.length > 0 && (
              <div className="plan-decisions">
                <h3>{undecided.length} URL{undecided.length === 1 ? "" : "s"} need a decision</h3>
                <p>Every legacy URL has to be kept, redirected, or explicitly removed. Nothing is dropped silently.</p>
                <ul className="url-decision-list">
                  {undecided.slice(0, 25).map((entry) => (
                    <li key={entry.legacyUrl}>
                      <div>
                        <code>{shortUrl(entry.legacyUrl)}</code>
                        <small>{entry.reason}</small>
                      </div>
                      <div className="url-decision-actions">
                        <button
                          type="button"
                          disabled={busy === `url:${entry.legacyUrl}`}
                          onClick={() => void decideUrl(entry.legacyUrl, "unchanged")}
                        >Keep</button>
                        <button
                          type="button"
                          disabled={busy === `url:${entry.legacyUrl}`}
                          onClick={() => {
                            const destination = window.prompt(`Redirect ${entry.legacyUrl} to which path?`, entry.destination ?? "/");
                            if (destination) void decideUrl(entry.legacyUrl, "redirect", destination);
                          }}
                        >Redirect…</button>
                        <button
                          type="button"
                          className="danger"
                          disabled={busy === `url:${entry.legacyUrl}`}
                          onClick={() => {
                            if (window.confirm(`Remove ${entry.legacyUrl}? Anyone linking to it will get a 404.`)) {
                              void decideUrl(entry.legacyUrl, "removed");
                            }
                          }}
                        >Remove</button>
                      </div>
                    </li>
                  ))}
                </ul>
                {undecided.length > 25 && <small>Showing the first 25 of {undecided.length}.</small>}
              </div>
            )}

            {unresolvedIntegrations.length > 0 && (
              <div className="plan-decisions">
                <h3>{unresolvedIntegrations.length} critical behaviour{unresolvedIntegrations.length === 1 ? "" : "s"} to resolve</h3>
                <ul className="integration-decision-list">
                  {unresolvedIntegrations.map((item) => (
                    <li key={item.id}>
                      <div>
                        <strong>{item.name}</strong>
                        <span className={`status-pill ${item.verdict}`}>{item.verdict}</span>
                        <p>{item.detail}</p>
                        <small>Plan: {item.migrationPlan}</small>
                        {item.customerActionRequired && <small className="action-required">You need to: {item.customerActionRequired}</small>}
                      </div>
                      <div className="url-decision-actions">
                        <button
                          type="button"
                          disabled={busy === `integration:${item.id}`}
                          onClick={() => {
                            const note = window.prompt(`Accept "${item.name}" as handled? Say why — the note is kept with the contract.`);
                            if (note) void decideIntegration(item.id, "pass", note);
                          }}
                        >Accept plan</button>
                        <button
                          type="button"
                          disabled={busy === `integration:${item.id}`}
                          onClick={() => {
                            const note = window.prompt(`Flag "${item.name}" for expert review. What is the concern?`);
                            if (note) void decideIntegration(item.id, "review", note);
                          }}
                        >Needs review</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      <section className="plan-stage">
        <div className="audit-section-heading">
          <span><small>03</small><strong>Acceptance contract</strong></span>
          <p>Generated from the audit above. Nothing here is estimated from a single page.</p>
        </div>

        {!contract && (
          <div className="plan-start">
            <p>
              {report?.readiness.status === "ready"
                ? "The audit is ready. Generating the contract fixes scope, price, timeline, and the URL guarantee."
                : "A contract can only be generated once the audit is ready and every blocker above is cleared."}
            </p>
            <button
              className="audit-submit"
              type="button"
              disabled={busy === "contract" || report?.readiness.status !== "ready"}
              onClick={() => void generateContract()}
            >
              {busy === "contract" ? <SpinnerIcon /> : <FileTextIcon />}
              <span>{busy === "contract" ? "Generating…" : "Generate the acceptance contract"}</span>
            </button>
          </div>
        )}

        {contract && currentProposal && (
          <ContractView
            contract={contract}
            proposal={currentProposal}
            busy={busy}
            onRegenerate={() => void generateContract()}
            onDecide={(transition, acceptedBy) => void decideProposal(transition, acceptedBy)}
          />
        )}
      </section>
    </div>
  );
}

function ContractView({
  contract,
  proposal,
  busy,
  onRegenerate,
  onDecide,
}: {
  contract: MigrationAcceptanceContractV1;
  proposal: ProposalSummary;
  busy: string;
  onRegenerate(): void;
  onDecide(transition: "accept" | "decline", acceptedBy?: string): void;
}) {
  const decidable = PROPOSAL_OPEN.has(proposal.status);
  const expired = Date.parse(proposal.expiresAt) < Date.now();

  return (
    <div className="contract-view">
      <header className="contract-header">
        <div>
          <small>Revision {contract.revision} · {proposal.id.slice(-8)}</small>
          <h2>{usd(contract.pricing.projectFixedPriceMicroUsd)} fixed, {contract.timeline.minWeeks}–{contract.timeline.maxWeeks} weeks</h2>
        </div>
        <StatusPill status={proposal.status} />
      </header>

      <div className="contract-grid">
        <section className="contract-card">
          <h3>Scope</h3>
          <div className="proof-metrics">
            <Metric label="Pages" value={String(contract.scope.pageCount)} />
            <Metric label="URLs kept" value={String(contract.urls.unchanged)} />
            <Metric label="Redirects" value={String(contract.urls.redirect)} />
            <Metric label="Removed" value={String(contract.urls.removed)} />
          </div>
          <p className="contract-guarantee"><ShieldIcon /> {contract.urls.guarantee}</p>
          {contract.scope.provisional && (
            <p className="plan-caveat"><AlertIcon /> Scope is provisional: the crawl was truncated.</p>
          )}
          {contract.scope.excludedRoutes.length > 0 && (
            <details>
              <summary>{contract.scope.excludedRoutes.length} route(s) deliberately excluded <ChevronDownIcon /></summary>
              <ul>{contract.scope.excludedRoutes.map((route) => <li key={route.route}><code>{route.route}</code> — {route.reason}</li>)}</ul>
            </details>
          )}
        </section>

        <section className="contract-card">
          <h3>Price basis</h3>
          <ul className="pricing-basis">
            {contract.pricing.basis.map((line) => (
              <li key={line.factor}>
                <span><strong>{line.factor}</strong><small>{line.detail}</small></span>
                <em>{usd(line.amountMicroUsd)}</em>
              </li>
            ))}
          </ul>
          <div className="price-row highlight">
            <span>Ongoing growth</span>
            <strong>{usd(contract.pricing.ongoingMonthlyMicroUsd)}/month</strong>
          </div>
          <ol className="milestone-list">
            {contract.paymentMilestones.map((milestone) => (
              <li key={milestone.sequence}>
                <strong>{milestone.title} — {usd(milestone.amountMicroUsd)}</strong>
                <p>{milestone.invoiceRule}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="contract-card">
          <h3>Behaviours we commit to</h3>
          <ul className="integration-list">
            {contract.integrations.map((item) => (
              <li key={item.id}>
                <span className={`status-pill ${item.verdict}`}>{item.verdict}</span>
                <strong>{item.name}</strong>
                <p>{item.commitment}</p>
                {item.customerActionRequired && <small className="action-required">Your action: {item.customerActionRequired}</small>}
              </li>
            ))}
          </ul>
        </section>

        <section className="contract-card">
          <h3>Known differences</h3>
          {contract.knownDifferences.length === 0
            ? <p>None recorded for this scope.</p>
            : (
              <ul className="difference-list">
                {contract.knownDifferences.map((difference, index) => (
                  <li key={`${difference.area}-${index}`}>
                    <span className={`impact-pill ${difference.impact}`}>{difference.impact} impact</span>
                    <strong>{difference.area}</strong>
                    <p>{difference.description}</p>
                  </li>
                ))}
              </ul>
            )}
        </section>

        <section className="contract-card">
          <h3>Who does what</h3>
          <div className="responsibility-columns">
            <div>
              <small>SiteAgain</small>
              <ul>{contract.responsibilities.siteagain.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
            <div>
              <small>You</small>
              <ul>{contract.responsibilities.customer.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          </div>
        </section>

        <section className="contract-card">
          <h3>Domain, rollback, ownership</h3>
          <dl className="contract-terms">
            <div><dt>Cutover</dt><dd>{contract.domain.cutoverMethod.replaceAll("_", " ")}{contract.domain.rehearsalRequired ? " · rehearsal required" : ""}</dd></div>
            <div><dt>TLS</dt><dd>{contract.domain.tls}</dd></div>
            <div><dt>Rollback</dt><dd>{contract.rollback.method} · target {contract.rollback.targetMinutes} min</dd></div>
            <div><dt>Repository</dt><dd>{contract.ownership.repository}</dd></div>
            <div><dt>If you cancel</dt><dd>{contract.ownership.exportOnCancellation}</dd></div>
            <div><dt>Support</dt><dd>{contract.support.channel} · {contract.support.includedWeeksAfterCutover} weeks after cutover</dd></div>
          </dl>
        </section>
      </div>

      {(contract.exclusions.length > 0 || contract.assumptions.length > 0) && (
        <details className="limitations-section">
          <summary>Exclusions and assumptions <ChevronDownIcon /></summary>
          <ul>
            {contract.exclusions.map((item) => <li key={item}>Excluded: {item}</li>)}
            {contract.assumptions.map((item) => <li key={item}>Assumes: {item}</li>)}
          </ul>
        </details>
      )}

      <div className="decision-bar">
        <p>
          <ShieldIcon />
          <span>
            <strong>{contract.acceptance.statement}</strong>
            <small>
              {proposal.decidedAt
                ? `${proposal.status} on ${formatConversationDate(proposal.decidedAt)}${proposal.decidedBy ? ` by ${proposal.decidedBy}` : ""}`
                : expired
                  ? "This revision has expired — generate a new one to proceed."
                  : `Valid until ${formatConversationDate(proposal.expiresAt)}`}
            </small>
          </span>
        </p>
        <div>
          <button className="secondary" type="button" disabled={busy === "contract"} onClick={onRegenerate}>
            New revision
          </button>
          {decidable && !expired && (
            <>
              <button
                className="secondary"
                type="button"
                disabled={busy.startsWith("proposal:")}
                onClick={() => onDecide("decline")}
              >Decline</button>
              <button
                className="primary"
                type="button"
                disabled={busy.startsWith("proposal:")}
                onClick={() => {
                  const acceptedBy = window.prompt("Acceptance is always attributed. Your full name:");
                  if (acceptedBy) onDecide("accept", acceptedBy);
                }}
              ><CheckIcon />Accept contract</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Mirrors `VerifyDomainDnsResult` from @siteagain/deployment. */
interface DomainVerification {
  verified: boolean;
  dnsRecordsChecked: number;
  missingRecords: string[];
  tlsProvisioned: boolean;
}

interface ExportPackageView {
  downloadUrl: string;
  generatedAt?: string;
  repository?: { url?: string | null };
  contents?: string[];
}

/**
 * Going live: the last stretch a customer drives themselves.
 *
 * Order is enforced by the API, and mirrored here — a domain must be verified
 * before a cutover can be rehearsed, and a rehearsal is what proves the switch
 * is safe. The export is available throughout, because ownership is not a
 * reward for staying.
 */
function LaunchView({ site, migration }: { site: SiteView; migration: MigrationRecord | null }) {
  const [domains, setDomains] = useState<CustomDomainRecord[]>([]);
  const [verification, setVerification] = useState<Record<string, DomainVerification>>({});
  const [rehearsal, setRehearsal] = useState<CutoverRehearsalRecord | null>(null);
  const [exportPackage, setExportPackage] = useState<ExportPackageView | null>(null);
  const [domainInput, setDomainInput] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);

  const activeDomain = domains.find((domain) => domain.status === "active" || domain.status === "verified") ?? domains[0] ?? null;
  const previewReady = Boolean(site.previewUrl) && isMigrationPreviewReady(migration);

  const loadDomains = useCallback(async () => {
    const response = await api<{ domains: CustomDomainRecord[] }>(`/sites/${site.id}/domains`);
    setDomains(response.domains);
    return response.domains;
  }, [site.id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void loadDomains()
      .catch((reason: unknown) => { if (active) setError(messageFor(reason)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadDomains]);

  async function run(label: string, operation: () => Promise<void>) {
    setBusy(label);
    setError("");
    try {
      await operation();
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setBusy("");
    }
  }

  function registerDomain(event: React.FormEvent) {
    event.preventDefault();
    const domain = domainInput.trim().toLowerCase();
    if (!domain || !consent) return;
    void run("register", async () => {
      await api(`/sites/${site.id}/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": `domain-${crypto.randomUUID()}` },
        body: JSON.stringify({ domain, consentAttested: true }),
      });
      setDomainInput("");
      setConsent(false);
      setNotice("Domain registered. Add the DNS records below, then verify.");
      await loadDomains();
    });
  }

  function verifyDomain(domainId: string) {
    return run(`verify:${domainId}`, async () => {
      const response = await api<{ domain: CustomDomainRecord; verification: DomainVerification }>(
        `/sites/${site.id}/domains/${domainId}/verify`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
      );
      setVerification((current) => ({ ...current, [domainId]: response.verification }));
      setNotice(response.verification.verified
        ? "Ownership verified. You can rehearse the cutover."
        : "The records are not visible yet. DNS can take a while to propagate.");
      await loadDomains();
    });
  }

  function rehearse() {
    if (!activeDomain) return;
    const deploymentId = site.previewDeploymentId;
    if (!deploymentId) {
      setError("There is no active deployment to rehearse against yet.");
      return;
    }
    void run("rehearse", async () => {
      const response = await api<{ rehearsal: CutoverRehearsalRecord }>(`/sites/${site.id}/cutover/rehearse`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": `rehearsal-${crypto.randomUUID()}` },
        body: JSON.stringify({ targetDomain: activeDomain.domain, activeDeploymentId: deploymentId }),
      });
      setRehearsal(response.rehearsal);
      setNotice(response.rehearsal.passed
        ? "Rehearsal passed, including the rollback."
        : "Rehearsal found problems. They are listed below and must be fixed before going live.");
    });
  }

  function generateExport() {
    return run("export", async () => {
      const response = await api<{ exportPackage: ExportPackageView }>(`/sites/${site.id}/export`, { method: "POST" });
      setExportPackage(response.exportPackage);
      setNotice("Your ownership package is ready to download.");
    });
  }

  if (loading) return <div className="proof-pending"><SpinnerIcon /> Loading launch status…</div>;

  return (
    <div className="launch-view">
      {error && <div className="rail-alert error" role="alert"><AlertIcon /><span>{error}</span><button onClick={() => setError("")}>×</button></div>}
      {notice && !error && <div className="rail-alert"><span className="working-dot" /><span>{notice}</span></div>}

      <header className="findings-header">
        <div>
          <small>Launch · {websiteHost(site)}</small>
          <h1>Point your domain at the new site</h1>
          <p>You keep control of DNS the whole way. Nothing switches until a rehearsal has proved the rollback works.</p>
        </div>
      </header>

      <section className="plan-stage">
        <div className="audit-section-heading">
          <span><small>01</small><strong>Your domain</strong></span>
          <p>We generate the records; you add them at your registrar.</p>
        </div>

        {domains.length === 0 && (
          <form className="domain-form" onSubmit={registerDomain}>
            <label htmlFor="custom-domain">Domain you want to serve this site</label>
            <div className="url-input-row">
              <GlobeIcon />
              <input
                id="custom-domain"
                type="text"
                inputMode="url"
                value={domainInput}
                onChange={(event) => setDomainInput(event.target.value)}
                placeholder="yourcompany.com"
                autoComplete="off"
              />
            </div>
            <label className="consent-row">
              <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
              <span>I control this domain and authorise SiteAgain to serve it.</span>
            </label>
            <button className="audit-submit" type="submit" disabled={!domainInput.trim() || !consent || busy === "register"}>
              {busy === "register" ? <SpinnerIcon /> : <ArrowIcon />}
              <span>{busy === "register" ? "Registering…" : "Add this domain"}</span>
            </button>
          </form>
        )}

        {domains.map((domain) => (
          <article className="domain-card" key={domain.id}>
            <header>
              <strong>{domain.domain}</strong>
              <span className={`status-pill ${domain.status}`}>{domain.status.replaceAll("_", " ")}</span>
              <span className={`status-pill ${domain.tlsStatus}`}>TLS {domain.tlsStatus}</span>
            </header>

            <table className="dns-table">
              <thead>
                <tr><th>Type</th><th>Name</th><th>Value</th><th>Purpose</th></tr>
              </thead>
              <tbody>
                {domain.dnsRecords.map((record) => (
                  <tr key={`${record.type}:${record.name}:${record.value}`}>
                    <td><code>{record.type}</code></td>
                    <td><code>{record.name}</code></td>
                    <td><code className="dns-value">{record.value}</code></td>
                    <td>{record.purpose.replaceAll("_", " ")}{record.note ? ` — ${record.note}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <footer>
              <button type="button" disabled={busy === `verify:${domain.id}`} onClick={() => void verifyDomain(domain.id)}>
                {busy === `verify:${domain.id}` ? <SpinnerIcon /> : <RefreshIcon />}
                <span>{busy === `verify:${domain.id}` ? "Checking DNS…" : "Check DNS now"}</span>
              </button>
              {verification[domain.id] && (
                <ul className="dns-results">
                  <li className={verification[domain.id]?.verified ? "found" : "missing"}>
                    {verification[domain.id]?.verified ? <CheckIcon /> : <AlertIcon />}
                    <span>
                      {verification[domain.id]?.verified
                        ? "Ownership TXT record found"
                        : "Ownership TXT record not visible yet"}
                    </span>
                    <small>{verification[domain.id]?.dnsRecordsChecked ?? 0} record(s) checked</small>
                  </li>
                  {(verification[domain.id]?.missingRecords ?? []).map((record) => (
                    <li key={record} className="missing">
                      <AlertIcon />
                      <code>{record}</code>
                      <small>not found</small>
                    </li>
                  ))}
                  <li className={verification[domain.id]?.tlsProvisioned ? "found" : "missing"}>
                    {verification[domain.id]?.tlsProvisioned ? <CheckIcon /> : <AlertIcon />}
                    <span>TLS certificate</span>
                    <small>{verification[domain.id]?.tlsProvisioned ? "active" : "not issued yet"}</small>
                  </li>
                </ul>
              )}
            </footer>
          </article>
        ))}
      </section>

      <section className="plan-stage">
        <div className="audit-section-heading">
          <span><small>02</small><strong>Cutover rehearsal</strong></span>
          <p>Runs the real behaviour checks and the real rollback, before any traffic moves.</p>
        </div>

        {!activeDomain || activeDomain.status === "pending_verification"
          ? <p className="assessment-empty">Verify a domain first — a rehearsal needs somewhere to point.</p>
          : !previewReady
            ? <p className="assessment-empty">The first-page proof has to finish before a rehearsal has anything to switch to.</p>
            : (
              <div className="plan-start">
                <p>Rehearses against <strong>{activeDomain.domain}</strong>, then rolls straight back. Your live site is untouched.</p>
                <button className="audit-submit" type="button" disabled={busy === "rehearse"} onClick={rehearse}>
                  {busy === "rehearse" ? <SpinnerIcon /> : <ShieldIcon />}
                  <span>{busy === "rehearse" ? "Rehearsing…" : "Rehearse the cutover"}</span>
                </button>
              </div>
            )}

        {rehearsal && (
          <div className={`rehearsal-result ${rehearsal.passed ? "passed" : "failed"}`}>
            <header>
              <strong>{rehearsal.passed ? "Rehearsal passed" : "Rehearsal failed"}</strong>
              <small>{Math.round(rehearsal.rehearsalDurationMs / 100) / 10}s · rolled back {rehearsal.rollbackRehearsedAt ? "successfully" : "was not reached"}</small>
            </header>
            {rehearsal.failureReason && <p className="plan-caveat"><AlertIcon /> {rehearsal.failureReason}</p>}
            {rehearsal.validationResult && (
              <ul className="behaviour-checks">
                {rehearsal.validationResult.checks.map((check) => (
                  <li key={check.id} className={`check-${check.status}`}>
                    {check.status === "pass" ? <CheckIcon /> : <AlertIcon />}
                    <span><strong>{check.name}</strong><small>{check.details}</small></span>
                    <em>{check.category}</em>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="plan-stage">
        <div className="audit-section-heading">
          <span><small>03</small><strong>Your copy of everything</strong></span>
          <p>The built site, its repository, and the configuration — yours to take at any time.</p>
        </div>
        <div className="plan-start">
          <p>Generating a package snapshots the current production deployment. It does not change anything.</p>
          <button className="audit-submit" type="button" disabled={busy === "export"} onClick={() => void generateExport()}>
            {busy === "export" ? <SpinnerIcon /> : <FileTextIcon />}
            <span>{busy === "export" ? "Packaging…" : "Generate ownership package"}</span>
          </button>
          {exportPackage && (
            <a className="big-preview-link" href={`/api${exportPackage.downloadUrl}`} download>
              <FileTextIcon />
              <span><small>Ready</small><strong>Download the archive</strong></span>
              <ExternalIcon />
            </a>
          )}
        </div>
      </section>
    </div>
  );
}

interface AssessmentResponse {
  status: "ready" | "running" | "failed" | "absent";
  jobId: string | null;
  error: Record<string, unknown> | null;
  report: SiteAgainAssessmentReportV1 | null;
}

function FindingsView({
  site,
  migration,
  onSelectFix,
}: {
  site: SiteView;
  migration: MigrationRecord | null;
  onSelectFix?: ((prompt: string) => void) | undefined;
}) {
  const [assessment, setAssessment] = useState<AssessmentResponse | null>(null);
  const [error, setError] = useState("");
  const t = assessmentCopy(useLocale());
  const host = sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name));

  useEffect(() => {
    let active = true;
    let timer = 0;
    async function load() {
      try {
        const response = await api<{ assessment: AssessmentResponse }>(`/sites/${site.id}/assessment`);
        if (!active) return;
        setAssessment(response.assessment);
        // The audit runs on a queue; keep looking until it settles.
        if (response.assessment.status === "running" || response.assessment.status === "absent") {
          timer = window.setTimeout(() => void load(), 4_000);
        }
      } catch (reason) {
        if (active) setError(messageFor(reason));
      }
    }
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [site.id]);

  if (error) return <div className="proof-pending error"><AlertIcon /> {error}</div>;
  if (!assessment) return <div className="proof-pending"><SpinnerIcon /> {t.loadingAssessment}</div>;

  if (assessment.status === "failed") {
    return (
      <div className="proof-empty">
        <div><AlertIcon /></div><small>{t.kicker}</small><h1>{t.studioFailedTitle}</h1>
        <p>{t.studioFailedDetail(host)}</p>
      </div>
    );
  }

  if (assessment.status !== "ready" || !assessment.report) {
    return (
      <div className="proof-pending">
        <SpinnerIcon /> {t.studioScanning(host)}
      </div>
    );
  }

  return (
    <AssessmentReportView
      report={assessment.report}
      host={host}
      showLanguageSwitch
      {...(onSelectFix ? { onSelectFix } : {})}
    />
  );
}



interface PublishedGrowthKpi {
  id: string;
  label: string;
  value: number | null;
  secondaryValue?: number | null;
  unit?: string;
  status?: string;
  basis: string;
}
interface PublishedGrowthChange {
  id: string;
  at: string;
  type: string;
  title: string;
  detail: string;
  tone: string;
}
interface PublishedGrowthItem { label: string; at: string; verified: boolean }
interface PublishedGrowthTask { title: string; detail: string; kind: string; dueAt?: string | null }
interface PublishedGrowthTrend { metric: string; points: Array<{ date: string; value: number | null }> }
interface PublishedGrowthResponse {
  publication: {
    headline: string;
    summary: string;
    generatedAt: string;
    dataThrough: string;
    site: { name: string; domain: string; url: string };
    kpis: PublishedGrowthKpi[];
    changes: PublishedGrowthChange[];
    fixed: PublishedGrowthItem[];
    regressed: PublishedGrowthItem[];
    needsYou: PublishedGrowthTask[];
    nextActions: PublishedGrowthTask[];
    geo: {
      status: string;
      citationRatePct: number | null;
      mentionOnlyRatePct: number | null;
      shareOfVoicePct: number | null;
      firstPartySourcePct: number | null;
      totalRuns: number;
      lastRunAt: string | null;
      confusionCount: number;
    };
    trends: PublishedGrowthTrend[];
    provenance: { dataNotes: string[]; measurementVersion: string };
  };
  meta: { version: number; hash: string; publishedAt: string; dataThrough: string; stale: boolean; staleDays: number };
}

function growthKpiValue(kpi: PublishedGrowthKpi): string {
  if (kpi.value === null || kpi.value === undefined) return "Not measured";
  if (kpi.id === "critical_issues") return `${kpi.value} open`;
  if (kpi.unit === "percent" || kpi.unit === "delta_percent") return `${kpi.value}%`;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(kpi.value);
}

function growthTone(tone: string): string {
  return tone === "good" ? "good" : tone === "bad" ? "bad" : tone === "warn" ? "warn" : "muted";
}

/**
 * The client-facing growth report. Rendered only from a published snapshot;
 * SiteAgain never recomputes these numbers. If nothing is published, the
 * section stays out of the way rather than showing an operator error.
 */
function PublishedGrowthSection({ siteId }: { siteId: string }) {
  const [data, setData] = useState<PublishedGrowthResponse | null>(null);
  const [history, setHistory] = useState<Array<{ id: string; version: number; publishedAt: string }>>([]);
  useEffect(() => {
    let live = true;
    setData(null);
    setHistory([]);
    api<PublishedGrowthResponse>(`/sites/${siteId}/growth`)
      .then((body) => { if (live) setData(body); })
      .catch(() => { /* no publication yet, or the flag is off */ });
    api<{ publications: Array<{ id: string; version: number; publishedAt: string }> }>(`/sites/${siteId}/growth/history`)
      .then((body) => { if (live) setHistory(body.publications ?? []); })
      .catch(() => { /* history is extra; the current report is enough */ });
    return () => { live = false; };
  }, [siteId]);
  if (!data?.publication) return null;

  const report = data.publication;
  const meta = data.meta;
  const geo = report.geo;
  const trends = report.trends.filter((trend) => trend.points.some((point) => point.value !== null)).slice(0, 2);
  return (
    <section className="pg-panel" aria-label="Published growth report">
      <header className="pg-head">
        <div>
          <small>Published growth report · {meta.version ? `v${meta.version}` : "current"}</small>
          <h2>{report.headline}</h2>
          <p>{report.summary}</p>
        </div>
        <div className="pg-meta">
          {meta.stale ? <span className="pg-stale">Stale · data through {report.dataThrough}</span> : <span className="pg-fresh">Data through {report.dataThrough}</span>}
          <span>Published {relativeTime(meta.publishedAt)}</span>
        </div>
      </header>

      {meta.stale ? (
        <p className="pg-stale-note">This snapshot is older than {meta.staleDays} days. Treat it as the last published state, not as current performance.</p>
      ) : null}

      <div className="pg-kpis">
        {report.kpis.map((kpi) => (
          <div key={kpi.id} className={`pg-kpi${kpi.value === null ? " is-empty" : ""}`}>
            <span>{kpi.label}</span>
            <strong>{growthKpiValue(kpi)}</strong>
            {kpi.id === "critical_issues" && typeof kpi.secondaryValue === "number" ? <em>{kpi.secondaryValue} verified fixed · 7d</em> : <em title={kpi.basis}>{kpi.status === "not_connected" ? "GA4 not connected" : kpi.basis}</em>}
          </div>
        ))}
      </div>

      <div className="pg-grid">
        <div className="pg-column">
          <section className="pg-block">
            <h3>What changed</h3>
            {report.changes.length === 0 ? <p className="pg-muted">No material change in this period.</p> : (
              <ul className="pg-list">
                {report.changes.slice(0, 6).map((change) => (
                  <li key={change.id} className={growthTone(change.tone)}>
                    <strong>{change.title}</strong>
                    {change.detail ? <span>{change.detail}</span> : null}
                    <small>{relativeTime(change.at)}</small>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="pg-block">
            <h3>Fixed and verified</h3>
            {report.fixed.length === 0 ? <p className="pg-muted">No fixes verified in this period.</p> : (
              <ul className="pg-list pg-list-tight">
                {report.fixed.slice(0, 6).map((item, index) => (
                  <li key={`${item.label}-${index}`} className="good">
                    <strong>{item.label}</strong>
                    <small>{item.verified ? "verified" : "recorded"} · {item.at}</small>
                  </li>
                ))}
              </ul>
            )}
            {report.regressed.length > 0 ? <p className="pg-regressed">{report.regressed.length} fix{report.regressed.length === 1 ? "" : "es"} regressed — it is back in the queue.</p> : null}
          </section>
        </div>

        <div className="pg-column">
          <section className="pg-block">
            <h3>What needs you</h3>
            {report.needsYou.length === 0 ? <p className="pg-muted">Nothing is waiting on you.</p> : (
              <ul className="pg-list">
                {report.needsYou.map((item, index) => (
                  <li key={`${item.title}-${index}`} className="warn">
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                    {item.dueAt ? <small>by {item.dueAt}</small> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="pg-block">
            <h3>Next actions</h3>
            {report.nextActions.length === 0 ? <p className="pg-muted">No next action is needed right now.</p> : (
              <ul className="pg-list pg-list-tight">
                {report.nextActions.map((item, index) => (
                  <li key={`${item.title}-${index}`} className="muted">
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {geo.status === "measured" ? (
            <section className="pg-block pg-geo">
              <h3>AI visibility</h3>
              <div className="pg-geo-grid">
                <span><strong>{geo.citationRatePct ?? "—"}%</strong><em>citation rate</em></span>
                <span><strong>{geo.mentionOnlyRatePct ?? "—"}%</strong><em>mention only</em></span>
                <span><strong>{geo.shareOfVoicePct ?? "—"}%</strong><em>share of voice</em></span>
                <span><strong>{geo.firstPartySourcePct ?? "—"}%</strong><em>first-party sources</em></span>
              </div>
              <p className="pg-muted">Across {geo.totalRuns} live prompt run{geo.totalRuns === 1 ? "" : "s"}{geo.confusionCount ? ` · ${geo.confusionCount} entity confusion check${geo.confusionCount === 1 ? "" : "s"}` : ""}.</p>
            </section>
          ) : null}
        </div>
      </div>

      {trends.length > 0 ? (
        <section className="pg-trends">
          <h3>Recent trend</h3>
          <div className="pg-trend-grid">
            {trends.map((trend) => {
              const values = trend.points.map((point) => point.value ?? 0);
              const max = Math.max(...values, 1);
              return (
                <div key={trend.metric} className="pg-trend">
                  <span>{trend.metric.replaceAll("_", " ")}</span>
                  <div className="pg-bars">
                    {trend.points.map((point, index) => (
                      <i key={point.date} style={{ height: `${Math.max(4, Math.round(((point.value ?? 0) / max) * 100))}%` }} title={`${point.date}: ${point.value ?? "no data"}`} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {history.length > 1 ? (
        <div className="pg-history" aria-label="Publication history">
          <span>Previous reports</span>
          {history.slice(0, 6).map((entry) => (
            <em key={entry.id} title={entry.publishedAt}>v{entry.version} · {new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(entry.publishedAt))}</em>
          ))}
        </div>
      ) : null}

      <footer className="pg-foot">
        <span>{report.provenance.dataNotes.join(" · ")}</span>
        <span className="pg-hash" title={meta.hash}>{meta.hash.slice(0, 20)}…</span>
      </footer>
    </section>
  );
}

function GrowthView({
  site,
  userEmail,
  onPreviewCreated,
  onRequestChange,
  changeBlocked,
}: {
  site: SiteView;
  userEmail: string;
  onPreviewCreated(changeSetId: string): void;
  onRequestChange?: ((requestText: string) => void) | undefined;
  changeBlocked: boolean;
}) {
  const [recommendations, setRecommendations] = useState<RecommendationRecord[]>([]);
  const [measurements, setMeasurements] = useState<RecommendationMeasurementRecord[]>([]);
  const [reports, setReports] = useState<WeeklyReportRecord[]>([]);
  const [auditRuns, setAuditRuns] = useState<SeoAuditRunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showAllPriorities, setShowAllPriorities] = useState(false);
  const [upgrade, setUpgrade] = useState<"idle" | "sending" | "requested">("idle");

  const load = useCallback(async () => {
    const [recommendationData, reportData, auditData] = await Promise.all([
      api<{ recommendations: RecommendationRecord[]; measurements: RecommendationMeasurementRecord[] }>(
        `/sites/${site.id}/recommendations`,
      ),
      api<{ reports: WeeklyReportRecord[] }>(`/sites/${site.id}/weekly-reports`),
      api<{ auditRuns: SeoAuditRunRecord[] }>(`/sites/${site.id}/seo-audits`),
    ]);
    setRecommendations(recommendationData.recommendations);
    setMeasurements(recommendationData.measurements);
    setReports(reportData.reports);
    setAuditRuns(auditData.auditRuns);
  }, [site.id]);

  useEffect(() => {
    setLoading(true);
    setError("");
    void load().catch((reason: unknown) => setError(messageFor(reason))).finally(() => setLoading(false));
  }, [load]);

  async function startAudit() {
    setBusy("audit");
    setError("");
    setMessage("");
    try {
      await api(`/sites/${site.id}/seo-audits`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": `seo-${crypto.randomUUID()}` },
        body: JSON.stringify({ tier: "full_seo", locale: "en" }),
      });
      setMessage("Sebora is collecting fresh SEO and GEO evidence. Recommendations will appear after the signed result arrives.");
      await load();
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setBusy("");
    }
  }

  async function decideRecommendation(item: RecommendationRecord, action: "save" | "skip" | "preview") {
    setBusy(`${item.id}:${action}`);
    setError("");
    setMessage("");
    try {
      const response = await api<{ recommendation: RecommendationRecord; changeSet?: ChangeSetRecord }>(
        `/recommendations/${item.id}/${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": `rec-${item.id}-${action}-${crypto.randomUUID()}`,
          },
          body: "{}",
        },
      );
      if (response.changeSet) {
        setMessage("A protected change set is building from this evidence. Nothing will publish without approval.");
        onPreviewCreated(response.changeSet.id);
      } else {
        setMessage(action === "save"
          ? "Saved for a later decision."
          : "Dismissed. It stays in the history, and the next audit can re-raise it with fresh evidence if it still matters.");
      }
      await load();
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setBusy("");
    }
  }

  async function requestUpgrade() {
    setUpgrade("sending");
    setError("");
    setMessage("");
    try {
      await api(`/sites/${site.id}/growth/upgrade-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail }),
      });
      setUpgrade("requested");
      setMessage("Request received. We will confirm activation at your account email — nobody is charged until the loop is running.");
    } catch (reason) {
      setUpgrade("idle");
      setError(messageFor(reason));
    }
  }

  if (loading) return <div className="proof-pending"><SpinnerIcon /> Loading the growth decision queue…</div>;
  // Dismissed items leave the priority list; the trail still holds them, and a
  // later audit can re-raise the same opportunity with fresh evidence.
  const actionable = recommendations.filter((item) => ["new", "viewed", "saved"].includes(item.status));
  const dismissed = recommendations.filter((item) => item.status === "skipped");
  const expired = recommendations.filter((item) => item.status === "expired");
  const inFlight = recommendations.filter((item) => ["previewing", "previewed", "applied", "measuring", "measured"].includes(item.status));
  const improvements = measurements.filter((item) => item.verdict === "improved").length;
  const pendingMeasurement = recommendations.some((item) => item.status === "applied" || item.status === "measuring");
  const priorities = showAllPriorities ? actionable : actionable.slice(0, 3);
  const detected = recommendations.length;
  const implemented = recommendations.filter((item) => ["applied", "measuring", "measured"].includes(item.status)).length;
  const earliestAudit = auditRuns.length > 0 ? auditRuns[auditRuns.length - 1] : undefined;
  const latestReport = reports[0];
  const latestAudit = auditRuns[0];

  return (
    <div className="growth-view">
      <header className="growth-header">
        <div>
          <small>Continuous improvement · Sebora intelligence</small>
          <h1>Evidence becomes a decision.<br />A decision becomes a safe release.</h1>
          <p>SiteAgain owns approval, deployment, rollback and measurement. Sebora supplies the diagnosis.</p>
        </div>
        <button className="growth-audit-button" onClick={() => void startAudit()} disabled={busy === "audit"}>
          {busy === "audit" ? <SpinnerIcon /> : <RefreshIcon />}
          {busy === "audit" ? "Requesting…" : "Run fresh audit"}
        </button>
      </header>

      {(message || error) && <div className={`growth-notice ${error ? "error" : ""}`}>{error ? <AlertIcon /> : <CheckIcon />}{error || message}</div>}

      <PublishedGrowthSection siteId={site.id} />

      <section className="growth-scoreboard" aria-label="Growth loop summary">
        <GrowthMetric label="Decisions waiting" value={String(actionable.length)} detail="Evidence-backed, not auto-published" tone="orange" />
        <GrowthMetric label="In the release loop" value={String(inFlight.length)} detail="Preview, applied or measuring" tone="ink" />
        <GrowthMetric label="Observed improvements" value={String(improvements)} detail="Directional, with attribution caveats" tone="green" />
        <GrowthMetric
          label="Evidence freshness"
          value={latestAudit?.completedAt ? relativeDate(latestAudit.completedAt) : "No baseline"}
          detail={latestAudit ? `${latestAudit.status} · ${latestAudit.tier}` : "Run an audit to establish it"}
          tone="paper"
        />
      </section>

      <SearchConsoleSection siteId={site.id} />

      <div id="growth-enquiries"><EnquiriesSection siteId={site.id} onRequestChange={onRequestChange} changeBlocked={changeBlocked} /></div>

      <MonthlyReportsSection siteId={site.id} />

      {latestReport && (
        <section className={`weekly-brief ${latestReport.meaningfulChange ? "meaningful" : "quiet"}`}>
          <div><span>W{weekNumber(latestReport.periodEnd)}</span><small>Latest weekly evidence brief</small></div>
          <h2>{latestReport.summary.headline}</h2>
          <p>{latestReport.summary.newRecommendations} new · {latestReport.summary.appliedRecommendations} applied · {latestReport.summary.measuredImprovements} observed improvements</p>
        </section>
      )}

      <section className="growth-section" id="growth-approvals">
        <div className="growth-section-heading">
          <div><small>Decision inbox</small><h2>Best next actions</h2></div>
          <span>
            {actionable.length} open
            {actionable.length > 3 && (
              <>
                {" · "}
                <button type="button" className="growth-link-button" onClick={() => setShowAllPriorities((value) => !value)}>
                  {showAllPriorities ? "Show top 3" : `Show all ${actionable.length}`}
                </button>
              </>
            )}
          </span>
        </div>
        {actionable.length === 0 ? (
          <div className="growth-empty">
            <GrowthIcon />
            <h3>No action required this week.</h3>
            <p>That is a valid outcome: nothing in the current evidence clears the bar for a change.</p>
          </div>
        ) : (
          <div className="recommendation-stack">
            {priorities.map((item, index) => (
              <article className="recommendation-card" key={item.id}>
                <div className="recommendation-rank">{String(index + 1).padStart(2, "0")}</div>
                <div className="recommendation-body">
                  <div className="recommendation-meta">
                    <span className={`impact-${item.impactBand}`}>{item.impactBand} impact</span>
                    <span>{item.effort} effort</span>
                    <span>{item.confidence} confidence</span>
                    <span>{item.safetyClass.replaceAll("_", " ")}</span>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.explanation}</p>
                  <div className="evidence-strip"><ProofIcon /><span><strong>Evidence</strong>{item.evidence[0]?.detail ?? "Evidence unavailable"}</span></div>
                  <div className="recommendation-targets">
                    {item.affectedQuery && <code>query: {item.affectedQuery}</code>}
                    {item.affectedUrls.slice(0, 2).map((url) => <code key={url}>{shortUrl(url)}</code>)}
                  </div>
                </div>
                <div className="recommendation-actions">
                  <button className="primary" disabled={busy.startsWith(item.id)} onClick={() => void decideRecommendation(item, "preview")}><EyeIcon />Build preview</button>
                  <button disabled={busy.startsWith(item.id)} onClick={() => void decideRecommendation(item, "save")}><HistoryIcon />Save</button>
                  <button disabled={busy.startsWith(item.id)} onClick={() => void decideRecommendation(item, "skip")}>Dismiss</button>
                </div>
              </article>
            ))}
          </div>
        )}
        {(dismissed.length > 0 || expired.length > 0) && (
          <p className="growth-note">
            {dismissed.length > 0 && `${dismissed.length} dismissed`}
            {dismissed.length > 0 && expired.length > 0 && " · "}
            {expired.length > 0 && `${expired.length} expired with their evidence window`}
            {" "}— both stay in the history and can return with fresh evidence.
          </p>
        )}
      </section>

      <div id="growth-results" aria-hidden="true" />
      {(detected > 0 || auditRuns.length > 0) && (
        <section className="growth-section pilot-results">
          <div className="growth-section-heading">
            <div><small>Pilot results</small><h2>Baseline → today</h2></div>
            <span>Evidence, not promises</span>
          </div>
          <div className="pilot-results-grid">
            <div className="pilot-result-col">
              <small>Baseline</small>
              <strong>{formatConversationDate(earliestAudit?.completedAt ?? earliestAudit?.createdAt ?? site.createdAt)}</strong>
              <p>First evidence captured for this site.</p>
            </div>
            <div className="pilot-result-col">
              <small>Today</small>
              <strong>{implemented} implemented · {detected} detected</strong>
              <p>
                {dismissed.length} dismissed · {expired.length} expired · {measurements.length} comparison{measurements.length === 1 ? "" : "s"} completed
                {measurements.length > 0 ? ` (${improvements} improved)` : ""}
              </p>
            </div>
          </div>
          <p className="growth-note">
            Clicks, impressions and ranking queries require the Search Console connection. Until it is connected, these results describe what
            changed on the site, not what Google did with it — and we do not fill that gap with estimates.
          </p>
          {upgrade === "requested" ? (
            <div className="growth-note growth-note-strong">
              <strong>Growth plan requested.</strong>
              <p>We activate it manually today so nobody is charged before the loop is actually running. You will get a confirmation at your account email.</p>
            </div>
          ) : (
            <button className="growth-upgrade-button" disabled={upgrade === "sending"} onClick={() => void requestUpgrade()}>
              {upgrade === "sending" ? <SpinnerIcon /> : <SparkIcon />}
              Activate Growth — €289/month
            </button>
          )}
        </section>
      )}

      {measurements.length > 0 && (
        <section className="growth-section measurement-section">
          <div className="growth-section-heading"><div><small>After release</small><h2>7 / 14 / 28-day observations</h2></div><span>Not causal claims</span></div>
          {improvements === 0 && (
            <div className="growth-note growth-note-strong">
              <strong>No measured improvement yet.</strong>
              <p>
                None of the completed comparisons show a positive movement we can stand behind. Some changes may still be too recent to
                judge, and a recommendation can simply have had no measurable effect. We keep observing and will not claim a win that is
                not visible in the data.
              </p>
            </div>
          )}
          <div className="measurement-grid">
            {measurements.slice(0, 12).map((item) => (
              <div className={`measurement-card verdict-${item.verdict}`} key={item.id}>
                <span>DAY {item.windowDays}</span><strong>{item.verdict}</strong>
                <p>{measurementSummary(item)}</p><small>{item.confidence} confidence · directional attribution</small>
              </div>
            ))}
          </div>
        </section>
      )}

      {measurements.length === 0 && pendingMeasurement && (
        <section className="growth-section measurement-section">
          <div className="growth-section-heading"><div><small>After release</small><h2>7 / 14 / 28-day observations</h2></div><span>Not causal claims</span></div>
          <div className="growth-note growth-note-strong">
            <strong>Measurement has not completed yet.</strong>
            <p>An observation starts once a change is deployed and its window closes. Nothing is claimed before then.</p>
          </div>
        </section>
      )}
    </div>
  );
}

function GrowthMetric({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: string }) {
  return <div className={`growth-metric tone-${tone}`}><small>{label}</small><strong>{value}</strong><p>{detail}</p></div>;
}

function measurementSummary(item: RecommendationMeasurementRecord): string {
  const key = Object.keys(item.delta)[0];
  const delta = key ? item.delta[key] : null;
  if (!key || delta === null || delta === undefined) return "Comparable evidence was insufficient, so no outcome was claimed.";
  const formatted = Number.isInteger(delta) ? String(delta) : delta.toFixed(2);
  return `${key.replaceAll(/([A-Z])/g, " $1").toLowerCase()} changed by ${Number(delta) > 0 ? "+" : ""}${formatted}.`;
}

function shortUrl(value: string): string {
  try { const url = new URL(value); return `${url.hostname}${url.pathname}`; } catch { return value; }
}

function relativeDate(value: string): string {
  const days = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000));
  return days === 0 ? "Today" : days === 1 ? "1 day" : `${days} days`;
}

function weekNumber(value: string): number {
  const date = new Date(value);
  const first = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - first.getTime()) / 86_400_000 + first.getUTCDay() + 1) / 7);
}

function SparkIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3c.7 4.4 2.6 6.3 7 7-4.4.7-6.3 2.6-7 7-.7-4.4-2.6-6.3-7-7 4.4-.7 6.3-2.6 7-7Z" /></svg>; }
function GrowthIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19V9M10 19V5M16 19v-7M22 19V2" /><path d="m3 13 6-5 6 2 7-7" /></svg>; }
function CommandIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>; }
function PanelIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M14 4v16" /></svg>; }
function XIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.8 20h18.4L12 3Z" /><path d="M12 9v5M12 17.5h.01" /></svg>; }
function ClockIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>; }
function HistoryIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7v5h5" /><path d="M5.5 16a8 8 0 1 0-.8-7" /></svg>; }
function ChevronDownIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>; }
function LockIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>; }
function MailIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 7 8.5 6 8.5-6" /></svg>; }
function RefreshIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 7v5h-5" /><path d="M18.5 16a8 8 0 1 1 .8-7" /></svg>; }
function SpinnerIcon() { return <svg className="spinner" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /></svg>; }

/**
 * The dedicated, standalone page for one audit at `/r/<jobId>`. It renders the
 * same AssessmentReportView without the dashboard shell, and only after the
 * emailed verification token has been exchanged. A visitor without a valid
 * token gets a short explanation, never a login wall.
 */
function StandaloneReport({ jobId, journey, unlockError }: { jobId: string; journey: AnonymousState | null; unlockError: string }) {
  const [report, setReport] = useState<SiteAgainAssessmentReportV1 | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "failed" | "locked">("loading");
  const [message, setMessage] = useState("");
  const token = journey && journey.auditJobId === jobId ? journey.auditToken : null;

  useEffect(() => {
    if (!token) {
      setStatus(unlockError ? "failed" : "locked");
      setMessage(unlockError);
      return;
    }
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const response = await fetch(`/api/audit/${jobId}`, {
          headers: { Authorization: `Audit ${token}` },
        });
        const body = (await response.json().catch(() => null)) as PublicAuditPoll | null;
        if (!active) return;
        if (!response.ok || !body?.audit) {
          setStatus("failed");
          setMessage("This report link is invalid or has expired.");
          return;
        }
        if (body.audit.result) {
          setReport(body.audit.result);
          setStatus("ready");
          return;
        }
        if (body.audit.status === "failed" || body.audit.status === "dead_lettered") {
          setStatus("failed");
          setMessage("This audit could not be completed. Start a new scan to try again.");
          return;
        }
        timer = window.setTimeout(poll, 3000);
      } catch {
        if (active) {
          setStatus("failed");
          setMessage("We could not load this report. Try again in a moment.");
        }
      }
    };
    void poll();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [jobId, token, unlockError]);

  const host = report ? reportHost(report.sourceUrl) : "your website";
  return (
    <div className="jr report-standalone">
      <JourneyPublicTopBar
        host={host}
        reportReady={status === "ready"}
        onRestart={() => window.location.assign(MARKETING_ORIGIN)}
        onSignIn={() => window.location.assign("/")}
      />
      {status === "loading" && <LoadingScreen />}
      {status === "locked" && (
        <div className="jr-wrap">
          <div className="jr-card pad">
            <span className="jr-eyebrow">Private report</span>
            <h1 className="jr-h1 small">This report is private.</h1>
            <p className="jr-lede">Open the link we emailed you to read it. Every audit has its own address, and only the recipient of that email can open it.</p>
            <a className="jr-btn" href={MARKETING_ORIGIN}>Start a new check</a>
          </div>
        </div>
      )}
      {status === "failed" && (
        <div className="jr-wrap">
          <div className="jr-card pad">
            <span className="jr-eyebrow">Report link</span>
            <h1 className="jr-h1 small">This link is not valid.</h1>
            <p className="jr-lede">{message}</p>
            <a className="jr-btn" href={MARKETING_ORIGIN}>Start a new check</a>
          </div>
        </div>
      )}
      {status === "ready" && report && (
        <JourneyReportView
          report={report}
          host={host}
          onBuildPreview={() => window.location.assign("/")}
          buildLabel="Sign in to build my preview →"
          onPreviewFix={() => window.location.assign("/")}
          onSignIn={() => window.location.assign("/")}
          fullReport={<AssessmentReportView report={report} host={host} showLanguageSwitch={false} concise />}
        />
      )}
    </div>
  );
}

function AuthGate() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [operatorEnabled, setOperatorEnabled] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  // Read once, before anything renders, so a refresh cannot restart the audit.
  const [journey, setJourney] = useState<AnonymousState | null>(() => adoptEntryParameters());
  const [forceSignIn, setForceSignIn] = useState(false);
  // The monthly-report email link, read once so it survives the sign-in round trip.
  const [monthlyReportId, setMonthlyReportId] = useState<MonthlyReportId | null>(() => adoptMonthlyReportLink());
  // Google's redirect back from Search Console: read once, so it survives a sign-in round trip.
  useState(() => adoptSearchConsoleResult());
  // The link in a "someone got in touch" email, read once so it survives the sign-in round trip.
  useState(() => adoptEnquiriesLink());
  // A verification link is exchanged before anything else renders: the report
  // is delivery-gated, not click-through gated.
  const [unlockChecking, setUnlockChecking] = useState(() => unlockTokenFromLocation() !== null);
  const [unlockError, setUnlockError] = useState("");
  // Assume the stricter of the two until the server says otherwise: offering an
  // account we cannot open is worse than under-promising for a moment.
  const [accessMode, setAccessMode] = useState<AccessMode>("invite_only");

  useEffect(() => {
    const token = unlockTokenFromLocation();
    if (!token) return;
    let active = true;
    void (async () => {
      try {
        const response = await fetch("/api/audit/unlock/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const payload = (await response.json().catch(() => null)) as
          | (VerifyAssessmentUnlockResponse & { error?: { message?: string } })
          | null;
        if (!response.ok || !payload?.unlock) {
          throw new Error(payload?.error?.message ?? "This verification link is invalid or has expired.");
        }
        if (!active) return;
        const unlock = payload.unlock;
        const existing = getAnonymousSession();
        const base: AnonymousState = existing && existing.auditJobId === unlock.audit.jobId
          ? existing
          : {
              anonymousId: crypto.randomUUID(),
              submittedUrl: unlock.audit.sourceUrl,
              goal: null,
              siteId: null,
              auditJobId: unlock.audit.jobId,
              auditToken: unlock.audit.authorization.token,
              createdAt: new Date().toISOString(),
            };
        const next: AnonymousState = {
          ...base,
          auditJobId: unlock.audit.jobId,
          auditToken: unlock.audit.authorization.token,
          submittedUrl: unlock.audit.sourceUrl || base.submittedUrl,
          email: unlock.lead.email,
          name: unlock.lead.name,
          emailExchanged: true,
          verificationSentTo: unlock.lead.email,
          verifiedAt: unlock.lead.verifiedAt,
        };
        saveAnonymousSession(next);
        setJourney(next);
        setForceSignIn(false);
        void sendTelemetry("email_verified", { jobId: unlock.audit.jobId }, next.anonymousId);
      } catch (reason) {
        if (active) setUnlockError(reason instanceof Error ? reason.message : "This verification link is invalid or has expired.");
      } finally {
        clearUnlockParam();
        if (active) setUnlockChecking(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    void fetch("/api/access-mode")
      .then((response) => response.ok ? response.json() as Promise<{ registrationOpen?: boolean }> : null)
      .then((body) => { if (active && body?.registrationOpen === true) setAccessMode("open"); })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);
  const refreshSession = useCallback(async () => {
    const response = await fetch("/api/auth/get-session");
    if (!response.ok) { setUser(null); return; }
    const body = (await response.json()) as { user?: SessionUser } | null;
    const nextUser = body?.user ?? null;
    setUser(nextUser);
    if (!nextUser) { setOperatorEnabled(false); return; }
    const accessResponse = await fetch("/api/operator/access");
    const access = accessResponse.ok ? await accessResponse.json() as { operator?: boolean } : null;
    setOperatorEnabled(access?.operator === true);
  }, []);
  useEffect(() => { void refreshSession().finally(() => setCheckingSession(false)); }, [refreshSession]);
  async function signOut() { await fetch("/api/auth/sign-out", { method: "POST" }); setUser(null); }
  async function deleteAccount() {
    if (!window.confirm("Send a secure email to permanently delete your account, websites, and artifacts?")) return;
    const response = await fetch("/api/auth/delete-user", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ callbackURL: window.location.origin }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as { message?: string } | null;
      window.alert(payload?.message ?? "Account deletion could not be started.");
      return;
    }
    window.alert("Check your email to confirm permanent account deletion.");
  }
  if (checkingSession || unlockChecking) return <LoadingScreen />;
  // A dedicated report address renders on its own, signed in or not, so an
  // emailed link never drops the visitor into the app shell or a login wall.
  const reportJobId = reportJobIdFromLocation();
  if (reportJobId) return <StandaloneReport jobId={reportJobId} journey={journey} unlockError={unlockError} />;
  if (!user) {
    // A visitor carrying a URL sees their assessment first; the sign-in wall
    // only comes after they have something to sign in for.
    if (journey && !forceSignIn) {
      return (
        <PublicAssessment
          state={journey}
          onState={setJourney}
          onSignIn={() => setForceSignIn(true)}
          onRestart={() => {
            clearAnonymousSession();
            setJourney(null);
            setForceSignIn(false);
            window.location.assign(MARKETING_ORIGIN);
          }}
        />
      );
    }
    return (
      <LoginScreen
        accessMode={accessMode}
        {...(journey ? { onBackToAssessment: () => setForceSignIn(false) } : {})}
        {...(unlockError ? { initialError: unlockError } : {})}
      />
    );
  }
  return (
    <App
      user={user}
      operatorEnabled={operatorEnabled}
      journey={journey}
      onJourneyConsumed={() => { clearAnonymousSession(); setJourney(null); }}
      onSignOut={() => void signOut()}
      onDeleteAccount={() => void deleteAccount()}
      monthlyReportId={monthlyReportId}
      onMonthlyReportClosed={() => setMonthlyReportId(null)}
    />
  );
}

function LoginScreen({
  accessMode,
  onBackToAssessment,
  initialError,
}: {
  accessMode: AccessMode;
  onBackToAssessment?: (() => void) | undefined;
  initialError?: string | undefined;
}) {
  const [email, setEmail] = useState("");
  const t = assessmentCopy(useLocale()).auth;
  const magicLinkError = new URLSearchParams(window.location.search).get("error");
  const [error, setError] = useState(initialError || (magicLinkError ? t.invalidLink : ""));
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError(""); setMessage(""); setSubmitting(true);
    try {
      const response = await fetch("/api/auth/sign-in/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, callbackURL: window.location.origin }),
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(payload?.message ?? t.sendFailed);
      }
      window.history.replaceState({}, "", "/");
      setMessage(t.checkInbox);
    } catch (reason) { setError(messageFor(reason)); } finally { setSubmitting(false); }
  }
  return (
    <AuthShell accessMode={accessMode}>
      <form className="jr-auth-card" onSubmit={(event) => void submit(event)}>
        <small>{accessMode === "open" ? t.getStarted : t.welcomeBack}</small>
        <h2>{accessMode === "open" ? t.continueWithEmail : t.signIn}</h2>
        <label><span>{t.email}</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" placeholder="you@company.com" /></label>
        {error && <div className="jr-error"><AlertIcon />{error}</div>}
        {message && <div className="jr-chip ok">{message}</div>}
        <button className="jr-btn wide" type="submit" disabled={submitting}>{submitting ? t.sending : t.sendLink}{!submitting && <ArrowIcon />}</button>
        {onBackToAssessment && <button type="button" className="jr-auth-link" onClick={onBackToAssessment}>{t.back}</button>}
      </form>
    </AuthShell>
  );
}

function AuthShell({ accessMode, children }: { accessMode: AccessMode; children: React.ReactNode }) {
  const inviteOnly = accessMode === "invite_only";
  const t = assessmentCopy(useLocale()).auth;
  return (
    <main className="jr jr-auth">
      <section className="jr-auth-story">
        <div className="jr-top-left">
          <span className="jr-logo"><span className="jr-logo-mark">S</span><span>SiteAgain</span></span>
          <LanguageSwitch />
        </div>
        <span className="jr-eyebrow">{inviteOnly ? t.privateTesting : t.welcome}</span>
        <h1 className="jr-h1">{t.headline[0]}<br />{t.headline[1]}</h1>
        <p className="jr-lede">{inviteOnly ? t.inviteOnly : t.startFrom}</p>
      </section>
      <section className="jr-auth-panel">{children}</section>
    </main>
  );
}

type JourneyStage = "check" | "choose" | "preview" | "plan" | "launch" | "grow";
/**
 * Each direction has its own quick proof (ADR-0031): the cleaned snapshot, or
 * the page's facts drawn by the generic renderer in the brand's look or a new one.
 */
type JourneyDirection = "as-is" | "conversion" | "redesign";
type JourneyPlanKey = "local" | "growth" | "enterprise";
type GrowthTab = "week" | "approvals" | "results" | "enquiries" | "site";

const JOURNEY_CALL_URL = "mailto:ismael@siteagain.com?subject=SiteAgain%2015-minute%20call";

const JOURNEY_STEPS: Array<{ key: JourneyStage; label: string }> = [
  { key: "check", label: "Check" },
  { key: "choose", label: "Direction" },
  { key: "preview", label: "Proof" },
  { key: "plan", label: "Plan" },
  { key: "launch", label: "Go live" },
  { key: "grow", label: "Grow" },
];

const JOURNEY_PLANS: Array<{
  key: JourneyPlanKey;
  name: string;
  price: string;
  unit: string;
  intro: string;
  features: string[];
  recommended?: boolean;
}> = [
  {
    key: "local",
    name: "Local",
    price: "€99",
    unit: "/ month",
    intro: "For a small local company that wants steady, expert improvements without an agency retainer.",
    features: [
      "Monthly audit and prioritised action plan",
      "Two approved improvements each month",
      "Monthly plain-language performance report",
      "Full approval, rollback and ownership controls",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    price: "€289",
    unit: "/ month",
    intro: "For a business ready to improve search, conversion and the site every week.",
    features: [
      "Weekly actions from live search evidence",
      "Conversion tests and A/B experiment plans",
      "Monthly report with decisions and measured outcomes",
      "Autopilot recommendations with human approval",
    ],
    recommended: true,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "Custom",
    unit: "tailored",
    intro: "For multi-site teams and businesses with bespoke integrations or governance.",
    features: [
      "Multi-site strategy and dedicated specialist",
      "Custom integrations, SLAs and team workflows",
      "Advanced experimentation and reporting",
      "Procurement, security and support requirements",
    ],
  },
];

type JourneyPreference = "direction" | "plan" | `proof:${JourneyDirection}`;

function journeyPreferenceKey(siteId: string, kind: JourneyPreference): string {
  return `siteagain:${kind}:${siteId}`;
}

function readJourneyDirection(siteId: string): JourneyDirection {
  try {
    const value = sessionStorage.getItem(journeyPreferenceKey(siteId, "direction"));
    if (value === "uiux") return "redesign";
    return value === "conversion" || value === "redesign" ? value : "as-is";
  } catch {
    return "as-is";
  }
}

function readJourneyProof(siteId: string, direction: JourneyDirection): string | null {
  try {
    const value = sessionStorage.getItem(journeyPreferenceKey(siteId, `proof:${direction}`));
    return value?.startsWith("job_") ? value : null;
  } catch {
    return null;
  }
}

function hasJourneyDirection(siteId: string): boolean {
  try {
    return ["as-is", "conversion", "redesign", "uiux"].includes(sessionStorage.getItem(journeyPreferenceKey(siteId, "direction")) ?? "");
  } catch {
    return false;
  }
}

function readJourneyPlan(siteId: string): JourneyPlanKey {
  try {
    const value = sessionStorage.getItem(journeyPreferenceKey(siteId, "plan"));
    return value === "local" || value === "growth" || value === "enterprise" ? value : "growth";
  } catch {
    return "growth";
  }
}

function rememberJourneyPreference(siteId: string, kind: JourneyPreference, value: string): void {
  try {
    sessionStorage.setItem(journeyPreferenceKey(siteId, kind), value);
  } catch {
    // Preferences are an enhancement; the journey still works without storage.
  }
}

const GROWTH_TABS: Array<{ key: GrowthTab; label: string }> = [
  { key: "week", label: "This week" },
  { key: "approvals", label: "Approvals" },
  { key: "results", label: "Results" },
  { key: "enquiries", label: "Enquiries" },
  { key: "site", label: "Your site" },
];

function journeyStageIndex(stage: JourneyStage): number {
  return Math.max(0, JOURNEY_STEPS.findIndex((step) => step.key === stage));
}

function JourneyTopBar({
  user,
  sites,
  site,
  stage,
  growthTab,
  canPreview,
  canChoose,
  canPlan,
  canLaunch,
  canGrow,
  onStage,
  onGrowthTab,
  onSelectSite,
  onNewProject,
  onOpenOperator,
  onSignOut,
  onDeleteAccount,
}: {
  user: SessionUser;
  sites: SiteView[];
  site: SiteView;
  stage: JourneyStage;
  growthTab: GrowthTab;
  canPreview: boolean;
  canChoose: boolean;
  canPlan: boolean;
  canLaunch: boolean;
  canGrow: boolean;
  onStage(stage: JourneyStage): void;
  onGrowthTab(tab: GrowthTab): void;
  onSelectSite(id: string): void;
  onNewProject(): void;
  onOpenOperator?: (() => void) | undefined;
  onSignOut(): void;
  onDeleteAccount(): void;
}) {
  const live = site.status === "active";
  const currentIndex = journeyStageIndex(stage);
  const available: Record<JourneyStage, boolean> = {
    check: true,
    preview: canPreview,
    choose: canChoose,
    plan: canPlan,
    launch: canLaunch,
    grow: canGrow,
  };
  return (
    <header className="jr-top">
      <div className="jr-top-left">
        <button className="jr-logo" onClick={() => onStage("check")} aria-label="SiteAgain home">
          <span className="jr-logo-mark">S</span>
          <span>SiteAgain</span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="jr-site-pill" aria-label="Switch website">
              <i className={`dot ${live ? "live" : ""}`} />
              <span className="host">{websiteHost(site)}</span>
              {live && <small>live</small>}
              <ChevronDownIcon />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="project-menu topbar-project-menu">
            <DropdownMenuLabel>Your websites</DropdownMenuLabel>
            {sites.map((item) => (
              <DropdownMenuItem className={item.id === site.id ? "selected" : ""} key={item.id} onSelect={() => onSelectSite(item.id)}>
                <span className="menu-favicon">{item.name.slice(0, 1).toUpperCase()}</span>
                <span className="menu-item-copy"><strong>{item.name}</strong><small>{websiteHost(item)} · {shortStatus(item.status)}</small></span>
                {item.id === site.id && <CheckIcon />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="new-project-item" onSelect={onNewProject}>
              <PlusIcon /><span><strong>Add a website</strong><small>Start another private proof</small></span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {stage === "grow" ? (
        <nav className="jr-steps" aria-label="Growth sections">
          {GROWTH_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`jr-step ${growthTab === tab.key ? "now" : ""}`}
              onClick={() => onGrowthTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      ) : (
        <nav className="jr-steps" aria-label="Your progress">
          {JOURNEY_STEPS.map((step, index) => {
            const done = index < currentIndex;
            const now = index === currentIndex;
            const enabled = available[step.key];
            return (
              <button
                key={step.key}
                type="button"
                className={`jr-step ${done ? "done" : ""} ${now ? "now" : ""}`}
                disabled={!enabled && !now}
                title={!enabled && !now ? "Available after the previous step" : undefined}
                onClick={() => enabled && onStage(step.key)}
              >
                <span className="n">{done ? <CheckIcon /> : index + 1}</span>
                {step.label}
              </button>
            );
          })}
        </nav>
      )}

      <div className="jr-top-right">
        {stage === "grow" ? (
          <span className="jr-top-note">Growth monitor</span>
        ) : stage === "check" ? (
          <span className="jr-top-note">Free · no card · live site untouched</span>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="jr-avatar" aria-label="Open account menu">{(user.name || user.email).slice(0, 1).toUpperCase()}</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="account-menu">
            <DropdownMenuLabel><strong>{user.name || "Your account"}</strong><span>{user.email}</span></DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onNewProject}><PlusIcon />Add a website</DropdownMenuItem>
            {onOpenOperator && <DropdownMenuItem onSelect={onOpenOperator}><CommandIcon />Mission Control</DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="destructive" onSelect={onDeleteAccount}>Delete account</DropdownMenuItem>
            <DropdownMenuItem className="destructive" onSelect={onSignOut}>Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function JourneyShell({
  user,
  sites,
  site,
  migration,
  migrationJobs,
  changes,
  selectedChange,
  usageMicroUsd,
  notice,
  error,
  hasActiveChange,
  onDismissError,
  onSelectSite,
  onSelectChange,
  onNewProject,
  onOpenOperator,
  onSignOut,
  onDeleteAccount,
  onSubmitChange,
  onRestartHomepage,
  onDecision,
  onSelectFix,
  onRecommendationPreview,
  onStartProof,
}: {
  user: SessionUser;
  sites: SiteView[];
  site: SiteView;
  migration: MigrationRecord | null;
  migrationJobs: JobRecord[];
  changes: ChangeView[];
  selectedChange: ChangeView | null;
  usageMicroUsd: number;
  notice: string;
  error: string;
  hasActiveChange: boolean;
  onDismissError(): void;
  onSelectSite(id: string): void;
  onSelectChange(id: string): void;
  onNewProject(): void;
  onOpenOperator?: (() => void) | undefined;
  onSignOut(): void;
  onDeleteAccount(): void;
  onSubmitChange(text: string): Promise<void>;
  onRestartHomepage(): void;
  onDecision(action: "approve" | "reject" | "rollback"): void;
  onSelectFix(prompt: string): void;
  onRecommendationPreview(changeSetId: string): void;
  onStartProof(direction: JourneyDirection): Promise<void>;
}) {
  const live = site.status === "active";
  const previewReady = Boolean(site.previewUrl) && isMigrationPreviewReady(migration);
  const initialStage: JourneyStage = live ? "grow" : "check";
  const [stage, setStage] = useState<JourneyStage>(initialStage);
  const [growthTab, setGrowthTab] = useState<GrowthTab>("week");
  const [choiceMade, setChoiceMade] = useState(hasJourneyDirection(site.id));
  const [assessmentReady, setAssessmentReady] = useState(previewReady);
  const [direction, setDirection] = useState<JourneyDirection>(() => readJourneyDirection(site.id));
  const [plan, setPlan] = useState<JourneyPlanKey>(() => readJourneyPlan(site.id));
  const [proofId, setProofId] = useState<string | null>(() => readJourneyProof(site.id, readJourneyDirection(site.id)));
  const markAssessmentReady = useCallback(() => setAssessmentReady(true), []);
  // A fix the owner asked to preview from the audit before any preview
  // existed. It waits in the preview's request box until the build is ready.
  const [pendingFix, setPendingFix] = useState("");

  useEffect(() => {
    setPendingFix("");
    setGrowthTab("week");
    setChoiceMade(hasJourneyDirection(site.id));
    setAssessmentReady(previewReady);
    setDirection(readJourneyDirection(site.id));
    setPlan(readJourneyPlan(site.id));
    setProofId(readJourneyProof(site.id, readJourneyDirection(site.id)));
    setStage(live ? "grow" : "check");
    // Reset only when the selected site changes. A preview becoming ready
    // must not yank the customer back to the audit.
  }, [site.id]);

  function selectGrowthTab(tab: GrowthTab) {
    setGrowthTab(tab);
    const target = tab === "week" ? "growth-week" : tab === "site" ? "growth-plan" : `growth-${tab}`;
    window.setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  const host = websiteHost(site);

  return (
    <div className="jr">
      <JourneyTopBar
        user={user}
        sites={sites}
        site={site}
        stage={stage}
        growthTab={growthTab}
        canPreview={choiceMade && (previewReady || Boolean(migration) || Boolean(proofId))}
        canChoose={assessmentReady || previewReady || Boolean(migration)}
        canPlan={choiceMade && (previewReady || Boolean(proofId))}
        canLaunch={choiceMade && previewReady}
        canGrow={live}
        onStage={setStage}
        onGrowthTab={selectGrowthTab}
        onSelectSite={onSelectSite}
        onNewProject={onNewProject}
        {...(onOpenOperator ? { onOpenOperator } : {})}
        onSignOut={onSignOut}
        onDeleteAccount={onDeleteAccount}
      />

      {(notice || error) && (
        <div className={`jr-toast ${error ? "error" : ""}`} role={error ? "alert" : "status"}>
          {error ? <AlertIcon /> : <CheckIcon />}
          <span>{error || notice}</span>
          {error && <button type="button" onClick={onDismissError} aria-label="Dismiss">×</button>}
        </div>
      )}

      {stage === "check" && (
        <JourneyCheckStage
          site={site}
          migration={migration}
          migrationJobs={migrationJobs}
          previewReady={previewReady}
          onAssessmentReady={markAssessmentReady}
          onChoose={() => setStage("choose")}
          onRestartHomepage={onRestartHomepage}
          onSelectFix={onSelectFix}
          onPreviewFix={(prompt) => {
            if (previewReady) {
              onSelectFix(prompt);
              setStage("preview");
              return;
            }
            setPendingFix(prompt);
            setStage(choiceMade && migration ? "preview" : "choose");
          }}
        />
      )}

      {stage === "preview" && proofId && (
        <JourneyQuickProof
          siteId={site.id}
          proofId={proofId}
          direction={direction}
          sourceHost={host}
          onNext={() => setStage("plan")}
          onChangeDirection={() => setStage("choose")}
          onRetry={() => {
            void onStartProof(direction)
              .then(() => setProofId(readJourneyProof(site.id, direction)))
              .catch(() => undefined);
          }}
        />
      )}

      {stage === "preview" && !proofId && (
        <JourneyPreviewStage
          site={site}
          migration={migration}
          change={selectedChange}
          changes={changes}
          usageMicroUsd={usageMicroUsd}
          hasActiveChange={hasActiveChange}
          onSubmitChange={async (text) => {
            await onSubmitChange(text);
            setPendingFix("");
          }}
          initialRequest={pendingFix}
          previewReady={previewReady}
          direction={direction}
          onChoose={() => setStage("choose")}
          onPlan={() => setStage("plan")}
          onDecision={onDecision}
          onSelectChange={onSelectChange}
          onRestartHomepage={() => {
            onRestartHomepage();
            setStage("check");
          }}
        />
      )}

      {stage === "choose" && (
        <JourneyChooseStage
          hasActiveChange={hasActiveChange}
          onContinue={async (chosen) => {
            setDirection(chosen);
            rememberJourneyPreference(site.id, "direction", chosen);
            try {
              setChoiceMade(true);
              if (!readJourneyProof(site.id, chosen)) await onStartProof(chosen);
              setProofId(readJourneyProof(site.id, chosen));
              setStage("preview");
            } catch {
              // The shell reports the failure in the toast; stay here so the choice can be retried.
            }
          }}
          onCall={() => { window.location.href = JOURNEY_CALL_URL; }}
        />
      )}

      {stage === "plan" && (
        <JourneyPlanStage
          site={site}
          direction={direction}
          proofId={proofId}
          email={user.email}
          onCall={() => { window.location.href = JOURNEY_CALL_URL; }}
        />
      )}

      {stage === "launch" && (
        <JourneyLaunchStage site={site} migration={migration} />
      )}

      {stage === "grow" && (
        <JourneyGrowStage
          site={site}
          migration={migration}
          user={user}
          changes={changes}
          hasActiveChange={hasActiveChange}
          plan={plan}
          onNewProject={onNewProject}
          onSubmitChange={onSubmitChange}
          onRecommendationPreview={(changeSetId) => {
            onRecommendationPreview(changeSetId);
            setStage("preview");
          }}
          onSelectFix={onSelectFix}
          onStage={setStage}
        />
      )}
    </div>
  );
}

function JourneyCheckStage({
  site,
  migration,
  migrationJobs,
  previewReady,
  onAssessmentReady,
  onChoose,
  onRestartHomepage,
  onSelectFix,
  onPreviewFix,
}: {
  site: SiteView;
  migration: MigrationRecord | null;
  migrationJobs: JobRecord[];
  previewReady: boolean;
  onAssessmentReady(): void;
  onChoose(): void;
  onRestartHomepage(): void;
  onSelectFix(prompt: string): void;
  onPreviewFix(prompt: string): void;
}) {
  const [assessment, setAssessment] = useState<AssessmentResponse | null>(null);
  const [loadError, setLoadError] = useState("");
  const host = sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name));

  useEffect(() => {
    let active = true;
    let timer = 0;
    async function load() {
      try {
        const response = await api<{ assessment: AssessmentResponse }>(`/sites/${site.id}/assessment`);
        if (!active) return;
        setAssessment(response.assessment);
        if (response.assessment.status === "ready") onAssessmentReady();
        if (response.assessment.status === "running" || response.assessment.status === "absent") {
          timer = window.setTimeout(() => void load(), 4_000);
        }
      } catch (reason) {
        if (active) setLoadError(messageFor(reason));
      }
    }
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [site.id, migration?.id, onAssessmentReady]);

  if (loadError) {
    return (
      <div className="jr-wrap">
        <div className="jr-card pad">
          <span className="jr-eyebrow">Website check</span>
          <h1 className="jr-h1 small">We could not read the check.</h1>
          <p className="jr-lede">{loadError}</p>
          <div className="jr-row" style={{ marginTop: 18 }}>
            <button className="jr-btn" type="button" onClick={onRestartHomepage}><RefreshIcon />Try again</button>
          </div>
        </div>
      </div>
    );
  }

  if (assessment?.status === "failed") {
    return (
      <div className="jr-wrap">
        <div className="jr-card pad">
          <span className="jr-eyebrow">Website check</span>
          <h1 className="jr-h1 small">We could not finish checking {host}.</h1>
          <p className="jr-lede">The address may be unreachable, blocked to crawlers, or not a public page.</p>
          <div className="jr-row" style={{ marginTop: 18 }}>
            <button className="jr-btn" type="button" onClick={onRestartHomepage}><RefreshIcon />Try the homepage again</button>
          </div>
        </div>
      </div>
    );
  }

  if ((assessment?.status === "absent" || assessment?.status === "running") && previewReady) {
    return (
      <div className="jr-wrap">
        <div className="jr-card pad">
          <span className="jr-eyebrow">Website check</span>
          <h1 className="jr-h1 small">Your audit is still being prepared.</h1>
          <p className="jr-lede">You can wait for the written report, or choose a direction from the evidence already available.</p>
          <div className="jr-row" style={{ marginTop: 18 }}>
            <button className="jr-btn" type="button" onClick={onChoose}>Choose a direction<ArrowIcon /></button>
          </div>
        </div>
      </div>
    );
  }

  if (assessment?.status === "ready" && assessment.report) {
    const report = assessment.report;
    return (
      <JourneyReportView
        report={report}
        host={host}
        siteId={site.id}
        onBuildPreview={onChoose}
        buildLabel="Choose what to change →"
        competitors={ownerCompetitorsOf(site)}
        onSaveCompetitors={async (competitors) => {
          const response = await api<{ competitors: OwnerNamedCompetitor[] }>(`/sites/${site.id}/competitors`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ competitors }),
          });
          return response.competitors;
        }}
        onPreviewFix={onPreviewFix}
        fullReport={
          <AssessmentReportView
            report={report}
            host={host}
            showLanguageSwitch={false}
            onSelectFix={onSelectFix}
          />
        }
        {...(migration && !previewReady ? {
          banner: (
            <div className="jr-card jr-build-banner">
              <span className="jr-working-dot" />
              <div>
                <strong>We are rebuilding the homepage privately.</strong>
                <p>{migration?.progressMessage || "You can keep this page open. The preview appears here when it is ready."}</p>
              </div>
              <span className="jr-mono">{Math.round(migration?.progressPercent ?? 0)}%</span>
            </div>
          ),
        } : {})}
      />
    );
  }

  return (
    <JourneyLiveCheck
      host={host}
      migration={migration}
      jobs={migrationJobs}
      startedAt={migration?.createdAt ?? site.createdAt}
      signedIn
    />
  );
}

function ownerCompetitorsOf(site: SiteView): OwnerNamedCompetitor[] {
  const stored = site.config.competitors;
  return Array.isArray(stored)
    ? stored.filter((entry): entry is OwnerNamedCompetitor => typeof entry === "object" && entry !== null && typeof (entry as OwnerNamedCompetitor).domain === "string")
    : [];
}

function JourneyLiveCheck({
  host,
  migration,
  jobs,
  startedAt,
  signedIn,
  saveCard,
  onSkip,
}: {
  host: string;
  migration?: MigrationRecord | null;
  jobs?: JobRecord[];
  startedAt?: string;
  signedIn?: boolean;
  saveCard?: React.ReactNode;
  onSkip?: (() => void) | undefined;
}) {
  const percent = Math.max(0, Math.min(100, Math.round(migration?.progressPercent ?? 0)));
  const [clock, setClock] = useState(() => Date.now());
  const checks = [
    { title: "Your site loads, and it is secure", detail: "HTTPS, the first response, and whether the page renders without scripts." },
    { title: "What Google reads", detail: "Titles, descriptions, headings, internal links and structured data." },
    { title: "Links and images", detail: "Every link we can reach, and the images that make the page heavy." },
    { title: "What AI assistants say about you", detail: "The questions your buyers ask, put to the AI assistants we can reach." },
    { title: "Speed on a phone", detail: "A real mobile browser, measured once — not a laboratory score." },
    { title: "Can it be rebuilt safely?", detail: "The page structure, the content delivery, and what a migration would touch." },
  ];
  useEffect(() => {
    if (migration) return;
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [migration]);

  const elapsedSeconds = startedAt ? Math.max(0, (clock - Date.parse(startedAt)) / 1_000) : 0;
  const estimatedPercent = Math.min(94, Math.max(3, Math.round(3 + elapsedSeconds * 1.1)));
  const observedPercent = migration ? percent : estimatedPercent;
  const thresholds = [8, 26, 44, 62, 80, 96];
  const activeIndex = thresholds.findIndex((threshold) => observedPercent < threshold);
  const currentIndex = activeIndex === -1 ? checks.length - 1 : activeIndex;
  const completed = activeIndex === -1 ? checks.length : activeIndex;

  return (
    <div className="jr-wrap">
      <div className="jr-check-head">
        <span className="jr-eyebrow"><i className="pulse" />Free website check · Running</span>
        <h1 className="jr-h1">Checking {host}</h1>
        <p className="jr-lede">About a minute. We look at your site the way Google, AI assistants and a first-time visitor do. Nothing on your site is changed.</p>
      </div>

      <div className="jr-check-grid">
        <div className="jr-card jr-check-list">
          <div className="jr-check-summary" aria-live="polite">
            <span>{completed} of {checks.length} checks complete</span>
            <strong>{observedPercent}%</strong>
          </div>
          {checks.map((check, index) => {
            const state = index < completed ? "done" : index === currentIndex ? "running" : "queued";
            const lower = index === 0 ? 0 : thresholds[index - 1]!;
            const upper = thresholds[index]!;
            const stepPercent = state === "running"
              ? Math.max(4, Math.min(100, ((observedPercent - lower) / (upper - lower)) * 100))
              : 0;
            return (
            <div className={`jr-check-item ${state}`} key={check.title}>
              <span className="marker">{state === "done" ? <CheckIcon /> : <span className="ring" />}</span>
              <div>
                <strong>{check.title}</strong>
                <p>{check.detail}</p>
                {state === "running" && (
                  <div className="jr-progress" aria-label="Check progress">
                    <i style={{ width: `${stepPercent}%` }} />
                  </div>
                )}
              </div>
              <span className="stage">{state === "done" ? "Complete" : state === "running" ? "Checking" : "Queued"}</span>
            </div>
            );
          })}
        </div>

        <aside className="jr-stack">
          <div className="jr-card jr-look-card">
            <span className="jr-eyebrow">On Google</span>
            <span className="site-line">{host}</span>
            <p>We are running the searches your buyers run. The report shows where you appear and names any search source that could not be checked.</p>
            <span className="jr-chip outline">Waiting for the scan</span>
          </div>
          <div className="jr-card jr-look-card">
            <span className="jr-eyebrow">In AI answers</span>
            <p>We ask the assistants buyers ask. Every answer we get is recorded word for word; a source that could not run is named in the report.</p>
            <span className="jr-chip outline">Waiting for the scan</span>
          </div>
          {saveCard ? (
            saveCard
          ) : signedIn ? (
            <div className="jr-save">
              <span className="jr-eyebrow" style={{ color: "#a39e94" }}>While you wait</span>
              <h3>We will email you when it is ready.</h3>
              <p>The private preview appears here too. Keep this page open, or come back whenever you like.</p>
              <div className="jr-row" style={{ gap: 8 }}>
                <span className="jr-chip" style={{ background: "#2a2926", color: "#e6e1d8" }}>{jobs?.length ?? 0} jobs running</span>
                <span className="jr-chip" style={{ background: "#2a2926", color: "#e6e1d8" }}>{migration?.currentStep?.replaceAll("_", " ") ?? "starting"}</span>
              </div>
            </div>
          ) : null}
          {onSkip && (
            <button className="jr-btn ghost jr-skip" type="button" onClick={onSkip}>Skip to the answer<ArrowIcon /></button>
          )}
        </aside>
      </div>
    </div>
  );
}

function SiteScopeBand({ report, siteId }: { report: SiteAgainAssessmentReportV1; siteId?: string }) {
  const [audit, setAudit] = useState<SiteAuditSummary | null>(null);
  const [scopeError, setScopeError] = useState(false);
  const started = useRef(false);
  const sampledPages = report.buyerEvidence?.pageHealth?.pagesChecked ?? null;

  useEffect(() => {
    if (!siteId) return;
    let active = true;
    let timer = 0;

    async function load() {
      try {
        let response = await api<{ audits: SiteAuditSummary[] }>(`/sites/${siteId}/audits`);
        let latest = response.audits[0] ?? null;
        if (!latest && !started.current) {
          started.current = true;
          await api(`/sites/${siteId}/audits`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Idempotency-Key": `site-inventory-${siteId}`,
            },
            body: "{}",
          });
          response = await api<{ audits: SiteAuditSummary[] }>(`/sites/${siteId}/audits`);
          latest = response.audits[0] ?? null;
        }
        if (!active) return;
        setAudit(latest);
        setScopeError(false);
        if (latest && !AUDIT_TERMINAL.has(latest.status)) {
          timer = window.setTimeout(() => void load(), 4_000);
        }
      } catch {
        if (active) setScopeError(true);
      }
    }

    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [siteId]);

  const mappedPages = audit?.pageCount ?? sampledPages;
  const running = audit ? !AUDIT_TERMINAL.has(audit.status) : false;
  const pageLabel = running ? "pages found so far" : "pages mapped";
  const summary = mappedPages == null
    ? "The page inventory will appear when the crawl reports its first page."
    : running
      ? `${mappedPages.toLocaleString()} page${mappedPages === 1 ? "" : "s"} found so far. The full inventory is still updating.`
      : `${mappedPages.toLocaleString()} page${mappedPages === 1 ? "" : "s"} mapped across the site. Every page now has a place in the migration plan.`;

  return (
    <section className="jr-scope-band" aria-label="Website inventory">
      <div className="jr-scope-copy">
        <span className="jr-eyebrow">{running ? <i className="pulse" /> : null}Site inventory</span>
        <h2>{running ? "We are counting every page." : mappedPages != null ? `${mappedPages.toLocaleString()} pages, one plan.` : "Page inventory in progress."}</h2>
        <p>
          {scopeError
            ? "The deeper page map could not start. The check below still reflects the pages already audited."
            : summary}
        </p>
      </div>
      <div className="jr-scope-facts">
        <div><strong>{mappedPages ?? "—"}</strong><span>{pageLabel}</span></div>
        <div><strong>{report.findings.length}</strong><span>evidence-backed findings</span></div>
        <div><strong>{report.findings.filter((finding) => finding.safetyClass === "safe_preview").length}</strong><span>safe to improve first</span></div>
        <div><strong>{report.qualification.status === "supported" ? "Ready" : "Review"}</strong><span>migration readiness</span></div>
      </div>
    </section>
  );
}

function JourneyReportView({
  report,
  host,
  siteId,
  onBuildPreview,
  buildLabel,
  buildDisabled,
  fullReport,
  banner,
  showLanguageSwitch,
  competitors,
  onSaveCompetitors,
  onPreviewFix,
  onSignIn,
}: {
  report: SiteAgainAssessmentReportV1;
  host: string;
  siteId?: string;
  onBuildPreview(): void;
  buildLabel: string;
  buildDisabled?: boolean;
  fullReport?: React.ReactNode;
  banner?: React.ReactNode;
  showLanguageSwitch?: boolean;
  competitors?: OwnerNamedCompetitor[];
  onSaveCompetitors?: ((competitors: OwnerNamedCompetitor[]) => Promise<OwnerNamedCompetitor[]>) | undefined;
  /** Leads from one recommended fix into the rebuild preview. */
  onPreviewFix(prompt: string): void;
  /** Where a visitor without an account signs in to save or connect anything. */
  onSignIn?: (() => void) | undefined;
}) {
  const locale = useLocale();
  const t = assessmentCopy(locale);
  const [question, setQuestion] = useState("");
  const mailSubject = encodeURIComponent(`Question about the website check for ${host}`);
  const mailBody = encodeURIComponent(question.trim() || "I have a question about my website check.");
  const askHref = `mailto:ismael@siteagain.com?subject=${mailSubject}&body=${mailBody}`;
  const supported = report.qualification.status !== "unsupported";

  return (
    <div className="jr-wrap wide">
      <div className="jr-split report">
        <main className="jr-main">
          <OwnerAudit
            report={report}
            host={host}
            {...(competitors ? { competitors } : {})}
            onSaveCompetitors={onSaveCompetitors}
            onPreviewFix={onPreviewFix}
            onSignIn={onSignIn}
            banner={banner}
            {...(siteId ? { connectSlot: <SearchConsoleSection siteId={siteId as SiteId} /> } : {})}
            technicalSlot={
              <>
                <SiteScopeBand report={report} {...(siteId ? { siteId } : {})} />
                {fullReport && <div className="oa-full-report">{fullReport}</div>}
              </>
            }
          />
        </main>

        <aside className="jr-aside">
          {showLanguageSwitch && <div className="jr-card pad"><LanguageSwitch /></div>}
          <section className="jr-card jr-next">
            <span className="jr-eyebrow">Your next decision</span>
            <h2>{supported ? "Choose the direction before we build." : "Let a person check this one."}</h2>
            {supported ? (
              <>
                <ul>
                  <li><CheckIcon />Move as-is, improve conversion or redesign</li>
                  <li><CheckIcon />Choose a growth plan before work starts</li>
                  <li><CheckIcon />Preview and approve every change</li>
                </ul>
                <button className="jr-btn wide" type="button" onClick={onBuildPreview} disabled={buildDisabled}>{buildLabel}</button>
              </>
            ) : (
              <>
                <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>
                  Something on this site prevents a reliable automated rebuild. A person will read the evidence and come back to you with a safe plan.
                </p>
                <a className="jr-btn wide" href="mailto:ismael@siteagain.com?subject=SiteAgain%20website%20check">Talk to a person</a>
              </>
            )}
            <a className="jr-btn ghost wide" href="mailto:ismael@siteagain.com?subject=SiteAgain%20website%20check">Ask a question by email</a>
          </section>
          <section className="jr-ask" aria-label="Ask about your results">
            <span className="jr-eyebrow">Ask about your results</span>
            <textarea
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Why does ChatGPT not mention me?"
              aria-label="Your question"
            />
            <div className="jr-row" style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
              <div className="jr-ask-chips">
                {["What would you fix first?", "How long does it take?"].map((prompt) => (
                  <button className="jr-chip jr-ask-chip" type="button" key={prompt} onClick={() => setQuestion(prompt)}>{prompt}</button>
                ))}
              </div>
              <a className="jr-btn ghost small" href={askHref}>Send<ArrowIcon /></a>
            </div>
          </section>
          <p className="jr-muted" style={{ fontSize: 12, margin: 0 }}>
            {report.partial ? t.partial : "Everything we checked is in the technical details."}
          </p>
        </aside>
      </div>
    </div>
  );
}

function JourneyPreviewStage({
  site,
  migration,
  change,
  changes,
  usageMicroUsd,
  hasActiveChange,
  onSubmitChange,
  previewReady,
  direction,
  onChoose,
  onPlan,
  onDecision,
  onSelectChange,
  onRestartHomepage,
  initialRequest,
}: {
  site: SiteView;
  migration: MigrationRecord | null;
  change: ChangeView | null;
  changes: ChangeView[];
  usageMicroUsd: number;
  hasActiveChange: boolean;
  onSubmitChange(text: string): Promise<void>;
  /** A fix chosen in the audit, waiting to be sent once the preview exists. */
  initialRequest?: string;
  previewReady: boolean;
  direction: JourneyDirection;
  onChoose(): void;
  onPlan(): void;
  onDecision(action: "approve" | "reject" | "rollback"): void;
  onSelectChange(id: string): void;
  onRestartHomepage(): void;
}) {
  const [mode, setMode] = useState<"today" | "new">("new");
  const [request, setRequest] = useState(initialRequest ?? "");
  const [proofOpen, setProofOpen] = useState(false);
  useEffect(() => {
    if (initialRequest) setRequest(initialRequest);
  }, [initialRequest]);
  const candidateUrl = change?.previewUrl ?? (isMigrationPreviewReady(migration) ? site.previewUrl : null);
  const publicUrl = candidateUrl ? publicPreviewUrl(candidateUrl) : null;
  const embeddedUrl = candidateUrl ? embeddedPreviewUrl(candidateUrl) : null;
  const beforeUrl = change?.visualDiff?.captures?.find((capture) => capture.beforeUrl)?.beforeUrl
    ?? change?.visualDiff?.beforeUrl
    ?? null;
  useEffect(() => {
    setMode("new");
  }, [change?.id]);
  useEffect(() => {
    if (!beforeUrl) setMode("new");
  }, [beforeUrl]);
  const gate = migration?.qualityGateReport ?? null;
  const comparison = gate?.comparison ?? null;
  const seo = migration?.seoScore != null ? Math.round(migration.seoScore <= 1 ? migration.seoScore * 100 : migration.seoScore) : null;
  const fidelity = gate?.visualFidelity != null ? Math.round(gate.visualFidelity <= 1 ? gate.visualFidelity * 100 : gate.visualFidelity) : null;
  const performance = gate?.performanceScore != null ? Math.round(gate.performanceScore <= 1 ? gate.performanceScore * 100 : gate.performanceScore) : null;
  const brokenLinks = gate?.brokenLinks ?? null;
  const sourceHostValue = sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name));
  const hasProof = Boolean(change) || Boolean(comparison);

  if (!previewReady) {
    return (
      <JourneyPreviewPending
        site={site}
        migration={migration}
        direction={direction}
        onChoose={onChoose}
      />
    );
  }

  function sendRequest() {
    const text = request.trim();
    if (!text || hasActiveChange) return;
    void onSubmitChange(text).catch(() => undefined);
    setRequest("");
  }

  return (
    <div className="jr-wrap wide">
      <div className="jr-preview-head">
        <div>
          <div className="jr-row">
            <span className="jr-chip ok">Preview ready</span>
            <span className="jr-chip">Private · only you can see it</span>
          </div>
          <h1 className="jr-h1">Your new homepage is ready.</h1>
        </div>
        <div className="jr-row">
          {beforeUrl && (
            <div className="jr-seg" role="group" aria-label="Compare versions">
              <button type="button" className={mode === "today" ? "on" : ""} onClick={() => setMode("today")}>Today</button>
              <button type="button" className={mode === "new" ? "on" : ""} onClick={() => setMode("new")}>New version</button>
            </div>
          )}
          {changes.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="jr-btn ghost small" type="button">History · {changes.length}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="history-menu">
                <DropdownMenuLabel>Your versions</DropdownMenuLabel>
                {changes.map((item) => (
                  <DropdownMenuItem key={item.id} onSelect={() => onSelectChange(item.id)}>
                    <StatusDot status={item.status} />
                    <span className="menu-item-copy">
                      <strong>{item.requestText}</strong>
                      <small>{relativeTime(item.createdAt)} · {shortStatus(item.status)}</small>
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="jr-split preview">
        <main className="jr-main">
          <div className="jr-frame">
            <div className="jr-frame-bar">
              <div className="jr-dots"><i /><i /><i /></div>
              <div className="jr-url">{mode === "new" ? displayUrl(publicUrl ?? sourceHostValue) : `${sourceHostValue} · today`}</div>
              {publicUrl && <a className="jr-chip outline" href={publicUrl} target="_blank" rel="noreferrer">Open full screen ↗</a>}
            </div>
            <JourneyPreviewFrame
              mode={mode}
              embeddedUrl={embeddedUrl}
              publicUrl={publicUrl}
              beforeUrl={beforeUrl}
              siteName={site.name}
              sourceUrl={migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name)}
            />
          </div>

          <div className="jr-section-head" style={{ marginTop: 28 }}>
            <h2>Checked before you saw it</h2>
            <button className="jr-link" type="button" disabled={!hasProof} onClick={() => setProofOpen(true)}>Full release proof →</button>
          </div>
          <div className="jr-proof-grid">
            <div className="jr-card jr-proof"><small>Quality score</small><strong>{qualityScore(migration) ?? "—"}</strong><span>{migration?.gateVerdict?.replaceAll("_", " ") ?? "Not measured"}</span></div>
            <div className="jr-card jr-proof"><small>SEO preserved</small><strong>{seo != null ? `${seo}%` : "—"}</strong><span>titles, structure and addresses</span></div>
            <div className="jr-card jr-proof"><small>Broken links</small><strong>{brokenLinks ?? "—"}</strong><span>{gate?.functionalTests ? `${gate.functionalTests.passed}/${gate.functionalTests.total} checks passed` : "not measured"}</span></div>
            <div className="jr-card jr-proof"><small>Phone performance</small><strong>{performance != null ? `${performance}` : "—"}</strong><span>{performance != null ? "static performance budget" : "not measured"}</span></div>
          </div>
          {proofOpen && (
            <JourneyProofDialog
              change={change}
              migration={migration}
              usageMicroUsd={usageMicroUsd}
              onDecision={onDecision}
              onClose={() => setProofOpen(false)}
            />
          )}
        </main>

        <aside className="jr-aside">
          {hasNestedPreviewPath(site.previewUrl) && (
            <section className="jr-card jr-next">
              <span className="jr-eyebrow">Homepage needed</span>
              <h2>This preview was built from an inner page.</h2>
              <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>The homepage is the strongest entry point. We can rebuild it from the root URL; your live site stays untouched.</p>
              <button className="jr-btn ghost wide" type="button" onClick={onRestartHomepage}><RefreshIcon />Rebuild from the homepage</button>
            </section>
          )}
          {gate && (gate.verdict === "NEEDS_REVIEW" || gate.reviewReasons.length > 0) && (
            <section className="jr-card jr-next">
              <span className="jr-chip" style={{ alignSelf: "flex-start", background: "#fdf0dc", color: "#8a5a11" }}>One human check</span>
              <h2>One detail needs a human look.</h2>
              <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>
                The build is ready, but a person should check {gate.reviewReasons.length || 1} fidelity detail{(gate.reviewReasons.length || 1) === 1 ? "" : "s"} before it goes live.
              </p>
              <a className="jr-btn ghost wide" href="mailto:ismael@siteagain.com?subject=SiteAgain%20preview%20review">Ask us to review it</a>
            </section>
          )}
          {change && (
            <section className="jr-card jr-next">
              <div className="jr-row" style={{ justifyContent: "space-between" }}>
                <span className="jr-eyebrow">Your change</span>
                <span className={`jr-chip ${change.status === "awaiting_approval" ? "ok" : ""}`}>{shortStatus(change.status)}</span>
              </div>
              <h2>{change.status === "awaiting_approval" ? "Ready for your approval." : change.status === "deployed" ? "Published." : "Building your change."}</h2>
              <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>{change.requestText}</p>
              <div className="jr-stack">
                {change.status === "awaiting_approval" && (
                  <>
                    <button className="jr-btn wide" type="button" onClick={() => onDecision("approve")}><CheckIcon />Approve &amp; publish</button>
                    <button className="jr-btn ghost wide" type="button" onClick={() => onDecision("reject")}>Reject this version</button>
                  </>
                )}
                {change.status === "deployed" && (
                  <button className="jr-btn ghost wide" type="button" onClick={() => onDecision("rollback")}>Roll back</button>
                )}
                <button className="jr-btn ghost wide" type="button" onClick={() => setProofOpen(true)}>See the full proof</button>
              </div>
            </section>
          )}

          <section className="jr-card jr-next">
            <span className="jr-eyebrow">Your next step</span>
            <h2>Like it? Choose a plan.</h2>
            <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>
              Pick the level of momentum after launch. Nothing is charged or published until you confirm.
            </p>
            <button className="jr-btn wide" type="button" onClick={onPlan}>Choose a plan<ArrowIcon /></button>
          </section>

          <section className="jr-ask" aria-label="Ask for a change">
            <span className="jr-eyebrow">Want something different? Just say it</span>
            <textarea
              value={request}
              onChange={(event) => setRequest(event.target.value)}
              placeholder="Make the headline shorter and put the call button in green"
              aria-label="Request a change"
              disabled={hasActiveChange}
            />
            <div className="jr-row" style={{ justifyContent: "space-between" }}>
              <small className="jr-muted" style={{ fontSize: 12 }}>{hasActiveChange ? "A change is already in progress." : "You will get a new preview to compare. Nothing goes live."}</small>
              <button className="jr-btn small" type="button" onClick={sendRequest} disabled={hasActiveChange || !request.trim()}>Send</button>
            </div>
          </section>

          <section className="jr-card jr-kept">
            <h3>Kept from your current site</h3>
            <ul>
              {fidelity != null && <li><CheckIcon />Visual match: {fidelity}%</li>}
              {seo != null && <li><CheckIcon />SEO preserved: {seo}%</li>}
              {comparison && <li><CheckIcon />Current → preview: {comparison.summary.wins} gains, {comparison.summary.losses} drops</li>}
              {!comparison && !fidelity && !seo && <li><CheckIcon />Your content and addresses are kept unless the plan says otherwise</li>}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function JourneyPreviewPending({
  site,
  migration,
  direction,
  onChoose,
}: {
  site: SiteView;
  migration: MigrationRecord | null;
  direction: JourneyDirection;
  onChoose(): void;
}) {
  const percent = Math.max(0, Math.min(100, Math.round(migration?.progressPercent ?? 0)));
  const host = sourceHost(migration?.sourceUrl ?? String(site.config.sourceUrl ?? site.name));
  const label = direction === "as-is" ? "As-is migration" : "Full rebuild preview";
  const stages = [
    { label: "Read the current site", at: 6 },
    { label: "Keep pages and content intact", at: 28 },
    { label: "Rebuild the page system", at: 56 },
    { label: "Check speed, SEO and behaviour", at: 78 },
    { label: "Publish a private preview", at: 96 },
  ];
  const activeIndex = stages.findIndex((stage) => percent < stage.at);
  const completed = activeIndex === -1 ? stages.length : activeIndex;

  return (
    <div className="jr-wrap wide jr-preview-wait">
      <div className="jr-preview-wait-head">
        <span className="jr-eyebrow"><i className="pulse" />Private preview in progress · {host}</span>
        <h1 className="jr-h1">{label}. Your live site stays untouched.</h1>
        <p className="jr-lede">
          We are building only the direction you chose. You will see the result beside the current page before anything can go live.
        </p>
        <div className="jr-row">
          <span className="jr-chip ink">{percent}% complete</span>
          <button className="jr-btn ghost small" type="button" onClick={onChoose}>Change my direction</button>
        </div>
      </div>

      <div className="jr-preview-wait-grid">
        <section className="jr-preview-wait-stage">
          <div className="jr-preview-wait-visual" aria-hidden="true">
            <span>{percent}</span>
            <i style={{ height: `${Math.max(8, percent)}%` }} />
          </div>
          <div>
            <span className="jr-eyebrow">Current work</span>
            <h2>{migration?.progressMessage ?? "Preparing the build"}</h2>
            <p>{migration?.currentStep?.replaceAll(".", " · ").replaceAll("_", " ") ?? "Waiting for the build queue"}</p>
          </div>
        </section>

        <section className="jr-preview-wait-list">
          {stages.map((stage, index) => {
            const state = index < completed ? "done" : index === activeIndex ? "running" : "queued";
            return (
              <div className={`jr-preview-wait-item ${state}`} key={stage.label}>
                <span>{state === "done" ? <CheckIcon /> : index + 1}</span>
                <strong>{stage.label}</strong>
                <small>{state === "done" ? "Complete" : state === "running" ? "In progress" : "Queued"}</small>
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
}

function JourneyPreviewFrame({
  mode,
  embeddedUrl,
  publicUrl,
  beforeUrl,
  siteName,
  sourceUrl,
}: {
  mode: "today" | "new";
  embeddedUrl: string | null;
  publicUrl: string | null;
  beforeUrl: string | null;
  siteName: string;
  sourceUrl: string;
}) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setStatus("loading");
    const timeout = window.setTimeout(() => setStatus((current) => current === "loading" ? "error" : current), 15_000);
    return () => window.clearTimeout(timeout);
  }, [embeddedUrl, reloadKey, mode]);

  if (mode === "today") {
    if (beforeUrl) {
      return <div className="jr-frame-body"><img src={beforeUrl} alt={`${siteName} before the rebuild`} /></div>;
    }
    return (
      <div className="jr-frame-body">
        <iframe
          key={`today:${sourceUrl}:${reloadKey}`}
          src={sourceUrl}
          title={`${siteName} today`}
          onLoad={() => setStatus("ready")}
          onError={() => setStatus("error")}
        />
        {status === "error" && (
          <div className="jr-frame-note">
            <p>Your live site does not allow embedding. <a href={sourceUrl} target="_blank" rel="noreferrer">Open it in a new tab ↗</a></p>
          </div>
        )}
      </div>
    );
  }

  if (!embeddedUrl) {
    return <div className="jr-frame-body"><div className="jr-frame-note">The preview is still being prepared.</div></div>;
  }

  return (
    <div className="jr-frame-body">
      <iframe
        key={`new:${embeddedUrl}:${reloadKey}`}
        src={embeddedUrl}
        title={`${siteName} new homepage`}
        onLoad={() => setStatus("ready")}
        onError={() => setStatus("error")}
      />
      {status !== "ready" && (
        <div className={`jr-frame-${status === "loading" ? "loading" : "note"}`} role="status">
          {status === "loading" ? <><SpinnerIcon />Loading your private preview…</> : (
            <p>The preview took too long to respond. <a href={publicUrl ?? embeddedUrl} target="_blank" rel="noreferrer">Open it in a new tab ↗</a> <button className="jr-link" type="button" onClick={() => { setStatus("loading"); setReloadKey((value) => value + 1); }}>Retry</button></p>
          )}
        </div>
      )}
    </div>
  );
}

function JourneyProofDialog({
  change,
  migration,
  usageMicroUsd,
  onDecision,
  onClose,
}: {
  change: ChangeView | null;
  migration: MigrationRecord | null;
  usageMicroUsd: number;
  onDecision(action: "approve" | "reject" | "rollback"): void;
  onClose(): void;
}) {
  return (
    <div className="jr-modal" role="dialog" aria-modal="true" aria-label="Release proof" onClick={onClose}>
      <div className="jr-modal-card" onClick={(event) => event.stopPropagation()}>
        <header className="jr-modal-head">
          <strong>Release proof</strong>
          <button className="jr-icon-btn" type="button" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="jr-modal-body">
          {change ? (
            <ProofView change={change} usageMicroUsd={usageMicroUsd} onDecision={onDecision} />
          ) : migration?.qualityGateReport?.comparison ? (
            <MigrationComparisonCard report={migration.qualityGateReport.comparison} />
          ) : (
            <p className="jr-muted">The proof appears here when the build reports its checks.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const JOURNEY_DIRECTIONS: Array<{ id: JourneyDirection; name: string; intro: string; features: string[] }> = [
  {
    id: "as-is",
    name: "Migrate as it is",
    intro: "Keep the same structure, copy and brand. Modernise the foundation without a redesign.",
    features: ["See your proof in seconds", "Search and AI gaps closed from your own content", "URLs and content preserved"],
  },
  {
    id: "conversion",
    name: "Improve conversion",
    intro: "Keep your look. Reorder the page so visitors understand what you do and what to do next.",
    features: ["See your proof in seconds", "Your colours, logo and words", "A clear next step, and contact on every screen"],
  },
  {
    id: "redesign",
    name: "New look",
    intro: "Your words and images in a new visual direction, built from your own accent colour.",
    features: ["See your proof in seconds", "New typography and page rhythm", "Nothing invented: only what your page says"],
  },
];

/**
 * Direction is one click: choosing a card starts its proof straight away.
 * The plan is chosen afterwards, once the owner has seen the result.
 */
function JourneyChooseStage({
  hasActiveChange,
  onContinue,
  onCall,
}: {
  hasActiveChange: boolean;
  onContinue(direction: JourneyDirection): Promise<void>;
  onCall(): void;
}) {
  const [starting, setStarting] = useState<JourneyDirection | null>(null);

  async function choose(direction: JourneyDirection) {
    if (starting || hasActiveChange) return;
    setStarting(direction);
    try {
      await onContinue(direction);
    } finally {
      setStarting(null);
    }
  }

  return (
    <div className="jr-wrap pricing">
      <div className="jr-pricing-head">
        <span className="jr-eyebrow">Step 2 · Audit complete</span>
        <h1 className="jr-h1">Choose a direction. See it right away.</h1>
        <p className="jr-lede">
          Each direction shows you a private proof of your homepage in seconds. You choose a plan only after you have seen it.
        </p>
        {hasActiveChange && (
          <p className="jr-error" style={{ marginTop: 14 }}>
            <AlertIcon />You already have a change in progress. Finish or approve it before starting a new proof.
          </p>
        )}
      </div>

      <section className="jr-decision-section" aria-labelledby="journey-direction-title">
        <div className="jr-decision-head">
          <div>
            <span className="jr-eyebrow">01 · Direction</span>
            <h2 id="journey-direction-title">What should we show you?</h2>
          </div>
          <p>Every option starts from the pages and evidence already audited.</p>
        </div>
        <div className="jr-direction-grid">
          {JOURNEY_DIRECTIONS.map((option) => (
            <button
              type="button"
              key={option.id}
              className={`jr-direction ${starting === option.id ? "on" : ""}`}
              disabled={hasActiveChange || (starting !== null && starting !== option.id)}
              aria-busy={starting === option.id}
              onClick={() => void choose(option.id)}
            >
              <div className="jr-direction-top">
                <h3>{option.name}</h3>
                {starting === option.id ? <SpinnerIcon /> : <ArrowIcon />}
              </div>
              <p>{option.intro}</p>
              <hr />
              <ul>{option.features.map((feature) => <li key={feature}><CheckIcon />{feature}</li>)}</ul>
            </button>
          ))}
        </div>
      </section>

      <section className="jr-timeline jr-process-band">
        <h2>What happens next</h2>
        <div className="jr-timeline-grid">
          {[
            ["You see the proof", "A private version of your homepage in seconds, in the direction you chose. Your live site stays untouched."],
            ["You choose a plan", "No card is taken in the app; we confirm by email before billing starts."],
            ["We build the full site", "Every page, checked for SEO, speed and visual match before anything can go live."],
            ["You approve the launch", "Domain details appear only now, when they are relevant and you are ready."],
          ].map(([title, detail], index) => (
            <div className="jr-timeline-step" key={title}>
              <span className="num">{index + 1}</span>
              <strong>{title}</strong>
              <p>{detail}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="jr-faqs">
        <div className="jr-card jr-faq"><strong>Can I change my mind?</strong><p>Yes. Come back here and try another direction at any time before launch.</p></div>
        <div className="jr-card jr-faq"><strong>Rather talk it through?</strong><p>Fifteen minutes with a person, before you choose anything.</p><button className="jr-link" type="button" style={{ marginTop: 8 }} onClick={onCall}>Book a call →</button></div>
      </div>
    </div>
  );
}

/**
 * The plan comes after the proof. Choosing one records a request the operator
 * sees and confirms by email: there is no self-serve checkout, and the full
 * build starts only once the plan is active (ADR-0031).
 */
function JourneyPlanStage({
  site,
  direction,
  proofId,
  email,
  onCall,
}: {
  site: SiteView;
  direction: JourneyDirection;
  proofId: string | null;
  email: string;
  onCall(): void;
}) {
  const [plan, setPlan] = useState<JourneyPlanKey>(() => readJourneyPlan(site.id));
  const [planOpen, setPlanOpen] = useState(false);
  const [request, setRequest] = useState<"idle" | "sending" | "requested">("idle");
  const [requestError, setRequestError] = useState("");
  const activePlan = JOURNEY_PLANS.find((item) => item.key === plan) ?? JOURNEY_PLANS[1]!;
  const activeDirection = JOURNEY_DIRECTIONS.find((item) => item.id === direction) ?? JOURNEY_DIRECTIONS[0]!;
  const enterprise = activePlan.key === "enterprise";
  const price = `${activePlan.price}${activePlan.unit === "/ month" ? " / month" : ""}`;

  useEffect(() => {
    setRequest("idle");
    setRequestError("");
  }, [site.id]);

  async function requestPlan() {
    if (request !== "idle") return;
    setRequest("sending");
    setRequestError("");
    rememberJourneyPreference(site.id, "plan", plan);
    try {
      await api(`/sites/${site.id}/growth/upgrade-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          note: `Plan: ${activePlan.name} (${price}) · Direction: ${activeDirection.name}${proofId ? ` · Proof: ${proofId}` : ""}`,
        }),
      });
      setRequest("requested");
    } catch (reason) {
      setRequest("idle");
      setRequestError(messageFor(reason));
    }
  }

  return (
    <>
      <div className="jr-wrap pricing">
        <div className="jr-pricing-head">
          <span className="jr-eyebrow">Step 4 · After your proof</span>
          <h1 className="jr-h1">Choose how we work together.</h1>
          <p className="jr-lede">
            Direction: {activeDirection.name}. Pick the level of momentum after launch. Nothing is charged in the app, and the full build starts once your plan is confirmed.
          </p>
        </div>

        <section className="jr-decision-section" aria-labelledby="journey-plan-title">
          <div className="jr-decision-head">
            <div>
              <span className="jr-eyebrow">Ongoing partnership</span>
              <h2 id="journey-plan-title">Choose the level of momentum.</h2>
            </div>
            <p>Start after launch. Keep full control on every plan.</p>
          </div>
          <div className="jr-plan-grid" role="radiogroup" aria-label="Growth plan">
            {JOURNEY_PLANS.map((option) => (
              <button
                type="button"
                role="radio"
                aria-checked={plan === option.key}
                key={option.key}
                className={`jr-plan-choice ${plan === option.key ? "on" : ""} ${option.recommended ? "featured" : ""}`}
                disabled={request !== "idle"}
                onClick={() => setPlan(option.key)}
              >
                <div className="jr-plan-choice-top">
                  <div>
                    <span>{option.recommended ? "Recommended" : "Plan"}</span>
                    <h3>{option.name}</h3>
                  </div>
                  <span className="jr-radio" aria-hidden="true" />
                </div>
                <div className="jr-price">{option.price}<small>{option.unit}</small></div>
                <p>{option.intro}</p>
                <ul>{option.features.map((feature) => <li key={feature}><CheckIcon />{feature}</li>)}</ul>
              </button>
            ))}
          </div>
        </section>

        <div className="jr-promise-band">
          <div><strong>No card in the app</strong><span>We confirm by email before billing starts.</span></div>
          <div><strong>Full control</strong><span>Approve, pause and roll back any change.</span></div>
          <div><strong>You own the work</strong><span>Site, content and settings remain yours.</span></div>
          <div><strong>Human strategy</strong><span>Autopilot is an option, never a black box.</span></div>
        </div>

        <div className="jr-faqs">
          <div className="jr-card jr-faq"><strong>Is it really mine?</strong><p>Yes. The code, repository and settings are yours to download at any time, even if you leave.</p></div>
          <div className="jr-card jr-faq"><strong>Need a bespoke scope?</strong><p>Use Enterprise for multi-site work, integrations, SLAs or a custom reporting model.</p><button className="jr-link" type="button" style={{ marginTop: 8 }} onClick={() => setPlanOpen(true)}>Open the full-site plan →</button></div>
        </div>
      </div>

      {planOpen && (
        <div className="jr-modal" role="dialog" aria-modal="true" aria-label="Full-site plan" onClick={() => setPlanOpen(false)}>
          <div className="jr-modal-card" onClick={(event) => event.stopPropagation()}>
            <header className="jr-modal-head">
              <strong>Full-site plan</strong>
              <button className="jr-icon-btn" type="button" onClick={() => setPlanOpen(false)} aria-label="Close">×</button>
            </header>
            <div className="jr-modal-body">
              <MigrationPlanView site={site} />
            </div>
          </div>
        </div>
      )}

      <div className="jr-sticky">
        <div className="jr-sticky-in">
          <div>
            <small>{request === "requested" ? "Request received" : "Your choice"}</small>
            <strong>
              {request === "requested"
                ? `${activePlan.name} requested. We confirm at ${email} before billing or the full build starts, and email you again when your new site is ready to review.`
                : `${activeDirection.name} · ${activePlan.name} ${price}`}
            </strong>
            {requestError && <small role="alert" style={{ color: "#f3b3a6", marginTop: 4 }}>{requestError}</small>}
          </div>
          <div className="jr-row">
            <button className="jr-btn ghost" type="button" onClick={onCall}>Talk to a person</button>
            {request !== "requested" && (
              <button
                className="jr-btn"
                type="button"
                disabled={request === "sending"}
                onClick={() => {
                  if (enterprise) onCall();
                  else void requestPlan();
                }}
              >
                {enterprise ? "Request an Enterprise plan" : request === "sending" ? "Sending…" : `Continue with ${activePlan.name}`}<ArrowIcon />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function JourneyLaunchStage({ site, migration }: { site: SiteView; migration: MigrationRecord | null }) {
  const host = websiteHost(site);
  return (
    <div className="jr-wrap wide jr-launch">
      <header className="jr-launch-head">
        <div className="jr-row">
          <span className="jr-chip ok">Preview ready</span>
          <span className="jr-chip">Your live site stays untouched</span>
        </div>
        <h1 className="jr-h1">Put it live on {host}.</h1>
        <p className="jr-lede jr-launch-lede">
          You keep control of your domain the whole way. Nothing switches until a rehearsal on your real domain has passed, and you can roll back with one click.
        </p>
      </header>
      <div className="jr-launch-grid">
        <LaunchView site={site} migration={migration} />
        <aside className="launch-aside">
          <section className="jr-card safety-card">
            <h3>Your safety net</h3>
            <ul>
              <li><CheckIcon />A rehearsal runs before any traffic moves</li>
              <li><CheckIcon />One-click rollback during the switch</li>
              <li><CheckIcon />Your domain ownership stays with you</li>
              <li><CheckIcon />An ownership package is always available</li>
            </ul>
          </section>
          <section className="jr-card safety-card">
            <h3>Your copy of everything</h3>
            <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>The built site, its repository and its settings. Yours to keep, wherever you go.</p>
            <p className="jr-muted" style={{ margin: "8px 0 0", fontSize: 13 }}>Generate the archive in step 03.</p>
          </section>
          <section className="safety-dark">
            <h3>Rather not touch DNS?</h3>
            <p>We do it with you on a 10-minute call, screen shared.</p>
            <a className="jr-btn wide" href="mailto:ismael@siteagain.com?subject=SiteAgain%2010-minute%20DNS%20call">Book 10 minutes</a>
          </section>
        </aside>
      </div>
    </div>
  );
}

function JourneyGrowStage({
  site,
  migration,
  user,
  changes,
  hasActiveChange,
  plan,
  onNewProject,
  onSubmitChange,
  onRecommendationPreview,
  onSelectFix,
  onStage,
}: {
  site: SiteView;
  migration: MigrationRecord | null;
  user: SessionUser;
  changes: ChangeView[];
  hasActiveChange: boolean;
  plan: JourneyPlanKey;
  onNewProject(): void;
  onSubmitChange(text: string): Promise<void>;
  onRecommendationPreview(changeSetId: string): void;
  onSelectFix(prompt: string): void;
  onStage(stage: JourneyStage): void;
}) {
  const [request, setRequest] = useState("");
  const [sent, setSent] = useState(false);
  const [weekData, setWeekData] = useState<{
    recommendations: RecommendationRecord[];
    measurements: RecommendationMeasurementRecord[];
    reports: WeeklyReportRecord[];
    enquiries: number | null;
    enquiryDays: number | null;
  } | null>(null);
  const [weekError, setWeekError] = useState(false);
  const [weekReload, setWeekReload] = useState(0);
  const host = websiteHost(site);
  const activePlan = JOURNEY_PLANS.find((item) => item.key === plan) ?? JOURNEY_PLANS[1]!;
  const firstName = (user.name || user.email).split(/[ @]/)[0] || "there";
  const week = weekNumber(new Date().toISOString());

  useEffect(() => {
    let active = true;
    async function loadWeek() {
      setWeekError(false);
      const [recommendationResult, reportResult, enquiryResult] = await Promise.allSettled([
        api<{ recommendations: RecommendationRecord[]; measurements: RecommendationMeasurementRecord[] }>(`/sites/${site.id}/recommendations`),
        api<{ reports: WeeklyReportRecord[] }>(`/sites/${site.id}/weekly-reports`),
        api<{ enquiries?: { totals?: { total: number } | null; window?: { days: number } | null } | null }>(`/sites/${site.id}/enquiries`),
      ]);
      if (!active) return;
      if ([recommendationResult, reportResult, enquiryResult].some((result) => result.status === "rejected")) {
        setWeekError(true);
        setWeekData(null);
        return;
      }
      setWeekData({
        recommendations: recommendationResult.status === "fulfilled" ? recommendationResult.value.recommendations : [],
        measurements: recommendationResult.status === "fulfilled" ? recommendationResult.value.measurements : [],
        reports: reportResult.status === "fulfilled" ? reportResult.value.reports : [],
        enquiries: enquiryResult.status === "fulfilled" ? enquiryResult.value.enquiries?.totals?.total ?? null : null,
        enquiryDays: enquiryResult.status === "fulfilled" ? enquiryResult.value.enquiries?.window?.days ?? null : null,
      });
    }
    void loadWeek();
    return () => { active = false; };
  }, [site.id, weekReload]);

  const weekLoading = weekData === null && !weekError;
  const recommendations = weekData?.recommendations ?? [];
  const measurements = weekData?.measurements ?? [];
  const improvements = recommendations.filter((item) => ["applied", "measuring", "measured"].includes(item.status)).length;
  const deployedChanges = changes.filter((item) => item.status === "deployed").length;
  const liveCount = improvements + deployedChanges;
  const waitingRecommendations = recommendations.filter((item) => ["new", "viewed", "saved"].includes(item.status)).length;
  const openChanges = changes.filter((item) => !["deployed", "rejected", "failed", "rolled_back"].includes(item.status)).length;
  const enquiries = weekData?.enquiries ?? null;
  const enquiryDays = weekData?.enquiryDays ?? null;
  const latestReport = weekData?.reports[0];
  const recentItems = [
    ...recommendations.map((item) => ({
      id: item.id,
      title: item.title,
      status: item.status,
      kind: "recommendation" as const,
      updatedAt: item.updatedAt,
      impactBand: item.impactBand,
      effort: item.effort,
    })),
    ...changes.map((item) => ({
      id: `change:${item.id}`,
      title: item.requestText,
      status: item.status,
      kind: "change" as const,
      updatedAt: item.updatedAt,
      impactBand: null,
      effort: null,
    })),
  ]
    .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt))
    .slice(0, 5);
  function scrollToSection(id: string) {
    window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  }
  async function send() {
    const text = request.trim();
    if (!text || hasActiveChange) return;
    try {
      await onSubmitChange(text);
      setRequest("");
      setSent(true);
    } catch {
      // The shell toast explains what failed.
    }
  }
  return (
    <div className="jr-wrap wide" id="growth-week">
      <header className="jr-growth-head">
        <span className="jr-eyebrow">Week {String(week).padStart(2, "0")} · {formatConversationDate(new Date().toISOString())}</span>
        <h1 className="jr-h1">
          Good week, {firstName}. {liveCount > 0 ? `${liveCount} improvement${liveCount === 1 ? "" : "s"} went live.` : "Your site is live and being watched."}{enquiries != null && enquiries > 0 && enquiryDays === 7 ? ` ${enquiries} ${enquiries === 1 ? "person" : "people"} got in touch.` : ""}
        </h1>
      </header>

      <section className="jr-composer" aria-label="Request a change">
        <span className="jr-eyebrow">What should change on your site?</span>
        <textarea
          value={request}
          onChange={(event) => setRequest(event.target.value)}
          placeholder="Add a new project, update the opening hours, change a photo…"
          disabled={hasActiveChange}
        />
        {sent && (
          <div className="jr-row" style={{ justifyContent: "space-between", background: "var(--jr-ok-wash)", borderRadius: 10, padding: "8px 12px" }}>
            <span className="jr-muted" style={{ fontSize: 13 }}>Request received. We are building a safe plan.</span>
            <button className="jr-link" type="button" onClick={() => onStage("preview")}>Open preview →</button>
          </div>
        )}
        <div className="jr-composer-foot">
          <div className="jr-composer-chips">
            {["Update my opening hours", "Add a new project", "Change a photo", "New page"].map((prompt) => (
              <button className="jr-composer-chip" type="button" key={prompt} onClick={() => setRequest(prompt)}>{prompt}</button>
            ))}
          </div>
          <div className="jr-row">
            <span className="jr-composer-note">{hasActiveChange ? "A change is already in progress." : "A preview to approve before anything goes live"}</span>
            <button className="jr-btn" type="button" onClick={() => void send()} disabled={hasActiveChange || !request.trim()}>Send request</button>
          </div>
        </div>
      </section>

      <div className="jr-week-grid">
        <section className="jr-week-card dark">
          <span className="jr-eyebrow" style={{ color: "#a39e94" }}>Activity</span>
          <h3>{weekError ? "Evidence unavailable" : weekLoading ? "Reading this week…" : `${liveCount} improvement${liveCount === 1 ? "" : "s"} went live`}</h3>
          <p>
            {weekError ? "This week's evidence could not load." : weekLoading ? "Loading evidence and approvals." : (
              <>
                {waitingRecommendations > 0 ? `${waitingRecommendations} decision${waitingRecommendations === 1 ? "" : "s"} waiting for you.` : "No recommendations are waiting for your approval."}
                {openChanges > 0 ? ` ${openChanges} change${openChanges === 1 ? "" : "s"} in progress.` : ""}
              </>
            )}
          </p>
          {!weekError && !weekLoading && (
            <div className="jr-row" style={{ gap: 14 }}>
              {waitingRecommendations > 0 && <button className="jr-link" type="button" style={{ color: "#fff" }} onClick={() => scrollToSection("growth-approvals")}>Review approvals →</button>}
              {openChanges > 0 && <button className="jr-link" type="button" style={{ color: "#fff" }} onClick={() => onStage("preview")}>Review change →</button>}
            </div>
          )}
        </section>
        <section className="jr-week-card">
          <span className="jr-eyebrow">Results</span>
          <h3>{weekError ? "—" : weekLoading ? "—" : `${measurements.length} result${measurements.length === 1 ? "" : "s"} measured`}</h3>
          <p>{weekError ? "This week's evidence could not load." : weekLoading ? "Reading this week's results." : latestReport?.summary?.headline ?? "Results appear 7–28 days after a change goes live."}</p>
          {!weekError && !weekLoading && <button className="jr-link" type="button" onClick={() => scrollToSection("growth-results")}>See results →</button>}
        </section>
        <section className="jr-week-card">
          <span className="jr-eyebrow">Enquiries</span>
          <h3>{weekError ? "—" : weekLoading ? "—" : enquiries != null ? `${enquiries} ${enquiries === 1 ? "person" : "people"} got in touch` : "Not counting yet"}</h3>
          <p>{weekError ? "This week's evidence could not load." : weekLoading ? "Reading this week's enquiries." : enquiries != null ? `${enquiryDays != null ? `Last ${enquiryDays} days · ` : ""}messages from your contact form, kept for you.` : "Connect the form to see who gets in touch."}</p>
          {!weekError && !weekLoading && <button className="jr-link" type="button" onClick={() => scrollToSection("growth-enquiries")}>Open enquiries →</button>}
        </section>
      </div>

      <div className="jr-week-done">
        <div className="jr-section-head">
          <h2>What did (and didn&apos;t) get done this week</h2>
          <button className="jr-link" type="button" onClick={() => scrollToSection("growth-approvals")}>All decisions →</button>
        </div>
        {weekError ? (
          <div className="jr-card pad jr-row" style={{ justifyContent: "space-between" }}>
            <p className="jr-muted" style={{ margin: 0 }}>This week&apos;s history could not load.</p>
            <button className="jr-btn ghost small" type="button" onClick={() => setWeekReload((value) => value + 1)}><RefreshIcon />Retry</button>
          </div>
        ) : weekLoading ? (
          <div className="jr-card pad"><p className="jr-muted" style={{ margin: 0 }}>Loading this week&apos;s history…</p></div>
        ) : recentItems.length === 0 ? (
          <div className="jr-card pad"><p className="jr-muted" style={{ margin: 0 }}>Nothing was requested this week. Ask for something above and we will prepare a preview.</p></div>
        ) : (
          <div className="jr-card jr-week-list">
            {recentItems.map((item) => {
              const done = ["applied", "measuring", "measured", "deployed"].includes(item.status);
              const declined = ["skipped", "expired", "rejected", "failed", "rolled_back"].includes(item.status);
              const awaiting = item.kind === "change" && item.status === "awaiting_approval";
              const inProgress = item.kind === "change" && ["requested", "planning", "editing", "validating", "preview_ready", "approved", "deploying"].includes(item.status);
              const detail = done
                ? "Went live"
                : declined
                  ? item.status === "expired" ? "Expired with its evidence" : item.status === "rolled_back" ? "Rolled back" : "Not done"
                  : awaiting
                    ? "Ready for your approval"
                    : inProgress
                      ? `Change request · ${shortStatus(item.status)}`
                      : "Waiting for your decision";
              return (
                <div className="jr-week-item" key={item.id}>
                  <span className={`jr-week-icon ${done ? "done" : declined ? "not" : "waiting"}`}>{done ? <CheckIcon /> : declined ? <XIcon /> : "·"}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {detail}
                      {item.impactBand ? ` · ${item.impactBand} impact` : ""}
                      {item.effort ? ` · ${item.effort} effort` : ""}
                    </small>
                  </div>
                  <span className={`jr-chip ${done || awaiting ? "ok" : ""}`}>{done ? "Done" : declined ? "Not done" : awaiting ? "Ready" : inProgress ? "In progress" : "Waiting"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="jr-growth-grid">
        <div className="jr-growth-main">
          <GrowthView
            site={site}
            userEmail={user.email}
            onPreviewCreated={onRecommendationPreview}
            onRequestChange={(text) => { void onSubmitChange(text).catch(() => undefined); }}
            changeBlocked={hasActiveChange}
          />
        </div>
        <aside className="jr-growth-aside" id="growth-plan">
          <section className="jr-card jr-plan">
            <h3>Your ongoing plan</h3>
            <div className="jr-plan-row"><span>{activePlan.name}</span><b>{activePlan.price}{activePlan.unit === "/ month" ? " / month" : ""}</b></div>
            <div className="jr-plan-row"><span>Cadence</span><b>{plan === "growth" ? "Weekly actions" : plan === "local" ? "Monthly actions" : "Custom"}</b></div>
            <div className="jr-plan-row"><span>Experiments</span><b>{plan === "local" ? "Not included" : "Included"}</b></div>
            <div className="jr-plan-row"><span>Approval</span><b>Always yours</b></div>
            <div className="jr-plan-row"><span>Rollback</span><b>One click</b></div>
            <button className="jr-btn ghost small" type="button" style={{ marginTop: 12 }} onClick={() => onStage("choose")}>See the plans</button>
          </section>
          <section className="jr-card safety-card">
            <h3>Your copy of everything</h3>
            <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>Code, repository and settings, always downloadable from Go live.</p>
            <button className="jr-btn ghost small" type="button" style={{ marginTop: 12 }} onClick={() => onStage("launch")}>Open go live</button>
          </section>
          <section className="jr-card safety-card">
            <h3>Add another website</h3>
            <p className="jr-muted" style={{ margin: 0, fontSize: 14 }}>Each site keeps its own private proof, preview and release history.</p>
            <button className="jr-btn ghost small" type="button" style={{ marginTop: 12 }} onClick={onNewProject}>Add a website</button>
          </section>
          <p className="jr-muted" style={{ fontSize: 11, margin: 0 }}>
            {migration?.completedAt ? `Last build ${formatConversationDate(migration.completedAt)}` : "Continuous improvement · Sebora intelligence"} · {host}
          </p>
        </aside>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<React.StrictMode><AuthGate /></React.StrictMode>);
